import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import IconPicker from './IconPicker';
import TranslationPageSettingsTab from './TranslationPageSettingsTab';

const TranslationsAdmin = ({ API, adminToken, isDarkMode, showNotification, settings, setSettings, languages, onLanguagesUpdate, onSettingsUpdate, badges, loadBadgesData }) => {
  const authHeaders = {
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  };

  const authHeadersForForm = {
    'Authorization': `Bearer ${adminToken}`
  };

  const flushUserRequests = async () => {
    try {
      const res = await fetch(`${API}/admin/user-requests/flush`, { method: 'DELETE', headers: authHeaders });
      if (res.ok) {
        setUserRequests([]);
        setUserRequestsCount({ total: 0, limit: 100 });
        setShowFlushRequestsModal(false);
        showNotification('All user requests deleted', 'success');
      } else {
        showNotification('Failed to delete requests', 'error');
      }
    } catch (e) {
      showNotification('Failed to delete requests', 'error');
    }
  };

  const [adminLanguages, setAdminLanguages] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [userRequests, setUserRequests] = useState([]);
  const [userRequestsCount, setUserRequestsCount] = useState({ total: 0, limit: 100 });
  const [showFlushRequestsModal, setShowFlushRequestsModal] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [requestEditForm, setRequestEditForm] = useState({
    sourceText: '',
    translatedText: '',
    latinScript: '',
    gender: '',
    number: '',
    audioUrl: ''
  });

  const [cardEdits, setCardEdits] = useState({});  // { translationId: { gender, number, audioUrl } }
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, sourceText, latinScript}
  const [newMeaningForm, setNewMeaningForm] = useState(null); // { id, variantNote, primaryNote }
  const [setPrimaryLoading, setSetPrimaryLoading] = useState(null); // translation id being processed
  const [editFormData, setEditFormData] = useState({
    sourceText: '',
    translatedText: '',
    latinScript: '',
    gender: '',
    number: '',
    audioUrl: '',
    audioEnabled: true,
    note: '',
    // Variants support for edit form
    variants: [],        // Existing variants from database
    newVariants: [],     // New variants to add
    isLoadingVariants: false
  });
  
  // Helper functions for edit form variants
  const addEditVariant = () => {
    setEditFormData(prev => ({
      ...prev,
      newVariants: [...prev.newVariants, { latin: '', arabic: '', isNew: true }]
    }));
  };
  
  const removeEditVariant = (index, isNew) => {
    if (isNew) {
      setEditFormData(prev => ({
        ...prev,
        newVariants: prev.newVariants.filter((_, i) => i !== index)
      }));
    }
    // Note: Existing variants deletion would need a separate API call
  };
  
  const updateEditVariant = (index, field, value, isNew) => {
    if (isNew) {
      setEditFormData(prev => ({
        ...prev,
        newVariants: prev.newVariants.map((v, i) => i === index ? { ...v, [field]: value } : v)
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        variants: prev.variants.map((v, i) => i === index ? { ...v, [field]: value, modified: true } : v)
      }));
    }
  };
  
  // Set a variant as the new primary
  const handleSetPrimary = async (translationId) => {
    try {
      setSetPrimaryLoading(translationId);
      const res = await fetch(`${API}/admin/translations/set-primary/${translationId}`, {
        method: 'PUT',
        headers: authHeaders
      });
      if (res.ok) {
        showNotification('Primary updated successfully', 'success');
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || 'Failed to set primary', 'error');
      }
    } catch (error) {
      showNotification('Failed to set primary', 'error');
    } finally {
      setSetPrimaryLoading(null);
    }
  };

  // Convert a variant to a new meaning (disambiguation)
  const handleConvertToMeaning = async (variantId) => {
    const form = newMeaningForm;
    if (!form || !form.variantNote.trim() || !form.primaryNote.trim()) {
      showNotification('Both notes are required for disambiguation', 'error');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('variantId', variantId);
      formData.append('variantNote', form.variantNote.trim());
      formData.append('primaryNote', form.primaryNote.trim());
      const res = await fetch(`${API}/admin/translations/convert-to-meaning`, {
        method: 'POST',
        headers: authHeadersForForm,
        body: formData
      });
      if (res.ok) {
        showNotification('Converted to separate meaning successfully', 'success');
        setNewMeaningForm(null);
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || 'Failed to convert to meaning', 'error');
      }
    } catch (error) {
      showNotification('Failed to convert to meaning', 'error');
    }
  };

  // Fetch variants for a translation when editing
  const fetchVariantsForEdit = async (sourceText) => {
    try {
      const res = await fetch(`${API}/translate?text=${encodeURIComponent(sourceText)}&sourceLang=en`, {
        method: 'GET'
      });
      if (res.ok) {
        const data = await res.json();
        return data.variants || [];
      }
    } catch (err) {
      console.error('Failed to fetch variants:', err);
    }
    return [];
  };

  const [languageFilter, setLanguageFilter] = useState("all");  // Filter for translations

  const [dbConnector, setDbConnector] = useState({
    migrationStats: null,
    migrationOldDomain: '',
    migrationNewDomain: '',
    migrationScanning: false,
    currentMigrationSession: null,
    migrationScanResults: null,
    migrationSessions: [],
    selectedMigrationSession: null,
    sessionRoutes: [],
    isLoading: false
  });


  // Search and Pagination states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [langFilter, setLangFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 30,
    total_count: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });

  const [wordSuggestions, setWordSuggestions] = useState([]);
  const [editingSuggestion, setEditingSuggestion] = useState(null);
  const [editSuggestionData, setEditSuggestionData] = useState({
    word: '',
    language: '',
    message: ''
  });
  const [feedback, setFeedback] = useState([]);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [editFeedbackData, setEditFeedbackData] = useState({
    sourceText: '',
    translatedText: '',
    sourceLang: '',
    feedbackType: 'like',
    message: '',
    status: 'reviewed'
  });
  const [quickTranslations, setQuickTranslations] = useState({}); // { suggestionId: translationText }

  const [newTranslation, setNewTranslation] = useState({
    sourceText: "",
    sourceLang: "en",
    translatedText: "",
    latinScript: "",
    gender: "",
    number: "",
    status: "verified",
    audioUrl: "",
    audioEnabled: true,
    note: "",
    // Variant support for manual "Add New Translation"
    variantRows: []
  });

  // Duplicate detection state
  const [duplicateCheck, setDuplicateCheck] = useState({ show: false, existing: null, entries: [] });
  const [newMeaningMode, setNewMeaningMode] = useState(false);
  const [existingEntrySubtitle, setExistingEntrySubtitle] = useState('');
  const [newEntrySubtitle, setNewEntrySubtitle] = useState('');
  const [newMeaningPrimary, setNewMeaningPrimary] = useState('existing'); // 'existing' or 'new'

  // ⚠️ DO NOT REMOVE — FIX: Edit duplicate detection (2026-03-15)
  // Prevents creating duplicate entries when editing a translation's sourceText
  // to a word that already exists. Shows popup: "Save as Variant" / "Save Anyway" / "Cancel"
  const [editDuplicateCheck, setEditDuplicateCheck] = useState({ show: false, pendingArgs: null, existingEntries: [] });

  // Helper functions for newTranslation variants
  const addNewTranslationVariant = () => {
    setNewTranslation(prev => ({
      ...prev,
      variantRows: [...prev.variantRows, { latin: "", arabic: "", gender: "", number: "" }]
    }));
  };
  
  const removeNewTranslationVariant = (index) => {
    setNewTranslation(prev => ({
      ...prev,
      variantRows: prev.variantRows.filter((_, i) => i !== index)
    }));
  };
  
  const updateNewTranslationVariant = (index, field, value) => {
    setNewTranslation(prev => ({
      ...prev,
      variantRows: prev.variantRows.map((row, i) => 
        i === index ? { ...row, [field]: value } : row
      )
    }));
  };

  const [translationStats, setTranslationStats] = useState({
    total: 0,
    singleWords: 0,
    sentences: 0,
    isLoading: false
  });
  
  // Fetch translation stats
  const fetchTranslationStats = async () => {
    if (!adminToken) return;
    setTranslationStats(prev => ({ ...prev, isLoading: true }));
    try {
      const res = await fetch(`${API}/admin/translations/stats`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTranslationStats({
          total: data.total || 0,
          singleWords: data.singleWords || 0,
          sentences: data.sentences || 0,
          isLoading: false
        });
      }
    } catch (err) {
      console.error('Failed to fetch translation stats:', err);
      setTranslationStats(prev => ({ ...prev, isLoading: false }));
    }
  };

  const fetchAuditData = async () => {
    if (!adminToken) return;
    setAuditLoading(true);
    try {
      const res = await fetch(`${API}/admin/translations/audit`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAuditData(data);
      }
    } catch (err) {
      console.error('Failed to fetch audit data:', err);
    }
    setAuditLoading(false);
  };

  // AI Translation Helper State (Groq - FREE)
  const [aiHelper, setAiHelper] = useState({
    sourceText: "",
    // Primary translation
    arabicScript: "",
    latinScript: "",
    gender: "",
    number: "",
    audioUrl: "",
    // Additional variants (array of {latin, arabic})
    variantRows: [],
    isLoading: false,
    error: null,
    // Bulk Import
    showBulkImport: false,
    bulkText: "",
    bulkPreview: null,
    bulkError: null,
    // Duplicate detection
    duplicateFound: false,
    existingTranslations: []
  });

  // Groq API Key Settings State
  const [groqSettings, setGroqSettings] = useState({
    apiKeySet: false,
    apiKeyMasked: "",
    newApiKey: "",
    enabled: false,
    isLoading: false,
    showApiKeyInput: false,
    message: null,
    // Prompt editor state
    systemPrompt: "",
    temperature: 0.3,
    model: "llama-3.3-70b-versatile",
    showPromptEditor: false,
    promptIsLoading: false,
    promptMessage: null
  });


  // Load Groq API key settings
  const loadGroqSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/translation-groq/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGroqSettings(prev => ({
          ...prev,
          apiKeySet: data.api_key_set,
          apiKeyMasked: data.api_key_masked,
          enabled: data.enabled || false,
          systemPrompt: data.system_prompt || "",
          temperature: data.temperature !== undefined && data.temperature !== null ? data.temperature : 0.3,
          model: data.model || "llama-3.3-70b-versatile"
        }));
      }
    } catch (err) {
      console.error('Failed to load Groq settings:', err);
    }
  };

  // Save Groq prompt (system_prompt + temperature + model)
  const saveGroqPrompt = async () => {
    setGroqSettings(prev => ({ ...prev, promptIsLoading: true, promptMessage: null }));
    try {
      const res = await fetch(`${API}/admin/translation-groq/prompt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          system_prompt: groqSettings.systemPrompt,
          temperature: parseFloat(groqSettings.temperature) || 0.3,
          model: groqSettings.model
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setGroqSettings(prev => ({
          ...prev,
          promptIsLoading: false,
          promptMessage: { type: 'success', text: 'Groq prompt saved' }
        }));
        showNotification('Groq prompt saved', 'success');
      } else {
        setGroqSettings(prev => ({
          ...prev,
          promptIsLoading: false,
          promptMessage: { type: 'error', text: data.detail || 'Failed to save prompt' }
        }));
      }
    } catch (err) {
      setGroqSettings(prev => ({
        ...prev,
        promptIsLoading: false,
        promptMessage: { type: 'error', text: 'Network error' }
      }));
    }
  };

  // Toggle Groq enable/disable
  const toggleGroq = async () => {
    const newEnabled = !groqSettings.enabled;
    try {
      const formData = new FormData();
      formData.append('enabled', String(newEnabled));
      const res = await fetch(`${API}/admin/translation-groq/settings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        setGroqSettings(prev => ({ ...prev, enabled: newEnabled }));
        showNotification(`Groq ${newEnabled ? 'enabled' : 'disabled'}`, 'success');
      }
    } catch (err) {
      showNotification('Failed to toggle Groq', 'error');
    }
  };

  // Save Groq API key
  const saveGroqApiKey = async () => {
    setGroqSettings(prev => ({ ...prev, isLoading: true, message: null }));
    try {
      const formData = new FormData();
      formData.append('api_key', groqSettings.newApiKey);
      const res = await fetch(`${API}/admin/translation-groq/settings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setGroqSettings(prev => ({
          ...prev,
          apiKeySet: true,
          enabled: true,
          apiKeyMasked: `***${groqSettings.newApiKey.slice(-4)}`,
          newApiKey: "",
          showApiKeyInput: false,
          isLoading: false,
          message: { type: 'success', text: 'Groq API key saved successfully!' }
        }));
        showNotification('Groq API key saved!', 'success');
      } else {
        setGroqSettings(prev => ({
          ...prev,
          isLoading: false,
          message: { type: 'error', text: data.detail || 'Failed to save API key' }
        }));
      }
    } catch (err) {
      setGroqSettings(prev => ({
        ...prev,
        isLoading: false,
        message: { type: 'error', text: 'Network error' }
      }));
    }
  };
  
  // OpenAI API Settings State (mirror DeepSeek shape — same admin pattern)
  const [openaiSettings, setOpenaiSettings] = useState({
    apiKeySet: false, apiKeyMasked: "", newApiKey: "", enabled: false,
    model: "gpt-5.4-mini", temperature: 0.3, maxTokens: 500,
    customPrompt: "",
    isLoading: false, showApiKeyInput: false, message: null,
    testText: "", testResult: null, isTesting: false,
    isPromptSaving: false
  });

  const loadOpenaiSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/openai/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOpenaiSettings(prev => ({
          ...prev,
          apiKeySet: data.api_key_set,
          apiKeyMasked: data.api_key_masked,
          enabled: data.enabled,
          model: data.model || "gpt-5.4-mini",
          temperature: data.temperature ?? 0.3,
          maxTokens: data.max_tokens ?? 500,
          customPrompt: data.custom_prompt || ""
        }));
      }
    } catch (err) { console.error('Failed to load OpenAI settings:', err); }
  };

  const saveOpenaiCustomPrompt = async () => {
    setOpenaiSettings(prev => ({ ...prev, isPromptSaving: true }));
    try {
      const formData = new FormData();
      formData.append('custom_prompt', openaiSettings.customPrompt);
      const res = await fetch(`${API}/admin/openai/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
      });
      if (res.ok) {
        showNotification('OpenAI custom prompt saved', 'success');
      } else {
        showNotification('Failed to save custom prompt', 'error');
      }
    } catch (err) {
      showNotification('Network error saving prompt', 'error');
    }
    setOpenaiSettings(prev => ({ ...prev, isPromptSaving: false }));
  };

  const saveOpenaiApiKey = async () => {
    setOpenaiSettings(prev => ({ ...prev, isLoading: true, message: null }));
    try {
      const formData = new FormData();
      formData.append('api_key', openaiSettings.newApiKey);
      const res = await fetch(`${API}/admin/openai/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setOpenaiSettings(prev => ({
          ...prev, apiKeySet: true, apiKeyMasked: `***${openaiSettings.newApiKey.slice(-4)}`,
          newApiKey: "", showApiKeyInput: false, isLoading: false, enabled: true,
          message: { type: 'success', text: 'OpenAI API key saved!' }
        }));
        showNotification('OpenAI API key saved!', 'success');
      } else {
        setOpenaiSettings(prev => ({ ...prev, isLoading: false, message: { type: 'error', text: data.detail || 'Failed to save' } }));
      }
    } catch (err) {
      setOpenaiSettings(prev => ({ ...prev, isLoading: false, message: { type: 'error', text: 'Network error' } }));
    }
  };

  const toggleOpenai = async () => {
    const newEnabled = !openaiSettings.enabled;
    try {
      const formData = new FormData();
      formData.append('enabled', String(newEnabled));
      const res = await fetch(`${API}/admin/openai/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
      });
      if (res.ok) {
        setOpenaiSettings(prev => ({ ...prev, enabled: newEnabled }));
        showNotification(`OpenAI ${newEnabled ? 'enabled' : 'disabled'}`, 'success');
      }
    } catch (err) { showNotification('Failed to toggle OpenAI', 'error'); }
  };

  const saveOpenaiModel = async (newModel) => {
    try {
      const formData = new FormData();
      formData.append('model', newModel);
      const res = await fetch(`${API}/admin/openai/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
      });
      if (res.ok) {
        setOpenaiSettings(prev => ({ ...prev, model: newModel }));
        showNotification(`OpenAI model set to ${newModel}`, 'success');
      }
    } catch (err) { showNotification('Failed to update model', 'error'); }
  };

  const testOpenai = async () => {
    if (!openaiSettings.testText.trim()) return;
    setOpenaiSettings(prev => ({ ...prev, isTesting: true, testResult: null }));
    try {
      const res = await fetch(`${API}/admin/openai/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: openaiSettings.testText })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setOpenaiSettings(prev => ({ ...prev, isTesting: false, testResult: data }));
      } else {
        setOpenaiSettings(prev => ({ ...prev, isTesting: false, testResult: { error: data.error || data.detail || 'Translation failed' } }));
      }
    } catch (err) {
      setOpenaiSettings(prev => ({ ...prev, isTesting: false, testResult: { error: 'Network error' } }));
    }
  };

  // DeepSeek API Settings State
  const [deepseekSettings, setDeepseekSettings] = useState({
    apiKeySet: false, apiKeyMasked: "", newApiKey: "", enabled: false,
    isLoading: false, showApiKeyInput: false, message: null,
    testText: "", testResult: null, isTesting: false
  });
  // DeepSeek Prompt Settings State
  const [deepseekPrompt, setDeepseekPrompt] = useState({
    temperature: 0.2,
    persona: '',
    grammarRules: '',
    substitutions: [],
    examples: [],
    isSaving: false,
    showPromptEditor: false
  });

  const loadDeepseekSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/deepseek/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDeepseekSettings(prev => ({
          ...prev, apiKeySet: data.api_key_set, apiKeyMasked: data.api_key_masked, enabled: data.enabled
        }));
        setDeepseekPrompt(prev => ({
          ...prev,
          temperature: data.temperature ?? 0.2,
          persona: data.persona || '',
          grammarRules: data.grammar_rules || '',
          substitutions: data.substitutions || [],
          examples: data.examples || []
        }));
      }
    } catch (err) { console.error('Failed to load DeepSeek settings:', err); }
  };

  const saveDeepseekApiKey = async () => {
    setDeepseekSettings(prev => ({ ...prev, isLoading: true, message: null }));
    try {
      const formData = new FormData();
      formData.append('api_key', deepseekSettings.newApiKey);
      const res = await fetch(`${API}/admin/deepseek/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setDeepseekSettings(prev => ({
          ...prev, apiKeySet: true, apiKeyMasked: `***${deepseekSettings.newApiKey.slice(-4)}`,
          newApiKey: "", showApiKeyInput: false, isLoading: false, enabled: true,
          message: { type: 'success', text: 'DeepSeek API key saved!' }
        }));
        showNotification('DeepSeek API key saved!', 'success');
      } else {
        setDeepseekSettings(prev => ({ ...prev, isLoading: false, message: { type: 'error', text: data.detail || 'Failed to save' } }));
      }
    } catch (err) {
      setDeepseekSettings(prev => ({ ...prev, isLoading: false, message: { type: 'error', text: 'Network error' } }));
    }
  };

  const toggleDeepseek = async () => {
    const newEnabled = !deepseekSettings.enabled;
    try {
      const formData = new FormData();
      formData.append('enabled', String(newEnabled));
      const res = await fetch(`${API}/admin/deepseek/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
      });
      if (res.ok) {
        setDeepseekSettings(prev => ({ ...prev, enabled: newEnabled }));
        showNotification(`DeepSeek ${newEnabled ? 'enabled' : 'disabled'}`, 'success');
      }
    } catch (err) { showNotification('Failed to toggle DeepSeek', 'error'); }
  };

  const testDeepseek = async () => {
    if (!deepseekSettings.testText.trim()) return;
    setDeepseekSettings(prev => ({ ...prev, isTesting: true, testResult: null }));
    try {
      const res = await fetch(`${API}/admin/deepseek/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: deepseekSettings.testText })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setDeepseekSettings(prev => ({ ...prev, isTesting: false, testResult: data }));
      } else {
        setDeepseekSettings(prev => ({ ...prev, isTesting: false, testResult: { error: data.detail || 'Translation failed' } }));
      }
    } catch (err) {
      setDeepseekSettings(prev => ({ ...prev, isTesting: false, testResult: { error: 'Network error' } }));
    }
  };

  const saveDeepseekPrompt = async () => {
    setDeepseekPrompt(prev => ({ ...prev, isSaving: true }));
    try {
      const res = await fetch(`${API}/admin/deepseek/prompt`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          persona: deepseekPrompt.persona,
          grammar_rules: deepseekPrompt.grammarRules,
          substitutions: deepseekPrompt.substitutions,
          examples: deepseekPrompt.examples,
          temperature: deepseekPrompt.temperature
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification('Prompt settings saved!', 'success');
      } else {
        showNotification(data.detail || 'Failed to save prompt', 'error');
      }
    } catch (err) {
      showNotification('Network error saving prompt', 'error');
    }
    setDeepseekPrompt(prev => ({ ...prev, isSaving: false }));
  };

  // ═══════════════════════════════════════════════════════════════
  // Gemini AI Settings State
  // ═══════════════════════════════════════════════════════════════
  const [geminiSettings, setGeminiSettings] = useState({
    apiKeySet: false, apiKeyMasked: "", newApiKey: "", enabled: false, ttsEnabled: false,
    isLoading: false, showApiKeyInput: false, message: null,
    model: "gemini-2.5-flash", temperature: 0.3, systemPrompt: "",
    pipelinePriority: "before_deepseek", maxAudioLength: 200, maxOutputTokens: 2000, voice: "Aoede",
    testText: "", testResult: null, isTesting: false, isTestingAudio: false, testAudioUrl: null,
    usageStats: { month: "", translation_count: 0, audio_count: 0 },
    cacheStats: { files: 0, size_mb: 0 },
    showPromptEditor: false, isSavingPrompt: false
  });

  const loadGeminiSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/gemini/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGeminiSettings(prev => ({
          ...prev,
          apiKeySet: data.api_key_set,
          apiKeyMasked: data.api_key_masked,
          enabled: data.enabled,
          ttsEnabled: data.tts_enabled,
          model: data.model || "gemini-2.5-flash",
          temperature: data.temperature ?? 0.3,
          systemPrompt: data.system_prompt || "",
          pipelinePriority: data.pipeline_priority || "before_deepseek",
          maxAudioLength: data.max_audio_length || 200,
          maxOutputTokens: data.max_output_tokens || 2000,
          voice: data.voice || "Aoede",
          usageStats: data.usage_stats || { month: "", translation_count: 0, audio_count: 0 },
          cacheStats: data.cache_stats || { files: 0, size_mb: 0 }
        }));
      }
    } catch (err) { console.error('Failed to load Gemini settings:', err); }
  };

  const saveGeminiApiKey = async () => {
    setGeminiSettings(prev => ({ ...prev, isLoading: true, message: null }));
    try {
      const formData = new FormData();
      formData.append('api_key', geminiSettings.newApiKey);
      const res = await fetch(`${API}/admin/gemini/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setGeminiSettings(prev => ({
          ...prev, apiKeySet: true, apiKeyMasked: `***${geminiSettings.newApiKey.slice(-4)}`,
          newApiKey: "", showApiKeyInput: false, isLoading: false, enabled: true,
          message: { type: 'success', text: 'Gemini API key saved!' }
        }));
        showNotification('Gemini API key saved!', 'success');
      } else {
        setGeminiSettings(prev => ({ ...prev, isLoading: false, message: { type: 'error', text: data.detail || 'Failed to save' } }));
      }
    } catch (err) {
      setGeminiSettings(prev => ({ ...prev, isLoading: false, message: { type: 'error', text: 'Network error' } }));
    }
  };

  const toggleGemini = async (field = 'enabled') => {
    const newValue = field === 'tts_enabled' ? !geminiSettings.ttsEnabled : !geminiSettings.enabled;
    try {
      const formData = new FormData();
      formData.append(field, String(newValue));
      const res = await fetch(`${API}/admin/gemini/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
      });
      if (res.ok) {
        if (field === 'tts_enabled') {
          setGeminiSettings(prev => ({ ...prev, ttsEnabled: newValue }));
          showNotification(`Gemini TTS ${newValue ? 'enabled' : 'disabled'}`, 'success');
        } else {
          setGeminiSettings(prev => ({ ...prev, enabled: newValue }));
          showNotification(`Gemini ${newValue ? 'enabled' : 'disabled'}`, 'success');
        }
      }
    } catch (err) { showNotification('Failed to toggle Gemini', 'error'); }
  };

  const removeGeminiApiKey = async () => {
    if (!window.confirm('Remove Gemini API Key?\nGemini translation and TTS will stop working until a new key is added.')) return;
    try {
      const formData = new FormData();
      formData.append('api_key', '');
      const res = await fetch(`${API}/admin/gemini/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
      });
      if (res.ok) {
        loadGeminiSettings();
        showNotification('Gemini API key removed', 'success');
      }
    } catch (err) { showNotification('Failed to remove key', 'error'); }
  };

  const saveGeminiSettings = async (updates) => {
    try {
      const formData = new FormData();
      Object.entries(updates).forEach(([key, val]) => formData.append(key, String(val)));
      const res = await fetch(`${API}/admin/gemini/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
      });
      if (res.ok) {
        showNotification('Gemini settings saved!', 'success');
      }
    } catch (err) { showNotification('Failed to save settings', 'error'); }
  };

  const saveGeminiPrompt = async () => {
    setGeminiSettings(prev => ({ ...prev, isSavingPrompt: true }));
    try {
      const res = await fetch(`${API}/admin/gemini/prompt`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system_prompt: geminiSettings.systemPrompt,
          temperature: geminiSettings.temperature,
          model: geminiSettings.model
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification('Gemini prompt settings saved!', 'success');
      } else {
        showNotification(data.detail || 'Failed to save prompt', 'error');
      }
    } catch (err) { showNotification('Network error saving prompt', 'error'); }
    setGeminiSettings(prev => ({ ...prev, isSavingPrompt: false }));
  };

  const testGemini = async () => {
    if (!geminiSettings.testText.trim()) return;
    setGeminiSettings(prev => ({ ...prev, isTesting: true, testResult: null, testAudioUrl: null }));
    try {
      const res = await fetch(`${API}/admin/gemini/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: geminiSettings.testText })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setGeminiSettings(prev => ({ ...prev, isTesting: false, testResult: data }));
      } else {
        setGeminiSettings(prev => ({ ...prev, isTesting: false, testResult: { error: data.detail || 'Translation failed' } }));
      }
    } catch (err) {
      setGeminiSettings(prev => ({ ...prev, isTesting: false, testResult: { error: 'Network error' } }));
    }
  };

  const testGeminiAudio = async (text) => {
    if (!text) return;
    setGeminiSettings(prev => ({ ...prev, isTestingAudio: true, testAudioUrl: null }));
    try {
      const res = await fetch(`${API}/audio/darija?text=${encodeURIComponent(text)}&voice=${geminiSettings.voice}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setGeminiSettings(prev => ({ ...prev, isTestingAudio: false, testAudioUrl: url }));
        const audio = new Audio(url);
        audio.play();
      } else {
        const data = await res.json().catch(() => ({}));
        showNotification(data.detail || 'Audio generation failed', 'error');
        setGeminiSettings(prev => ({ ...prev, isTestingAudio: false }));
      }
    } catch (err) {
      showNotification('Audio generation failed', 'error');
      setGeminiSettings(prev => ({ ...prev, isTestingAudio: false }));
    }
  };

  const resetGeminiUsage = async () => {
    try {
      const res = await fetch(`${API}/admin/gemini/reset-usage`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        setGeminiSettings(prev => ({
          ...prev, usageStats: { ...prev.usageStats, translation_count: 0, audio_count: 0 }
        }));
        showNotification('Usage counters reset', 'success');
      }
    } catch (err) { showNotification('Failed to reset usage', 'error'); }
  };

  const clearGeminiCache = async () => {
    if (!window.confirm('Clear Audio Cache?\nAll cached audio files will be deleted. They will be regenerated on next request.')) return;
    try {
      const res = await fetch(`${API}/admin/gemini/clear-cache`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        loadGeminiSettings();
        showNotification('Audio cache cleared', 'success');
      }
    } catch (err) { showNotification('Failed to clear cache', 'error'); }
  };

  // DeepSeek AI Cache Review State
  // Per-provider pending AI translation states
  const [openaiPending, setOpenaiPending] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [openaiApproved, setOpenaiApproved] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [deepseekPending, setDeepseekPending] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [groqPending, setGroqPending] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [geminiPending, setGeminiPending] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [pendingLoading, setPendingLoading] = useState({ deepseek: false, groq: false, gemini: false });
  const [pendingEditing, setPendingEditing] = useState(null);
  const [pendingEditData, setPendingEditData] = useState({});
  const [pendingCounts, setPendingCounts] = useState({ deepseek: 0, groq: 0, gemini: 0, openai: 0 });
  const [aiSubTab, setAiSubTab] = useState({ groq: 'settings', deepseek: 'settings', gemini: 'settings', openai: 'settings' });
  // Approved AI translations state
  const [deepseekApproved, setDeepseekApproved] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [groqApproved, setGroqApproved] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [geminiApproved, setGeminiApproved] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [approvedLoading, setApprovedLoading] = useState({ deepseek: false, groq: false, gemini: false });
  const [approvedCounts, setApprovedCounts] = useState({ deepseek: 0, groq: 0, gemini: 0, openai: 0 });
  const [approvedEditing, setApprovedEditing] = useState(null);
  const [approvedEditData, setApprovedEditData] = useState({});
  const [aiCacheStats, setAiCacheStats] = useState(null);

  const loadAiCacheStats = async () => {
    try {
      const res = await fetch(`${API}/admin/ai-cache/stats`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) setAiCacheStats(await res.json());
    } catch (err) { /* ignore */ }
  };

  const loadProviderPending = async (provider, page = 1) => {
    const sourceMap = { deepseek: 'deepseek_ai', groq: 'groq_ai', gemini: 'gemini_ai', openai: 'openai_ai' };
    const setterMap = { deepseek: setDeepseekPending, groq: setGroqPending, gemini: setGeminiPending, openai: setOpenaiPending };
    setPendingLoading(prev => ({ ...prev, [provider]: true }));
    try {
      const res = await fetch(`${API}/admin/deepseek/cache?status=pending&api_source=${sourceMap[provider]}&page=${page}&limit=20`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setterMap[provider](data);
        setPendingCounts(prev => ({ ...prev, [provider]: data.total }));
      }
    } catch (err) { console.error(`Failed to load ${provider} pending:`, err); }
    setPendingLoading(prev => ({ ...prev, [provider]: false }));
  };

  // Delete-All confirmation modal state — { provider, count, isLoading } or null
  const [clearAllConfirm, setClearAllConfirm] = useState(null);



  const clearPendingForProvider = (provider) => {
    const countMap = {
      deepseek: deepseekPending.total,
      groq: groqPending.total,
      gemini: geminiPending.total,
      openai: openaiPending.total
    };
    setClearAllConfirm({ provider, count: countMap[provider] || 0, isLoading: false });
  };

  const executeClearPending = async () => {
    if (!clearAllConfirm) return;
    const { provider } = clearAllConfirm;
    const sourceMap = { deepseek: 'deepseek_ai', groq: 'groq_ai', gemini: 'gemini_ai', openai: 'openai_ai' };
    const labelMap = { deepseek: 'DeepSeek', groq: 'Groq', gemini: 'Gemini', openai: 'OpenAI' };
    const apiSource = sourceMap[provider];
    if (!apiSource) {
      setClearAllConfirm(null);
      return;
    }
    setClearAllConfirm(prev => prev ? { ...prev, isLoading: true } : prev);
    try {
      const res = await fetch(`${API}/admin/cache/clear-pending?api_source=${apiSource}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification(`Deleted ${data.deleted} pending ${labelMap[provider]} translation(s)`, 'success');
        await loadProviderPending(provider);
        await loadAllPendingCounts();
      } else {
        showNotification(`Failed to clear ${labelMap[provider]} queue`, 'error');
      }
    } catch (err) {
      showNotification('Network error', 'error');
    }
    setClearAllConfirm(null);
  };

  const loadAllPendingCounts = async () => {
    for (const provider of ['deepseek', 'groq', 'gemini', 'openai']) {
      const sourceMap = { deepseek: 'deepseek_ai', groq: 'groq_ai', gemini: 'gemini_ai', openai: 'openai_ai' };
      try {
        const res = await fetch(`${API}/admin/deepseek/cache?status=pending&api_source=${sourceMap[provider]}&page=1&limit=1`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPendingCounts(prev => ({ ...prev, [provider]: data.total }));
        }
      } catch (err) { /* ignore */ }
    }
  };

  const loadProviderApproved = async (provider, page = 1) => {
    const sourceMap = { deepseek: 'deepseek_ai', groq: 'groq_ai', gemini: 'gemini_ai', openai: 'openai_ai' };
    const setterMap = { deepseek: setDeepseekApproved, groq: setGroqApproved, gemini: setGeminiApproved, openai: setOpenaiApproved };
    setApprovedLoading(prev => ({ ...prev, [provider]: true }));
    try {
      const res = await fetch(`${API}/admin/deepseek/cache?status=approved&api_source=${sourceMap[provider]}&page=${page}&limit=20`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setterMap[provider](data);
        setApprovedCounts(prev => ({ ...prev, [provider]: data.total }));
      }
    } catch (err) { console.error(`Failed to load ${provider} approved:`, err); }
    setApprovedLoading(prev => ({ ...prev, [provider]: false }));
  };

  const saveApprovedEdit = async (id, provider) => {
    try {
      const res = await fetch(`${API}/admin/deepseek/cache/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(approvedEditData)
      });
      if (res.ok) {
        showNotification('Updated!', 'success');
        setApprovedEditing(null);
        const stateMap = { deepseek: deepseekApproved, groq: groqApproved, gemini: geminiApproved, openai: openaiApproved };
        loadProviderApproved(provider, stateMap[provider].page);
      }
    } catch (err) { showNotification('Update failed', 'error'); }
  };

  const approvePending = async (id, provider) => {
    try {
      const res = await fetch(`${API}/admin/deepseek/cache/${id}/approve`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification('Translation approved and saved!', 'success');
        const stateMap = { deepseek: deepseekPending, groq: groqPending, gemini: geminiPending, openai: openaiPending };
        loadProviderPending(provider, stateMap[provider].page);
        loadAiCacheStats();
      } else { showNotification('Approve failed', 'error'); }
    } catch (err) { showNotification('Approve failed', 'error'); }
  };

  const rejectPending = async (id, provider) => {
    setPendingEditing(`reject_${id}`);
  };
  const confirmRejectPending = async (id, provider) => {
    setPendingEditing(null);
    try {
      const res = await fetch(`${API}/admin/deepseek/cache/${id}/reject`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification('Translation rejected', 'success');
        const stateMap = { deepseek: deepseekPending, groq: groqPending, gemini: geminiPending, openai: openaiPending };
        loadProviderPending(provider, stateMap[provider].page);
        loadAiCacheStats();
      }
    } catch (err) { showNotification('Reject failed', 'error'); }
  };

  const savePendingEdit = async (id, provider) => {
    try {
      const res = await fetch(`${API}/admin/deepseek/cache/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(pendingEditData)
      });
      if (res.ok) {
        showNotification('Updated!', 'success');
        setPendingEditing(null);
        const stateMap = { deepseek: deepseekPending, groq: groqPending, gemini: geminiPending, openai: openaiPending };
        loadProviderPending(provider, stateMap[provider].page);
      }
    } catch (err) { showNotification('Update failed', 'error'); }
  };

  // Translation sub-tab state
  const [translationSubTab, setTranslationSubTab] = useState('translations');
  const [iconPickerOpen, setIconPickerOpen] = useState(null); // 'favorites' | 'request_word' | null
  const [availableIcons, setAvailableIcons] = useState([]);
  const [iconSearchQuery, setIconSearchQuery] = useState('');
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditExpandedCat, setAuditExpandedCat] = useState(null);
  const [auditSelectedMissing, setAuditSelectedMissing] = useState(new Set());
  const [auditPanelOpen, setAuditPanelOpen] = useState(false);
  const [auditGenerating, setAuditGenerating] = useState(false);
  const [auditGenResults, setAuditGenResults] = useState([]);
  // Map DeepSeek variation type to gender/number fields
  const mapGenResults = (results) => (results || []).map(r => ({
    ...r,
    number: r.number || 'singular',
    variations: (r.variations || []).map(v => ({
      ...v,
      gender: v.gender || (v.type === 'female' ? 'female' : null),
      number: v.number || (v.type === 'plural' ? 'plural' : 'singular'),
    }))
  }));
  const [feedbackSubTab, setFeedbackSubTab] = useState('requests');
  const [specialWords, setSpecialWords] = useState([]);
  const [specialSearchResults, setSpecialSearchResults] = useState([]);
  const [specialPreviewStyle, setSpecialPreviewStyle] = useState('gold-shimmer');
  const [specialPreviewSize, setSpecialPreviewSize] = useState('large');
  const [removeStyleModal, setRemoveStyleModal] = useState(null);

  const [bulkVerbs, setBulkVerbs] = useState({
    latinText: "",      // Left box: English = Latin format
    arabicText: "",     // Right box: Arabic script only
    preview: null,      // Parsed preview array with validation status
    isLoading: false,
    isValidating: false,
    error: null,
    success: null,
    showSection: false,
    // Validation summary
    summary: null,
    // Options for handling variants
    addVariants: true   // Whether to add entries marked as VARIANT
  });
  
  // Parse and VALIDATE bulk verbs from two text boxes
  const parseBulkVerbs = async () => {
    const latinLines = bulkVerbs.latinText.trim().split('\n').filter(line => line.trim());
    const arabicLines = bulkVerbs.arabicText.trim().split('\n').filter(line => line.trim());
    
    if (latinLines.length === 0) {
      setBulkVerbs(prev => ({ ...prev, error: 'Please enter Latin script lines', preview: null, summary: null }));
      return;
    }
    
    if (latinLines.length !== arabicLines.length) {
      setBulkVerbs(prev => ({ 
        ...prev, 
        error: `Line count mismatch! Latin: ${latinLines.length} lines, Arabic: ${arabicLines.length} lines. They must match!`,
        preview: null,
        summary: null
      }));
      return;
    }
    
    // Parse entries first
    const parsed = [];
    for (let i = 0; i < latinLines.length; i++) {
      const latinLine = latinLines[i].trim();
      const arabicLine = arabicLines[i].trim();
      
      // Parse Latin line: "English = Latin" or just "Latin"
      let english = '';
      let latin = '';
      
      if (latinLine.includes('=')) {
        const parts = latinLine.split('=');
        english = parts[0].trim();
        latin = parts.slice(1).join('=').trim();
      } else {
        english = latinLine;
        latin = latinLine;
      }
      
      // Auto-detect (S)/(P)/(M)/(F) tags from both Latin and Arabic text
      let gender = '';
      let number = '';
      const upperLatin = latin.toUpperCase();
      const upperArabic = arabicLine.toUpperCase();
      const combinedUpper = upperLatin + ' ' + upperArabic;
      const hasM = combinedUpper.includes('(M)');
      const hasF = combinedUpper.includes('(F)');
      if (hasM && hasF) gender = 'both';
      else if (hasM) gender = 'male';
      else if (hasF) gender = 'female';
      if (combinedUpper.includes('(S)')) number = 'singular';
      else if (combinedUpper.includes('(P)')) number = 'plural';

      // Clean tags from English, Latin, and Arabic
      const tagPattern = /\s*\([SMFP]\)\s*/gi;
      const cleanEnglish = english.replace(tagPattern, ' ').trim();
      const cleanLatin = latin.replace(tagPattern, ' ').trim();
      const cleanArabic = arabicLine.replace(tagPattern, ' ').trim();

      parsed.push({
        english: cleanEnglish,
        latin: cleanLatin,
        arabic: cleanArabic,
        gender: gender,
        number: number,
        original: latinLine
      });
    }
    
    // Now validate against database
    setBulkVerbs(prev => ({ ...prev, isValidating: true, error: null }));
    
    try {
      const res = await fetch(`${API}/admin/translations/bulk-validate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ entries: parsed })
      });
      
      if (res.ok) {
        const data = await res.json();
        setBulkVerbs(prev => ({ 
          ...prev, 
          preview: data.results, 
          summary: data.summary,
          isValidating: false,
          error: null 
        }));
      } else {
        // Fallback: just show preview without validation
        setBulkVerbs(prev => ({ 
          ...prev, 
          preview: parsed.map(p => ({ ...p, status: 'NEW', statusMessage: 'Will be added' })),
          summary: { total: parsed.length, new: parsed.length, exists: 0, variant: 0, invalid: 0 },
          isValidating: false
        }));
      }
    } catch (err) {
      // Fallback on error
      setBulkVerbs(prev => ({ 
        ...prev, 
        preview: parsed.map(p => ({ ...p, status: 'NEW', statusMessage: 'Will be added' })),
        summary: { total: parsed.length, new: parsed.length, exists: 0, variant: 0, invalid: 0 },
        isValidating: false
      }));
    }
  };
  
  // Delete an existing entry from the preview (so it can be re-added)
  const deleteBulkPreviewEntry = async (item, index) => {
    if (!item.existingTranslations || item.existingTranslations.length === 0) return;
    const entryId = item.existingTranslations[0].id || item.existingTranslations[0]._id;
    if (!entryId) return;
    try {
      const res = await fetch(`${API}/admin/translations/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        // Update this item's status to NEW in preview
        setBulkVerbs(prev => {
          const newPreview = [...prev.preview];
          newPreview[index] = { ...newPreview[index], status: 'NEW', existingTranslations: [] };
          const summary = { ...prev.summary };
          summary.exists = Math.max(0, (summary.exists || 0) - 1);
          summary.new = (summary.new || 0) + 1;
          return { ...prev, preview: newPreview, summary };
        });
      }
    } catch (err) {
      console.error('Failed to delete existing entry:', err);
    }
  };

  // Delete ALL existing entries from preview at once
  const deleteAllExistingBulkEntries = async () => {
    const existsItems = bulkVerbs.preview
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => item.status === 'EXISTS' && item.existingTranslations?.length > 0);
    if (existsItems.length === 0) return;
    for (const { item, idx } of existsItems) {
      await deleteBulkPreviewEntry(item, idx);
    }
  };

  // Save all bulk verbs to database (PROTECTED entries)
  const saveBulkVerbs = async () => {
    if (!bulkVerbs.preview || bulkVerbs.preview.length === 0) {
      setBulkVerbs(prev => ({ ...prev, error: 'Please preview first' }));
      return;
    }
    
    setBulkVerbs(prev => ({ ...prev, isLoading: true, error: null, success: null }));
    
    let saved = 0;
    let skipped = 0;
    let variants = 0;
    let updated = 0;
    let failed = 0;

    for (const item of bulkVerbs.preview) {
      // Skip items marked as EXISTS (already in database)
      if (item.status === 'EXISTS') {
        skipped++;
        continue;
      }

      // Skip VARIANT items if addVariants is false
      if (item.status === 'VARIANT' && !bulkVerbs.addVariants) {
        skipped++;
        continue;
      }

      try {
        if (item.status === 'UPDATE') {
          // UPDATE items - update existing entry's gender/number tags
          const formData = new FormData();
          formData.append('sourceText', item.english);
          formData.append('sourceLang', 'en');
          formData.append('translatedText', item.arabic);
          formData.append('latinScript', item.latin);
          formData.append('status', 'verified');
          formData.append('protected', 'true');
          if (item.gender) formData.append('gender', item.gender);
          if (item.number) formData.append('number', item.number);

          const res = await fetch(`${API}/admin/translations`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${adminToken}` },
            body: formData
          });

          if (res.ok) {
            updated++;
          } else {
            failed++;
          }
        } else if (item.status === 'VARIANT') {
          // Use add-variant endpoint for VARIANT items
          const formData = new FormData();
          formData.append('sourceText', item.english);
          formData.append('sourceLang', 'en');
          formData.append('translatedText', item.arabic);
          formData.append('latinScript', item.latin);
          if (item.gender) formData.append('gender', item.gender);
          if (item.number) formData.append('number', item.number);

          const res = await fetch(`${API}/admin/translations/add-variant`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${adminToken}` },
            body: formData
          });

          if (res.ok) {
            variants++;
          } else {
            failed++;
          }
        } else {
          // NEW items - use regular create endpoint
          const formData = new FormData();
          formData.append('sourceText', item.english);
          formData.append('sourceLang', 'en');
          formData.append('translatedText', item.arabic);
          formData.append('latinScript', item.latin);
          formData.append('status', 'verified');
          formData.append('protected', 'true');
          if (item.gender) formData.append('gender', item.gender);
          if (item.number) formData.append('number', item.number);

          const res = await fetch(`${API}/admin/translations`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${adminToken}` },
            body: formData
          });

          if (res.ok) {
            saved++;
          } else {
            failed++;
          }
        }
      } catch (err) {
        failed++;
      }
    }

    let successMsg = `✅ Saved: ${saved} new`;
    if (updated > 0) successMsg += `, ${updated} updated`;
    if (variants > 0) successMsg += `, ${variants} variants`;
    if (skipped > 0) successMsg += `, ${skipped} skipped`;
    if (failed > 0) successMsg += ` (${failed} failed)`;
    
    setBulkVerbs(prev => ({ 
      ...prev, 
      isLoading: false,
      success: successMsg,
      preview: null,
      summary: null,
      latinText: '',
      arabicText: ''
    }));
    
    loadData();
  };
  
  // Helper function to add a variant row
  const addVariantRow = () => {
    setAiHelper(prev => ({
      ...prev,
      variantRows: [...prev.variantRows, { latin: "", arabic: "", gender: "", number: "" }]
    }));
  };
  
  // Helper function to remove a variant row
  const removeVariantRow = (index) => {
    setAiHelper(prev => ({
      ...prev,
      variantRows: prev.variantRows.filter((_, i) => i !== index)
    }));
  };
  
  // Helper function to update a variant row
  const updateVariantRow = (index, field, value) => {
    setAiHelper(prev => ({
      ...prev,
      variantRows: prev.variantRows.map((row, i) => 
        i === index ? { ...row, [field]: value } : row
      )
    }));
  };

  useEffect(() => {
    if (translationSubTab === 'feedback') {
      loadFeedbackSubTabData(feedbackSubTab);
    }
  }, [feedbackSubTab, translationSubTab]);

  useEffect(() => {
    if (translationSubTab === 'specialwords') {
      loadSpecialWords();
    }
  }, [translationSubTab]);

  // Load badges when progress sub-tab opens
  useEffect(() => {
    if (translationSubTab === 'progress') {
      loadBadgesData();
    }
  }, [translationSubTab]);

  useEffect(() => {
    loadData();
  }, [currentPage]);

  const loadFeedbackSubTabData = async (subTab) => {
    try {
      if (subTab === 'requests') {
        const requestsRes = await fetch(`${API}/admin/user-requests`, { headers: authHeaders });
        if (requestsRes.ok) {
          const requestsData = await requestsRes.json();
          if (requestsData.requests) {
            setUserRequests(Array.isArray(requestsData.requests) ? requestsData.requests : []);
            setUserRequestsCount({ total: requestsData.total_count, limit: requestsData.limit });
          } else {
            setUserRequests(Array.isArray(requestsData) ? requestsData : []);
          }
        } else {
          setUserRequests([]);
        }
      } else if (subTab === 'suggestions') {
        const suggestionsRes = await fetch(`${API}/admin/word-suggestions?t=${Date.now()}`, {
          headers: authHeaders, cache: 'no-store'
        });
        if (suggestionsRes.ok) {
          const suggestionsData = await suggestionsRes.json();
          setWordSuggestions(Array.isArray(suggestionsData) ? suggestionsData : []);
        } else {
          setWordSuggestions([]);
        }
      } else if (subTab === 'feedback') {
        const feedbackRes = await fetch(`${API}/admin/feedback`, { headers: authHeaders });
        if (feedbackRes.ok) {
          const feedbackData = await feedbackRes.json();
          const fList = feedbackData.feedback || feedbackData;
          setFeedback(Array.isArray(fList) ? fList : []);
        } else {
          setFeedback([]);
        }
      }
    } catch (error) {
      console.error("Error loading feedback sub-tab data:", error);
    }
  };

  useEffect(() => {
    if (translationSubTab === 'feedback') {
      loadFeedbackSubTabData(feedbackSubTab);
    }
  }, [feedbackSubTab, translationSubTab]);

  // Load special words when tab opens
  const loadSpecialWords = async () => {
    try {
      const res = await fetch(`${API}/admin/translations/special-words`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSpecialWords(data.specialWords || []);
      }
    } catch (err) {
      console.error('Error loading special words:', err);
    }
  };

  useEffect(() => {
    if (translationSubTab === 'specialwords') {
      loadSpecialWords();
    }
  }, [translationSubTab]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearchAndFilter = () => {
    setCurrentPage(1); // Reset to page 1 when searching
    loadData();
  };

  const handleSaveTranslation = async (id, sourceText, translatedText, latinScript = '', gender = '', number = '', audioUrl = '', audioEnabled = true, status = "verified", note = '', skipDuplicateCheck = false) => {
    try {
      // Check if sourceText changed and if it duplicates another entry
      if (!skipDuplicateCheck && sourceText?.trim()) {
        const currentEntry = translations.find(t => t.id === id);
        const normalizedNew = sourceText.toLowerCase().trim();
        const normalizedOld = (currentEntry?.sourceText || '').toLowerCase().trim();

        if (normalizedNew !== normalizedOld) {
          // sourceText changed — check for duplicates
          const checkRes = await fetch(
            `${API}/admin/translations/check-duplicate?sourceText=${encodeURIComponent(sourceText)}&sourceLang=${currentEntry?.sourceLang || 'en'}`,
            { headers: { 'Authorization': `Bearer ${adminToken}` } }
          );
          if (checkRes.ok) {
            const checkData = await checkRes.json();
            if (checkData.exists && checkData.entries?.length > 0) {
              // Show edit duplicate popup
              setEditDuplicateCheck({
                show: true,
                pendingArgs: { id, sourceText, translatedText, latinScript, gender, number, audioUrl, audioEnabled, status, note },
                existingEntries: checkData.entries
              });
              return;
            }
          }
        }
      }

      const formData = new FormData();
      formData.append('sourceText', sourceText);
      formData.append('translatedText', translatedText);
      formData.append('latinScript', latinScript || '');
      formData.append('gender', gender || '');
      formData.append('number', number || '');
      formData.append('status', status);
      formData.append('audioUrl', audioUrl);
      formData.append('audioEnabled', audioEnabled);
      formData.append('note', note || '');

      const response = await fetch(`${API}/admin/translations/${id}`, {
        method: "PUT",
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (response.ok) {
        showNotification("Translation updated successfully!", "success");
        loadData();
        setEditingTranslation(null);
      } else {
        throw new Error("Failed to update translation");
      }
    } catch (error) {
      console.error("Error updating translation:", error);
      showNotification("Failed to update translation", "error");
    }
  };

  // Update a single field on a card (local state only — no API call)
  const handleCardFieldChange = (translationId, field, value) => {
    setCardEdits(prev => ({
      ...prev,
      [translationId]: {
        ...(prev[translationId] || {}),
        [field]: value
      }
    }));
  };

  // Save all inline card edits to DB via PATCH
  const handleQuickUpdate = async (translationId) => {
    const edits = cardEdits[translationId];
    if (!edits || Object.keys(edits).length === 0) return;
    try {
      const response = await fetch(`${API}/admin/translations/${translationId}/quick-update`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(edits)
      });
      if (response.ok) {
        // Update local translations state (optimistic)
        setTranslations(prev => prev.map(t =>
          t.id === translationId ? { ...t, ...edits } : t
        ));
        // Clear edits for this card
        setCardEdits(prev => {
          const next = { ...prev };
          delete next[translationId];
          return next;
        });
        showNotification("Saved!", "success");
      } else {
        throw new Error("Failed to update");
      }
    } catch (error) {
      console.error("Quick update error:", error);
      showNotification("Failed to save changes", "error");
    }
  };

  // Save ALL pending card edits at once
  const handleQuickUpdateAll = async () => {
    const ids = Object.keys(cardEdits);
    if (ids.length === 0) return;
    let saved = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        const response = await fetch(`${API}/admin/translations/${id}/quick-update`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(cardEdits[id])
        });
        if (response.ok) {
          setTranslations(prev => prev.map(t =>
            t.id === id ? { ...t, ...cardEdits[id] } : t
          ));
          saved++;
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }
    if (saved > 0) {
      setCardEdits({});
      showNotification(`Saved ${saved} translation(s)${failed > 0 ? `, ${failed} failed` : ''}`, saved > 0 ? 'success' : 'error');
    } else {
      showNotification(`Failed to save changes`, 'error');
    }
  };

  // Actually save the translation (called directly or after override confirmation)
  const saveTranslation = async (asNewMeaning = false) => {
    try {
      const formData = new FormData();
      formData.append('sourceText', newTranslation.sourceText);
      formData.append('sourceLang', newTranslation.sourceLang);
      formData.append('translatedText', newTranslation.translatedText);
      formData.append('latinScript', newTranslation.latinScript || '');
      formData.append('gender', newTranslation.gender || '');
      formData.append('number', newTranslation.number || '');
      formData.append('status', newTranslation.status);
      formData.append('audioUrl', newTranslation.audioUrl || '');
      formData.append('audioEnabled', newTranslation.audioEnabled);

      // If adding as new meaning, send subtitle as note + update existing entry's note + primary choice
      if (asNewMeaning && duplicateCheck.existing) {
        formData.append('note', newEntrySubtitle || '');
        formData.append('newMeaning', 'true');
        formData.append('existingEntryId', duplicateCheck.existing.id || '');
        formData.append('existingEntryNote', existingEntrySubtitle || '');
        formData.append('primaryChoice', newMeaningPrimary || 'existing');
      } else {
        formData.append('note', newTranslation.note || '');
      }

      const response = await fetch(`${API}/admin/translations`, {
        method: "POST",
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        // Now save any additional variants
        const validVariants = (newTranslation.variantRows || []).filter(v => v.latin?.trim() || v.arabic?.trim());

        if (validVariants.length > 0) {
          let variantsSaved = 0;
          for (const variant of validVariants) {
            try {
              const variantFormData = new FormData();
              variantFormData.append('sourceText', newTranslation.sourceText);
              variantFormData.append('sourceLang', newTranslation.sourceLang);
              variantFormData.append('translatedText', variant.arabic || '');
              variantFormData.append('latinScript', variant.latin || '');
              variantFormData.append('gender', variant.gender || '');
              variantFormData.append('number', variant.number || '');
              variantFormData.append('audioUrl', '');

              const variantRes = await fetch(`${API}/admin/translations/add-variant`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${adminToken}` },
                body: variantFormData
              });

              if (variantRes.ok) {
                variantsSaved++;
              }
            } catch (err) {
              console.error('Error saving variant:', err);
            }
          }
          showNotification(`Translation ${result.updated ? 'updated' : 'created'} + ${variantsSaved} variant(s) saved!`, "success");
        } else {
          showNotification(result.updated ? "Translation updated (overridden)!" : "Translation created successfully!", "success");
        }

        // Reset form + disambiguation state
        setNewTranslation({
          sourceText: "",
          sourceLang: "en",
          translatedText: "",
          latinScript: "",
          gender: "",
          number: "",
          status: "verified",
          audioUrl: "",
          audioEnabled: true,
          note: "",
          variantRows: []
        });
        setDuplicateCheck({ show: false, existing: null, entries: [] });
        setNewMeaningMode(false);
        setNewMeaningPrimary('existing');
        setExistingEntrySubtitle('');
        setNewEntrySubtitle('');
        loadData();
      } else {
        throw new Error("Failed to create translation");
      }
    } catch (error) {
      console.error("Error creating translation:", error);
      showNotification("Failed to create translation", "error");
    }
  };

  const handleCreateTranslation = async (e) => {
    e.preventDefault();
    if (!newTranslation.sourceText?.trim() || !newTranslation.translatedText?.trim()) {
      showNotification("Source text and translation are required", "error");
      return;
    }
    try {
      // Check for duplicates first
      const checkRes = await fetch(
        `${API}/admin/translations/check-duplicate?sourceText=${encodeURIComponent(newTranslation.sourceText)}&sourceLang=${newTranslation.sourceLang}`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        if (checkData.exists && checkData.entries?.length > 0) {
          // Show duplicate modal with existing translation info
          setDuplicateCheck({ show: true, existing: checkData.entries[0], entries: checkData.entries });
          setNewMeaningMode(false);
          setExistingEntrySubtitle(checkData.entries[0]?.note || '');
          setNewEntrySubtitle('');
          return;
        }
      }
      // No duplicate — save directly
      await saveTranslation();
    } catch (error) {
      console.error("Error checking duplicate:", error);
      // If check fails, save anyway
      await saveTranslation();
    }
  };

  const handleDeleteTranslation = async (id) => {
    try {
      const response = await fetch(`${API}/admin/translations/${id}`, {
        method: "DELETE",
        headers: authHeaders
      });

      if (response.ok) {
        showNotification("Translation deleted successfully!", "success");
        setDeleteConfirm(null);
        loadData();
      } else {
        throw new Error("Failed to delete translation");
      }
    } catch (error) {
      console.error("Error deleting translation:", error);
      showNotification("Failed to delete translation", "error");
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await fetch(`${API}/admin/export/${format}`, {
        headers: authHeaders
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `darija_translations.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        showNotification(`Exported as ${format.toUpperCase()}!`, "success");
      } else {
        throw new Error("Export failed");
      }
    } catch (error) {
      console.error("Export error:", error);
      showNotification("Export failed", "error");
    }
  };

  const handleToggleSetting = async (settingName, value) => {
    console.log(`[handleToggleSetting] Called with:`, { settingName, value });
    console.log(`[handleToggleSetting] Current settings:`, settings);
    
    // Update local state immediately for responsive UI
    console.log(`[handleToggleSetting] Updating local state...`);
    setSettings(prev => {
      const newSettings = {
        ...prev,
        [settingName]: value
      };
      console.log(`[handleToggleSetting] New settings state:`, newSettings);
      return newSettings;
    });
    
    try {
      const url = `${API}/admin/settings?${settingName}=${value}`;
      console.log('[handleToggleSetting] Request URL:', url);
      console.log('[handleToggleSetting] Auth headers:', authHeaders);
      
      const response = await fetch(url, {
        method: "PUT",
        headers: authHeaders
      });

      console.log('[handleToggleSetting] Response status:', response.status);
      console.log('[handleToggleSetting] Response ok:', response.ok);

      if (response.ok) {
        showNotification(
          settingName === 'maintenanceMode' 
            ? (value ? 'Maintenance mode enabled! Users will see maintenance page.' : 'Maintenance mode disabled! App is accessible.')
            : "Settings updated successfully!", 
          "success"
        );
        // Reload settings in main app
        if (onSettingsUpdate) {
          await onSettingsUpdate();
        }
      } else {
        const error = await response.text();
        console.error('Error response:', error);
        // Revert local state on error
        setSettings(prev => ({
          ...prev,
          [settingName]: !value
        }));
        throw new Error("Failed to update settings");
      }
    } catch (error) {
      console.error("Settings error:", error);
      showNotification(`Failed to update settings: ${error.message}`, "error");
    }
  };

  const handleUpdateIconConfig = async (configType, newConfig) => {
    try {
      // Update local state immediately for responsive UI
      setSettings(prev => ({
        ...prev,
        [configType]: newConfig
      }));

      // Send to backend
      const response = await fetch(`${API}/admin/settings`, {
        method: "PUT",
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          [configType]: newConfig
        })
      });

      if (response.ok) {
        // Also update main app settings
        if (onSettingsUpdate) {
          await onSettingsUpdate();
        }
      } else {
        throw new Error("Failed to update icon config");
      }
    } catch (error) {
      console.error("Icon config error:", error);
      showNotification("Failed to update icon configuration", "error");
    }
  };

  const handleDeleteSuggestion = async (id) => {
    try {
      const response = await fetch(`${API}/admin/word-suggestions/${id}`, {
        method: "DELETE",
        headers: authHeaders
      });

      if (response.ok) {
        showNotification("Suggestion deleted", "success");
        loadFeedbackSubTabData('suggestions');
      } else {
        throw new Error("Failed to delete suggestion");
      }
    } catch (error) {
      console.error("Delete error:", error);
      showNotification("Failed to delete suggestion", "error");
    }
  };

  const handleQuickAddTranslation = async (suggestion) => {
    const translation = quickTranslations[suggestion.id];
    if (!translation || !translation.trim()) {
      showNotification("Please enter a translation", "error");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('sourceText', suggestion.word);
      formData.append('sourceLang', suggestion.language);
      formData.append('translatedText', translation);
      formData.append('status', 'verified');

      const response = await fetch(`${API}/admin/translations`, {
        method: 'POST',
        headers: authHeadersForForm,
        body: formData
      });

      if (response.ok) {
        showNotification(`✅ Added "${suggestion.word}" to database!`, 'success');
        
        // Update suggestion status to approved
        const statusFormData = new FormData();
        statusFormData.append('status', 'approved');
        
        await fetch(`${API}/admin/word-suggestions/${suggestion.id}/status`, {
          method: 'PUT',
          headers: authHeadersForForm,
          body: statusFormData
        });
        
        // Clear the input
        setQuickTranslations(prev => {
          const newState = {...prev};
          delete newState[suggestion.id];
          return newState;
        });

        loadFeedbackSubTabData('suggestions');
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to add translation', 'error');
      }
    } catch (error) {
      console.error('Error adding translation:', error);
      showNotification('Failed to add translation', 'error');
    }
  };

  const handleAddWordFromSuggestion = async (suggestion) => {
    // Pre-fill the Add Translation form with suggestion data
    setNewTranslation({
      sourceText: suggestion.word,
      sourceLang: suggestion.language,
      translatedText: "",
      status: "verified",
      audioUrl: "",
      audioEnabled: true,
      note: ""
    });
    
    // Update suggestion status to approved
    try {
      const formData = new FormData();
      formData.append('status', 'approved');
      
      await fetch(`${API}/admin/word-suggestions/${suggestion.id}/status`, {
        method: "PUT",
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
    } catch (error) {
      console.error("Error updating suggestion status:", error);
    }
  };

  const handleSaveSuggestion = async (id) => {
    try {
      const formData = new FormData();
      formData.append('word', editSuggestionData.word);
      formData.append('language', editSuggestionData.language);
      formData.append('message', editSuggestionData.message || '');

      const response = await fetch(`${API}/admin/word-suggestions/${id}`, {
        method: "PUT",
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (response.ok) {
        showNotification("Suggestion updated successfully!", "success");
        setEditingSuggestion(null);
        loadFeedbackSubTabData('suggestions');
      } else {
        throw new Error("Failed to update suggestion");
      }
    } catch (error) {
      console.error("Error updating suggestion:", error);
      showNotification("Failed to update suggestion", "error");
    }
  };

  const handleUpdateSuggestionStatus = async (id, status) => {
    try {
      const formData = new FormData();
      formData.append('status', status);

      const response = await fetch(`${API}/admin/word-suggestions/${id}/status`, {
        method: "PUT",
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (response.ok) {
        showNotification(`Suggestion ${status} successfully!`, "success");
        loadFeedbackSubTabData('suggestions');
      } else {
        throw new Error("Failed to update suggestion status");
      }
    } catch (error) {
      console.error("Error updating suggestion status:", error);
      showNotification("Failed to update suggestion status", "error");
    }
  };

  // Load translations data
  const loadData = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '30'
      });

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      if (langFilter) {
        params.append('sourceLang', langFilter);
      }

      const translationsRes = await fetch(`${API}/admin/translations?${params.toString()}`, {
        headers: authHeaders
      });
      if (translationsRes.ok) {
        const translationsData = await translationsRes.json();
        const tList = translationsData.translations || translationsData;
        setTranslations(Array.isArray(tList) ? tList : []);
        if (translationsData.pagination) {
          setPagination(translationsData.pagination);
        }
      } else {
        setTranslations([]);
      }
      fetchTranslationStats();
      loadGroqSettings();
      loadDeepseekSettings();
      loadGeminiSettings();
      loadOpenaiSettings();
      loadAllPendingCounts();
      loadAiCacheStats();
      // Load all languages (including inactive) for settings tab
      try {
        const langRes = await fetch(`${API}/admin/languages`, { headers: authHeaders });
        if (langRes.ok) {
          const langData = await langRes.json();
          setAdminLanguages(Array.isArray(langData) ? langData : []);
        }
      } catch (e) { /* ignore */ }
    } catch (error) {
      console.error("Error loading data:", error);
      showNotification("Failed to load data", "error");
    }
  };

  const sourceLanguages = languages.filter(lang => lang.code !== 'auto');

  return (
    <div className="space-y-6">

      {/* 🔒 PROTECTED: Manual Translation Stats - AI must NEVER delete this data */}
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📊</span>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              My Translation Database
            </h3>
            <span className="text-xs px-2 py-1 bg-red-500 text-white rounded-full font-bold">
              🔒 PROTECTED
            </span>
          </div>
          <button
            type="button"
            onClick={fetchTranslationStats}
            className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
          >
            🔄 Refresh
          </button>
        </div>
        
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
          ⚠️ These are YOUR manual entries. AI will NEVER delete, modify, or remove them.
        </p>
        
        <div className="grid grid-cols-3 gap-4">
          {/* Total Entries */}
          <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
            <div className={`text-3xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
              {translationStats.isLoading ? '...' : translationStats.total.toLocaleString()}
            </div>
            <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              📚 Total Entries
            </div>
          </div>
          
          {/* Single Words */}
          <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
            <div className={`text-3xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              {translationStats.isLoading ? '...' : translationStats.singleWords.toLocaleString()}
            </div>
            <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              🔤 Single Words
            </div>
          </div>
          
          {/* Sentences/Phrases */}
          <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
            <div className={`text-3xl font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
              {translationStats.isLoading ? '...' : translationStats.sentences.toLocaleString()}
            </div>
            <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              💬 Sentences/Phrases
            </div>
          </div>
        </div>
      </div>

      {/* Sub-Tab Navigation */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'translations', label: 'Translations', icon: '📝' },
          { id: 'pagesettings', label: 'Page Settings', icon: '🏷️' },
          { id: 'importexport', label: 'Import/Export', icon: '📦' },
          { id: 'groq', label: 'Groq AI', icon: '🤖' },
          { id: 'deepseek', label: 'DeepSeek', icon: '🧠' },
          { id: 'openai', label: 'OpenAI', icon: '⚡' },
          { id: 'gemini', label: 'Gemini AI', icon: '✨' },
          { id: 'settings', label: 'Settings', icon: '⚙️' },
          { id: 'audiovoice', label: 'Audio & Voice', icon: '🔊' },
          { id: 'favorites', label: 'Favorites', icon: '❤️' },
          { id: 'gendericons', label: 'Gender Icons', icon: '👥' },
          { id: 'limits', label: 'Limits', icon: '🔢' },
          { id: 'autotranslation', label: 'Auto-Translation', icon: '🤖' },
          { id: 'feedback', label: 'Feedback', icon: '💬' },
          { id: 'specialwords', label: 'Special Words', icon: '✨' },
          { id: 'audit', label: 'Audit', icon: '🔍' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setTranslationSubTab(tab.id);
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              translationSubTab === tab.id
                ? 'bg-emerald-600 text-white'
                : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Page Settings — controls the /translation page title + every
          user-visible label on that page. Saved values are also mirrored
          into the SEO Meta Tags Manager for /translation (one-way write). */}
      {translationSubTab === 'pagesettings' && (
        <TranslationPageSettingsTab
          API={API}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          showNotification={showNotification}
        />
      )}

      {/* Export Translations */}
      {translationSubTab === 'importexport' && (<>
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-blue-500' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300'
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">📥</span>
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Export Translations
          </h3>
        </div>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Download all your translations in different formats
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleExport('csv')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            📁 Export CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
          >
            📁 Export JSON
          </button>
          <button
            onClick={() => handleExport('txt')}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm"
          >
            📁 Export TXT
          </button>
        </div>
      </div>

      {/* Import Translations */}
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-emerald-900/30 to-green-900/30 border-emerald-500' : 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-300'
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">📤</span>
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Import Translations
          </h3>
        </div>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Upload translations from CSV, TXT, or JSON files
        </p>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".csv,.txt,.json"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;

              const formData = new FormData();
              formData.append('file', file);

              try {
                const response = await fetch(`${API}/admin/translations/bulk-import`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${adminToken}` },
                  body: formData
                });

                if (response.ok) {
                  const result = await response.json();
                  showNotification(
                    `Import complete! Created: ${result.created}, Updated: ${result.updated}, Errors: ${result.errors}`,
                    'success'
                  );
                  loadData();
                } else {
                  const error = await response.json();
                  showNotification(error.detail || 'Import failed', 'error');
                }
              } catch (error) {
                console.error('Import error:', error);
                showNotification('Failed to import file', 'error');
              }

              e.target.value = '';
            }}
            className={`flex-1 px-3 py-2 border rounded-lg text-sm ${
              isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
            }`}
          />
        </div>
        <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Supports CSV (comma-separated), TXT (tab-separated), and JSON (array of objects)
        </p>
      </div>
      </>)}

      {/* Groq API Key Configuration */}
      {translationSubTab === 'groq' && (<>

      {/* Sub-tab navigation */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setAiSubTab(prev => ({...prev, groq: 'settings'}))}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            aiSubTab.groq === 'settings'
              ? 'bg-orange-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Settings
        </button>
        <button
          onClick={() => { setAiSubTab(prev => ({...prev, groq: 'queue'})); loadProviderPending('groq'); }}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            aiSubTab.groq === 'queue'
              ? 'bg-orange-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Groq Queue{pendingCounts.groq > 0 ? ` (${pendingCounts.groq})` : ''}
        </button>
        <button
          onClick={() => { setAiSubTab(prev => ({...prev, groq: 'approved'})); loadProviderApproved('groq'); }}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            aiSubTab.groq === 'approved'
              ? 'bg-green-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Approved{approvedCounts.groq > 0 ? ` (${approvedCounts.groq})` : ''}
        </button>
      </div>

      {aiSubTab.groq === 'settings' && (<>
      <div className={`p-3 rounded-lg border mb-4 ${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
          Word-by-word database lookup is limited to <strong>{settings.maxWordsForDatabase || 4} words</strong>. Phrases longer than {settings.maxWordsForDatabase || 4} words skip the database and go directly to AI for a natural translation. You can change this limit in <strong>Translations → Limits</strong> tab.
        </p>
      </div>
      <div className={`p-4 rounded-lg border-2 mb-4 ${
        isDarkMode ? 'bg-gradient-to-r from-orange-900/30 to-yellow-900/30 border-orange-500' : 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🔑</span>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Groq API Key
            </h3>
            {groqSettings.apiKeySet && groqSettings.enabled ? (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                Active
              </span>
            ) : groqSettings.apiKeySet && !groqSettings.enabled ? (
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                Disabled
              </span>
            ) : (
              <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full font-medium">
                Not Set
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Enable/Disable Toggle */}
            <button
              onClick={toggleGroq}
              disabled={!groqSettings.apiKeySet}
              className={`px-3 py-1 text-sm rounded-lg font-medium transition-all ${
                !groqSettings.apiKeySet ? 'bg-gray-400 cursor-not-allowed text-gray-200' :
                groqSettings.enabled
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-300' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
              }`}
            >
              {groqSettings.enabled ? 'Enabled' : 'Disabled'}
            </button>
            <button
              onClick={() => {
                loadGroqSettings();
                setGroqSettings(prev => ({ ...prev, showApiKeyInput: !prev.showApiKeyInput }));
              }}
              className={`px-3 py-1 text-sm rounded-lg ${
                isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
              }`}
            >
              {groqSettings.showApiKeyInput ? '✕ Close' : '⚙️ Configure'}
            </button>
          </div>
        </div>

        <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Required for AI translation. Get your free API key from{' '}
          <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
            console.groq.com/keys
          </a>
        </p>

        {groqSettings.showApiKeyInput && (
          <div className="space-y-3">
            {groqSettings.apiKeySet && (
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Current key: <span className="font-mono">{groqSettings.apiKeyMasked}</span>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="password"
                value={groqSettings.newApiKey}
                onChange={(e) => setGroqSettings(prev => ({ ...prev, newApiKey: e.target.value, message: null }))}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
                placeholder="Enter your Groq API key (gsk_...)"
              />
              <button
                onClick={saveGroqApiKey}
                disabled={!groqSettings.newApiKey.trim() || groqSettings.isLoading}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  !groqSettings.newApiKey.trim() || groqSettings.isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white'
                }`}
              >
                {groqSettings.isLoading ? '⏳ Saving...' : '💾 Save Key'}
              </button>
            </div>
            {groqSettings.apiKeySet && (
              <button
                onClick={async () => {
                  if (!window.confirm('Remove Groq API Key?\nAI translation will stop working until a new key is added.')) return;
                  setGroqSettings(prev => ({ ...prev, isLoading: true }));
                  try {
                    const formData = new FormData();
                    formData.append('api_key', '');
                    const res = await fetch(`${API}/admin/translation-groq/settings`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${adminToken}` },
                      body: formData
                    });
                    if (res.ok) {
                      setGroqSettings(prev => ({ ...prev, apiKeySet: false, apiKeyMasked: '', newApiKey: '', showApiKeyInput: false, isLoading: false, message: { type: 'success', text: 'API key removed' } }));
                      showNotification('Groq API key removed', 'success');
                      await loadGroqSettings();
                    }
                  } catch (err) {
                    setGroqSettings(prev => ({ ...prev, isLoading: false }));
                    showNotification('Failed to remove key', 'error');
                  }
                }}
                disabled={groqSettings.isLoading}
                className="w-full px-3 py-2 text-sm rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-all font-medium"
              >
                Remove API Key
              </button>
            )}
            {groqSettings.message && (
              <p className={`text-sm ${groqSettings.message.type === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                {groqSettings.message.type === 'success' ? '✓' : '✕'} {groqSettings.message.text}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Groq Prompt Editor */}
      <div className={`p-4 rounded-lg border-2 mb-4 ${
        isDarkMode ? 'bg-gray-800 border-orange-700' : 'bg-orange-50 border-orange-200'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📝</span>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Groq System Prompt
            </h3>
          </div>
          <button
            onClick={() => setGroqSettings(prev => ({ ...prev, showPromptEditor: !prev.showPromptEditor }))}
            className={`px-3 py-1 text-sm rounded-lg ${
              isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
            }`}
          >
            {groqSettings.showPromptEditor ? '✕ Close' : '⚙️ Edit Prompt'}
          </button>
        </div>

        <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Tells Groq how to translate to Moroccan Darija. Edit and save to update behavior on the next translation.
        </p>

        {groqSettings.showPromptEditor && (
          <div className="space-y-3">
            <div>
              <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                System Prompt
              </label>
              <textarea
                value={groqSettings.systemPrompt}
                onChange={(e) => setGroqSettings(prev => ({ ...prev, systemPrompt: e.target.value, promptMessage: null }))}
                rows={14}
                className={`w-full mt-1 px-3 py-2 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'
                }`}
                placeholder="You are a Moroccan Darija translator..."
              />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Model
                </label>
                <input
                  type="text"
                  value={groqSettings.model}
                  onChange={(e) => setGroqSettings(prev => ({ ...prev, model: e.target.value, promptMessage: null }))}
                  className={`w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'
                  }`}
                  placeholder="llama-3.3-70b-versatile"
                />
              </div>
              <div className="w-40">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Temperature
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  value={groqSettings.temperature}
                  onChange={(e) => setGroqSettings(prev => ({ ...prev, temperature: e.target.value, promptMessage: null }))}
                  className={`w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'
                  }`}
                />
              </div>
            </div>

            <button
              onClick={saveGroqPrompt}
              disabled={groqSettings.promptIsLoading}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                groqSettings.promptIsLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white'
              }`}
            >
              {groqSettings.promptIsLoading ? '⏳ Saving...' : '💾 Save Prompt'}
            </button>

            {groqSettings.promptMessage && (
              <p className={`text-sm ${groqSettings.promptMessage.type === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                {groqSettings.promptMessage.type === 'success' ? '✓' : '✕'} {groqSettings.promptMessage.text}
              </p>
            )}
          </div>
        )}
      </div>

      {/* AI Translation Helper (Groq - FREE) */}
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-500' : 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-300'
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">🤖</span>
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            AI Translation Helper
          </h3>
          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
            Groq - FREE
          </span>
          {!groqSettings.apiKeySet && (
            <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-medium">
              ⚠️ API Key Required
            </span>
          )}
        </div>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Generate Darija translations using AI. Review, edit, and save to your database.
        </p>
        
        <div className="space-y-4">
          {/* Source Text Input */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              📝 Source Text (English)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={aiHelper.sourceText}
                onChange={(e) => setAiHelper({...aiHelper, sourceText: e.target.value, error: null})}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
                placeholder="Enter English text (e.g., I need to go)"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!aiHelper.sourceText.trim()) {
                    setAiHelper({...aiHelper, error: 'Please enter text to translate'});
                    return;
                  }
                  setAiHelper({...aiHelper, isLoading: true, error: null});
                  try {
                    const formData = new FormData();
                    formData.append('text', aiHelper.sourceText);
                    const res = await fetch(`${API}/admin/ai-translate`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${adminToken}` },
                      body: formData
                    });
                    const data = await res.json();
                    if (res.ok && data.success) {
                      setAiHelper({
                        ...aiHelper,
                        arabicScript: data.arabicScript || '',
                        latinScript: data.latinScript || '',
                        isLoading: false,
                        error: null
                      });
                    } else {
                      setAiHelper({...aiHelper, isLoading: false, error: data.detail || 'Translation failed'});
                    }
                  } catch (err) {
                    setAiHelper({...aiHelper, isLoading: false, error: 'Network error'});
                  }
                }}
                disabled={aiHelper.isLoading}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  aiHelper.isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white'
                }`}
              >
                {aiHelper.isLoading ? '⏳ Generating...' : '🔍 Generate from Groq'}
              </button>
            </div>
            {aiHelper.error && (
              <p className="text-red-500 text-sm mt-1">❌ {aiHelper.error}</p>
            )}
          </div>
          
          {/* Generated Results - NEW ORDER: English > Latin > Arabic > Gender > Number > Audio */}
          {(aiHelper.arabicScript || aiHelper.latinScript) && (
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                ✅ AI Generated - Edit if needed, then save:
              </p>
              
              <div className="space-y-3">
                {/* English (Source) - READ ONLY for verification */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    🇬🇧 English (Source)
                  </label>
                  <input
                    type="text"
                    value={aiHelper.sourceText}
                    readOnly
                    className={`w-full px-3 py-2 border rounded-lg ${
                      isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-gray-100 border-gray-300 text-gray-800'
                    }`}
                  />
                </div>
                
                {/* Latin Darija Translation */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    🔤 Latin Darija Translation
                  </label>
                  <input
                    type="text"
                    value={aiHelper.latinScript}
                    onChange={(e) => setAiHelper({...aiHelper, latinScript: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                    }`}
                    placeholder="Latin script (e.g., salam)"
                  />
                </div>

                {/* Arabic Script Darija */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    🇲🇦 Arabic Script Darija (Optional)
                  </label>
                  <input
                    type="text"
                    value={aiHelper.arabicScript}
                    onChange={(e) => setAiHelper({...aiHelper, arabicScript: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-right ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                    }`}
                    dir="rtl"
                    placeholder="Arabic script (e.g., سلام)"
                  />
                </div>

                {/* Gender (Optional) */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    👥 Gender (Optional)
                  </label>
                  <select
                    value={aiHelper.gender}
                    onChange={(e) => setAiHelper({...aiHelper, gender: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                    }`}
                  >
                    <option value="">None</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="both">Both</option>
                  </select>
                </div>

                {/* Number (Optional) */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    🔢 Number (Optional)
                  </label>
                  <select
                    value={aiHelper.number}
                    onChange={(e) => setAiHelper({...aiHelper, number: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                    }`}
                  >
                    <option value="">None</option>
                    <option value="singular">Singular</option>
                    <option value="plural">Plural</option>
                  </select>
                </div>

                {/* Audio URL (Optional) */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    🔊 Audio URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={aiHelper.audioUrl}
                    onChange={(e) => setAiHelper({...aiHelper, audioUrl: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                    }`}
                    placeholder="https://..."
                  />
                </div>

                {/* + Add Variant button */}
                <button
                  type="button"
                  onClick={addVariantRow}
                  className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm"
                >
                  + Add Variant
                </button>

                {/* VARIANT ROWS - Additional Variants */}
                {aiHelper.variantRows && aiHelper.variantRows.length > 0 && (
                  <div className={`p-3 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-400 bg-blue-50'}`}>
                    <p className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      📝 Additional Variants ({aiHelper.variantRows.length}):
                    </p>
                    {aiHelper.variantRows.map((row, index) => (
                      <div key={index} className={`mb-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                        <div className="flex justify-between items-center mb-2">
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Variant #{index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeVariantRow(index)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              🔤 Latin
                            </label>
                            <input
                              type="text"
                              value={row.latin}
                              onChange={(e) => updateVariantRow(index, 'latin', e.target.value)}
                              className={`w-full px-2 py-1 border rounded text-sm ${
                                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                              }`}
                              placeholder="Latin variant"
                            />
                          </div>
                          <div>
                            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              🇲🇦 Arabic Script
                            </label>
                            <input
                              type="text"
                              value={row.arabic}
                              onChange={(e) => updateVariantRow(index, 'arabic', e.target.value)}
                              className={`w-full px-2 py-1 border rounded text-sm text-right ${
                                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                              }`}
                              dir="rtl"
                              placeholder="Arabic variant"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                👥 Gender
                              </label>
                              <select
                                value={row.gender || ''}
                                onChange={(e) => updateVariantRow(index, 'gender', e.target.value)}
                                className={`w-full px-2 py-1 border rounded text-sm ${
                                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                                }`}
                              >
                                <option value="">No Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="both">Both</option>
                              </select>
                            </div>
                            <div>
                              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                🔢 Number
                              </label>
                              <select
                                value={row.number || ''}
                                onChange={(e) => updateVariantRow(index, 'number', e.target.value)}
                                className={`w-full px-2 py-1 border rounded text-sm ${
                                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                                }`}
                              >
                                <option value="">No Number</option>
                                <option value="singular">Singular</option>
                                <option value="plural">Plural</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addVariantRow}
                      className={`w-full py-2 border-2 border-dashed rounded-lg text-sm ${
                        isDarkMode ? 'border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400' : 'border-gray-300 text-gray-500 hover:border-blue-500 hover:text-blue-600'
                      }`}
                    >
                      + Add Another Variant
                    </button>
                  </div>
                )}
              </div>
              
              {/* Duplicate Warning Modal */}
              {aiHelper.duplicateFound && (
                <div className={`p-4 rounded-lg border-2 mb-4 ${isDarkMode ? 'bg-yellow-900/30 border-yellow-500' : 'bg-yellow-50 border-yellow-400'}`}>
                  <p className={`font-semibold mb-2 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    ⚠️ Duplicate Found! "{aiHelper.sourceText}" already exists:
                  </p>
                  <div className="space-y-2 mb-3">
                    {aiHelper.existingTranslations.map((t, i) => (
                      <div key={i} className={`p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-white'} flex items-center gap-2`}>
                        {t.isPrimary && <span className="text-yellow-500">⭐</span>}
                        <span className="font-medium">{t.latinScript}</span>
                        <span className="text-gray-500">|</span>
                        <span dir="rtl">{t.translatedText}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => setAiHelper({...aiHelper, duplicateFound: false, existingTranslations: []})}
                      className="px-3 py-1 bg-gray-500 text-white rounded-lg text-sm"
                    >
                      Keep Existing
                    </button>
                    <button
                      onClick={async () => {
                        // Replace - update existing primary
                        setAiHelper({...aiHelper, isLoading: true});
                        const formData = new FormData();
                        formData.append('sourceText', aiHelper.sourceText);
                        formData.append('sourceLang', 'en');
                        formData.append('translatedText', aiHelper.arabicScript);
                        formData.append('latinScript', aiHelper.latinScript || '');
                        formData.append('gender', aiHelper.gender || '');
                        formData.append('number', aiHelper.number || '');
                        formData.append('status', 'verified');
                        formData.append('audioUrl', aiHelper.audioUrl || '');
                        
                        const res = await fetch(`${API}/admin/translations`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${adminToken}` },
                          body: formData
                        });
                        if (res.ok) {
                          showNotification(`✅ Replaced: "${aiHelper.sourceText}"`, 'success');
                          setAiHelper({sourceText: '', arabicScript: '', latinScript: '', gender: '', number: '', audioUrl: '', isLoading: false, error: null, duplicateFound: false, existingTranslations: [], showBulkImport: false, bulkText: '', bulkPreview: null, bulkError: null, variants: []});
                          loadData();
                        }
                      }}
                      className="px-3 py-1 bg-orange-500 text-white rounded-lg text-sm"
                    >
                      Replace with New
                    </button>
                    <button
                      onClick={async () => {
                        // Add as variant
                        setAiHelper({...aiHelper, isLoading: true});
                        const formData = new FormData();
                        formData.append('sourceText', aiHelper.sourceText);
                        formData.append('sourceLang', 'en');
                        formData.append('translatedText', aiHelper.arabicScript);
                        formData.append('latinScript', aiHelper.latinScript || '');
                        formData.append('gender', aiHelper.gender || '');
                        formData.append('number', aiHelper.number || '');
                        formData.append('audioUrl', aiHelper.audioUrl || '');
                        
                        const res = await fetch(`${API}/admin/translations/add-variant`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${adminToken}` },
                          body: formData
                        });
                        if (res.ok) {
                          showNotification(`✅ Added as variant: "${aiHelper.latinScript}"`, 'success');
                          setAiHelper({sourceText: '', arabicScript: '', latinScript: '', gender: '', number: '', audioUrl: '', isLoading: false, error: null, duplicateFound: false, existingTranslations: [], showBulkImport: false, bulkText: '', bulkPreview: null, bulkError: null, variants: []});
                          loadData();
                        }
                      }}
                      className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm"
                    >
                      + Add as Variant
                    </button>
                  </div>
                </div>
              )}
              
              {/* Save Button - Now checks for duplicates first */}
              {!aiHelper.duplicateFound && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!aiHelper.sourceText.trim() || !aiHelper.arabicScript.trim()) {
                      setAiHelper({...aiHelper, error: 'Source text and Arabic script are required'});
                      return;
                    }
                    setAiHelper({...aiHelper, isLoading: true, error: null});
                    
                    // First check for duplicates
                    try {
                      const checkRes = await fetch(`${API}/admin/translations/check-duplicate?sourceText=${encodeURIComponent(aiHelper.sourceText)}&sourceLang=en`, {
                        headers: { 'Authorization': `Bearer ${adminToken}` }
                      });
                      const checkData = await checkRes.json();
                      
                      if (checkData.exists && checkData.count > 0) {
                        // Duplicate found - show modal
                        setAiHelper({
                          ...aiHelper,
                          isLoading: false,
                          duplicateFound: true,
                          existingTranslations: checkData.entries || []
                        });
                        return;
                      }
                      
                      // No duplicate - save directly
                      const formData = new FormData();
                      formData.append('sourceText', aiHelper.sourceText);
                      formData.append('sourceLang', 'en');
                      formData.append('translatedText', aiHelper.arabicScript);
                      formData.append('latinScript', aiHelper.latinScript || '');
                      formData.append('gender', aiHelper.gender || '');
                      formData.append('number', aiHelper.number || '');
                      formData.append('status', 'verified');
                      formData.append('audioUrl', aiHelper.audioUrl || '');
                      
                      const res = await fetch(`${API}/admin/translations`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${adminToken}` },
                        body: formData
                      });
                      const data = await res.json();
                      if (res.ok) {
                        // Now save any additional variants from variantRows
                        const validVariants = (aiHelper.variantRows || []).filter(v => v.latin?.trim() || v.arabic?.trim());
                        let variantsSaved = 0;
                        
                        if (validVariants.length > 0) {
                          for (const variant of validVariants) {
                            try {
                              const variantFormData = new FormData();
                              variantFormData.append('sourceText', aiHelper.sourceText);
                              variantFormData.append('sourceLang', 'en');
                              variantFormData.append('translatedText', variant.arabic || '');
                              variantFormData.append('latinScript', variant.latin || '');
                              variantFormData.append('gender', variant.gender || '');
                              variantFormData.append('number', variant.number || '');
                              variantFormData.append('audioUrl', '');
                              
                              const variantRes = await fetch(`${API}/admin/translations/add-variant`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${adminToken}` },
                                body: variantFormData
                              });
                              
                              if (variantRes.ok) {
                                variantsSaved++;
                              }
                            } catch (err) {
                              console.error('Error saving variant:', err);
                            }
                          }
                          showNotification(`✅ Saved: "${aiHelper.sourceText}" + ${variantsSaved} variant(s)`, 'success');
                        } else {
                          showNotification(`✅ Saved: "${aiHelper.sourceText}" → "${aiHelper.latinScript}"`, 'success');
                        }
                        
                        setAiHelper({sourceText: '', arabicScript: '', latinScript: '', gender: '', number: '', audioUrl: '', isLoading: false, error: null, duplicateFound: false, existingTranslations: [], showBulkImport: false, bulkText: '', bulkPreview: null, bulkError: null, variantRows: []});
                        loadData();
                      } else {
                        setAiHelper({...aiHelper, isLoading: false, error: data.detail || 'Save failed'});
                      }
                    } catch (err) {
                      setAiHelper({...aiHelper, isLoading: false, error: 'Network error'});
                    }
                  }}
                  disabled={aiHelper.isLoading}
                  className={`w-full mt-4 py-3 rounded-lg font-semibold text-white transition-all ${
                    aiHelper.isLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                  }`}
                >
                  {aiHelper.isLoading ? '⏳ Saving...' : `💾 SAVE TO DATABASE: "${aiHelper.sourceText}"${aiHelper.variantRows?.length > 0 ? ` + ${aiHelper.variantRows.length} variant(s)` : ''}`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      </>)}

      {aiSubTab.groq === 'queue' && (
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-orange-900/30 to-amber-900/30 border-orange-500' : 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📋</span>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Pending Groq Translations
            </h3>
            {groqPending.total > 0 && (
              <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-bold">{groqPending.total}</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => loadProviderPending('groq')} className={`px-3 py-1 text-sm rounded-lg ${
              isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
            }`}>Refresh</button>
            <button
              onClick={() => clearPendingForProvider('groq')}
              disabled={!groqPending.total}
              className={`px-3 py-1 text-sm rounded-lg font-medium ${
                !groqPending.total
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
              title="Delete all pending Groq translations"
            >
              🗑 Delete All
            </button>
          </div>
        </div>

        {pendingLoading.groq ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading...</p>
        ) : groqPending.items.length === 0 ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No pending Groq translations.
          </p>
        ) : (
          <div className="space-y-2">
            {groqPending.items.map(item => (
              <div key={item.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {new Date(item.created_at).toLocaleDateString()}
                    </div>
                    <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      EN: {item.source_text_original}
                    </div>
                    {pendingEditing === item.id ? (
                      <div className="space-y-1 mt-2">
                        <input value={pendingEditData.latin_script || ''} onChange={e => setPendingEditData(prev => ({ ...prev, latin_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} placeholder="Latin script" />
                        <input value={pendingEditData.arabic_script || ''} onChange={e => setPendingEditData(prev => ({ ...prev, arabic_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} dir="rtl" placeholder="Arabic script" />
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => savePendingEdit(item.id, 'groq')} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Save</button>
                          <button onClick={() => setPendingEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                        </div>
                      </div>
                    ) : pendingEditing === `reject_${item.id}` ? (
                      <div className={`flex items-center gap-2 mt-2 p-2 rounded border ${isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
                        <span className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>Reject this translation?</span>
                        <button onClick={() => confirmRejectPending(item.id, 'groq')} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Yes, Reject</button>
                        <button onClick={() => setPendingEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                      </div>
                    ) : (
                      <>
                        <div className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>DARIJA: {item.latin_script}</div>
                        <div className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>ARABIC: <span dir="rtl">{item.arabic_script?.trim() || <span className={`italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(no arabic script)</span>}</span></div>
                      </>
                    )}
                  </div>
                  {pendingEditing !== item.id && pendingEditing !== `reject_${item.id}` && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => approvePending(item.id, 'groq')} className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 font-medium">Approve</button>
                      <button onClick={() => { setPendingEditing(item.id); setPendingEditData({ latin_script: item.latin_script, arabic_script: item.arabic_script }); }}
                        className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 font-medium">Edit</button>
                      <button onClick={() => rejectPending(item.id, 'groq')} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium">Reject</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {groqPending.pages > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                {Array.from({ length: groqPending.pages }, (_, i) => (
                  <button key={i} onClick={() => loadProviderPending('groq', i + 1)}
                    className={`px-3 py-1 text-sm rounded ${groqPending.page === i + 1 ? 'bg-orange-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>{i + 1}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {aiSubTab.groq === 'approved' && (
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Approved Groq Translations
            </h3>
            {groqApproved.total > 0 && (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-bold">{groqApproved.total}</span>
            )}
          </div>
          <button onClick={() => loadProviderApproved('groq')} className={`px-3 py-1 text-sm rounded-lg ${
            isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
          }`}>Refresh</button>
        </div>

        {approvedLoading.groq ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading...</p>
        ) : groqApproved.items.length === 0 ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No approved Groq translations yet. Approve translations from the Queue tab.
          </p>
        ) : (
          <div className="space-y-2">
            {groqApproved.items.map(item => (
              <div key={item.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {item.reviewed_at ? new Date(item.reviewed_at).toLocaleDateString() : new Date(item.created_at).toLocaleDateString()}
                      {item.reviewed_by && ` by ${item.reviewed_by}`}
                    </div>
                    <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      EN: {item.source_text_original}
                    </div>
                    {approvedEditing === item.id ? (
                      <div className="space-y-1 mt-2">
                        <input value={approvedEditData.latin_script || ''} onChange={e => setApprovedEditData(prev => ({ ...prev, latin_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} placeholder="Latin script" />
                        <input value={approvedEditData.arabic_script || ''} onChange={e => setApprovedEditData(prev => ({ ...prev, arabic_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} dir="rtl" placeholder="Arabic script" />
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => saveApprovedEdit(item.id, 'groq')} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Save</button>
                          <button onClick={() => setApprovedEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>DARIJA: {item.latin_script}</div>
                        <div className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>ARABIC: <span dir="rtl">{item.arabic_script?.trim() || <span className={`italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(no arabic script)</span>}</span></div>
                      </>
                    )}
                  </div>
                  {approvedEditing !== item.id && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => { setApprovedEditing(item.id); setApprovedEditData({ latin_script: item.latin_script, arabic_script: item.arabic_script }); }}
                        className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 font-medium">Edit</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {groqApproved.pages > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                {Array.from({ length: groqApproved.pages }, (_, i) => (
                  <button key={i} onClick={() => loadProviderApproved('groq', i + 1)}
                    className={`px-3 py-1 text-sm rounded ${groqApproved.page === i + 1 ? 'bg-green-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>{i + 1}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}
      </>)}

      {/* BULK VERBS UPLOAD — REMOVED: verb management moved to Grammar → View Verbs */}
      {false && (
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gray-700 border-orange-500' : 'bg-orange-50 border-orange-400'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            📝 Bulk Verbs Upload
            <span className="text-xs px-2 py-0.5 bg-orange-500 text-white rounded">PROTECTED</span>
          </h3>
          <button
            type="button"
            onClick={() => setBulkVerbs(prev => ({ ...prev, showSection: !prev.showSection }))}
            className={`px-3 py-1 text-sm rounded-lg ${
              bulkVerbs.showSection
                ? 'bg-red-500 text-white'
                : 'bg-orange-500 text-white hover:bg-orange-600'
            }`}
          >
            {bulkVerbs.showSection ? '✕ Close' : '+ Add Verbs'}
          </button>
        </div>
        
        <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          🔒 Upload verb conjugations in bulk. Data is PROTECTED and will never be deleted by AI.
        </p>
        
        {bulkVerbs.showSection && (
          <div className="space-y-4">
            {/* Instructions */}
            <div className={`p-3 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-700'}`}>
              <p className="font-medium mb-2">📋 How to use:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li><strong>Left Box:</strong> Paste Latin format (English = Latin Darija)</li>
                <li><strong>Right Box:</strong> Paste Arabic script (same order, line by line)</li>
                <li>Click <strong>Preview</strong> to check matching</li>
                <li>Click <strong>Save All</strong> to add to database</li>
              </ol>
              <div className={`mt-2 p-2 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-orange-300' : 'bg-orange-100 text-orange-800'}`}>
                <p className="font-semibold mb-1">Auto-detect gender/number tags:</p>
                <p>Add tags in the Latin text and they will be auto-filled:</p>
                <p className="font-mono mt-1"><strong>(M)</strong> = Male, <strong>(F)</strong> = Female, <strong>(S)</strong> = Singular, <strong>(P)</strong> = Plural</p>
                <p className="mt-1">Example: <span className="font-mono">You asked = Anta swalti (S) (M)</span></p>
                <p>Tags are removed from the Latin text and gender/number are set automatically.</p>
              </div>
            </div>
            
            {/* Two Column Text Boxes */}
            <div className="grid grid-cols-2 gap-4">
              {/* Left: Latin Script */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  🔤 Latin Script (English = Darija Latin)
                </label>
                <textarea
                  value={bulkVerbs.latinText}
                  onChange={(e) => setBulkVerbs(prev => ({ ...prev, latinText: e.target.value, preview: null, error: null, success: null }))}
                  className={`w-full h-64 px-3 py-2 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                  }`}
                  placeholder={`ask = sawal
I asked = Ana swalt
You asked = Anta swalti (S) (M)
You asked = Anti swalti (S) (F)
He asked = Howa swal
She asked = Hiya swlat
We asked = Hena swalna
You asked = Antoma swalto (P)
They asked = Homa swelo`}
                />
              </div>
              
              {/* Right: Arabic Script */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  🔠 Arabic Script (same order)
                </label>
                <textarea
                  value={bulkVerbs.arabicText}
                  onChange={(e) => setBulkVerbs(prev => ({ ...prev, arabicText: e.target.value, preview: null, error: null, success: null }))}
                  className={`w-full h-64 px-3 py-2 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                  }`}
                  dir="rtl"
                  placeholder={`سول
أنا سولت
أنت سولتي
أنتي سولتي
هو سول
هي سولت
حنا سولنا
نتوما سولتو
هما سولو`}
                />
              </div>
            </div>
            
            {/* Error Message */}
            {bulkVerbs.error && (
              <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
                ❌ {bulkVerbs.error}
              </div>
            )}
            
            {/* Success Message */}
            {bulkVerbs.success && (
              <div className="p-3 bg-green-100 border border-green-300 rounded-lg text-green-700 text-sm">
                {bulkVerbs.success}
              </div>
            )}
            
            {/* Preview Button */}
            <button
              type="button"
              onClick={parseBulkVerbs}
              disabled={bulkVerbs.isValidating}
              className={`px-4 py-2 rounded-lg transition-colors ${
                bulkVerbs.isValidating 
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {bulkVerbs.isValidating ? '⏳ Validating...' : '👁️ Preview & Validate'}
            </button>
            
            {/* Preview Table with Validation Status */}
            {bulkVerbs.preview && bulkVerbs.preview.length > 0 && (
              <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                {/* Summary Header */}
                {bulkVerbs.summary && (
                  <div className={`p-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                    <div className={`font-medium text-sm mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      📋 Validation Results: {bulkVerbs.summary.total} entries
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {bulkVerbs.summary.new > 0 && (
                        <span className="px-2 py-1 bg-green-500 text-white rounded">
                          ✅ {bulkVerbs.summary.new} NEW
                        </span>
                      )}
                      {bulkVerbs.summary.exists > 0 && (
                        <span className="px-2 py-1 bg-yellow-500 text-white rounded inline-flex items-center gap-1">
                          ⚠️ {bulkVerbs.summary.exists} EXISTS
                          <button
                            type="button"
                            onClick={deleteAllExistingBulkEntries}
                            className="ml-1 px-1.5 py-0.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-bold"
                            title="Delete all existing entries so you can re-add them"
                          >
                            Delete Old
                          </button>
                        </span>
                      )}
                      {(bulkVerbs.summary.update || 0) > 0 && (
                        <span className="px-2 py-1 bg-teal-500 text-white rounded">
                          🏷️ {bulkVerbs.summary.update} UPDATE (tags)
                        </span>
                      )}
                      {bulkVerbs.summary.variant > 0 && (
                        <span className="px-2 py-1 bg-blue-500 text-white rounded">
                          🔄 {bulkVerbs.summary.variant} VARIANT
                        </span>
                      )}
                      {bulkVerbs.summary.invalid > 0 && (
                        <span className="px-2 py-1 bg-red-500 text-white rounded">
                          ❌ {bulkVerbs.summary.invalid} INVALID
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Variant Option Toggle */}
                {bulkVerbs.summary && bulkVerbs.summary.variant > 0 && (
                  <div className={`px-3 py-2 border-t ${isDarkMode ? 'border-gray-500 bg-blue-900/30' : 'border-gray-200 bg-blue-50'}`}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkVerbs.addVariants}
                        onChange={(e) => setBulkVerbs(prev => ({ ...prev, addVariants: e.target.checked }))}
                        className="w-4 h-4 rounded"
                      />
                      <span className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        Add {bulkVerbs.summary.variant} entries as new variants (different Darija for same English)
                      </span>
                    </label>
                  </div>
                )}
                
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                      <tr>
                        <th className="px-2 py-2 text-left w-16">Status</th>
                        <th className="px-2 py-2 text-left">English</th>
                        <th className="px-2 py-2 text-left">Latin</th>
                        <th className="px-2 py-2 text-right">Arabic</th>
                        <th className="px-2 py-2 text-center w-24">Gender/Num</th>
                        <th className="px-2 py-2 text-center w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkVerbs.preview.map((item, index) => (
                        <tr key={index} className={`border-t ${
                          item.status === 'EXISTS'
                            ? (isDarkMode ? 'border-gray-600 bg-yellow-900/20' : 'border-gray-200 bg-yellow-50')
                            : item.status === 'UPDATE'
                            ? (isDarkMode ? 'border-gray-600 bg-teal-900/20' : 'border-gray-200 bg-teal-50')
                            : item.status === 'VARIANT'
                            ? (isDarkMode ? 'border-gray-600 bg-blue-900/20' : 'border-gray-200 bg-blue-50')
                            : (isDarkMode ? 'border-gray-600 hover:bg-gray-600' : 'border-gray-200 hover:bg-gray-50')
                        }`}>
                          <td className="px-2 py-2">
                            {item.status === 'NEW' && <span className="text-green-500" title="New - will add">✅</span>}
                            {item.status === 'EXISTS' && <span className="text-yellow-500" title="Already exists - will skip">⚠️</span>}
                            {item.status === 'UPDATE' && <span className="text-teal-500" title="Will update gender/number tags">🏷️</span>}
                            {item.status === 'VARIANT' && <span className="text-blue-500" title="Can add as variant">🔄</span>}
                            {item.status === 'INVALID' && <span className="text-red-500" title="Invalid">❌</span>}
                          </td>
                          <td className={`px-2 py-2 font-medium ${
                            item.status === 'EXISTS' 
                              ? (isDarkMode ? 'text-yellow-400' : 'text-yellow-700')
                              : (isDarkMode ? 'text-white' : 'text-gray-800')
                          }`}>
                            {item.english}
                            {item.status === 'EXISTS' && item.existingTranslations && item.existingTranslations.length > 0 && (
                              <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Has: {item.existingTranslations[0].latinScript}
                              </div>
                            )}
                            {item.status === 'UPDATE' && (
                              <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                                {item.statusMessage}
                              </div>
                            )}
                            {item.status === 'VARIANT' && item.existingTranslations && item.existingTranslations.length > 0 && (
                              <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                Existing: {item.existingTranslations[0].latinScript} | Your: {item.latin}
                              </div>
                            )}
                          </td>
                          <td className={`px-2 py-2 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>{item.latin}</td>
                          <td className={`px-2 py-2 text-right ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`} dir="rtl">{item.arabic}</td>
                          <td className={`px-2 py-2 text-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {item.gender && <span className={`px-1 rounded ${item.gender === 'female' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>{item.gender === 'female' ? 'F' : 'M'}</span>}
                            {item.number && <span className={`px-1 rounded ml-1 ${item.number === 'singular' ? 'bg-gray-200 text-gray-700' : 'bg-purple-100 text-purple-700'}`}>{item.number === 'singular' ? 'S' : 'P'}</span>}
                          </td>
                          <td className="px-2 py-2 text-center">
                            {(item.status === 'EXISTS' || item.status === 'VARIANT') && item.existingTranslations?.length > 0 && (
                              <button
                                type="button"
                                onClick={() => deleteBulkPreviewEntry(item, index)}
                                className="text-red-500 hover:text-red-700 text-xs font-bold"
                                title="Delete existing entry"
                              >
                                🗑️
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Save Button with Count */}
                <div className={`p-3 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  {bulkVerbs.summary && (
                    <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Will save: {bulkVerbs.summary.new} new
                      {(bulkVerbs.summary.update || 0) > 0 && ` + ${bulkVerbs.summary.update} tag updates`}
                      {bulkVerbs.addVariants && bulkVerbs.summary.variant > 0 && ` + ${bulkVerbs.summary.variant} variants`}
                      {bulkVerbs.summary.exists > 0 && ` (skipping ${bulkVerbs.summary.exists} existing)`}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={saveBulkVerbs}
                    disabled={bulkVerbs.isLoading || (bulkVerbs.summary && bulkVerbs.summary.new === 0 && (bulkVerbs.summary.update || 0) === 0 && (!bulkVerbs.addVariants || bulkVerbs.summary.variant === 0))}
                    className={`w-full py-3 rounded-lg font-semibold text-white transition-all ${
                      bulkVerbs.isLoading || (bulkVerbs.summary && bulkVerbs.summary.new === 0 && (bulkVerbs.summary.update || 0) === 0 && (!bulkVerbs.addVariants || bulkVerbs.summary.variant === 0))
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                    }`}
                  >
                    {bulkVerbs.isLoading
                      ? '⏳ Saving...'
                      : `🔒 SAVE ${(bulkVerbs.summary?.new || 0) + (bulkVerbs.summary?.update || 0) + (bulkVerbs.addVariants ? (bulkVerbs.summary?.variant || 0) : 0)} TRANSLATIONS (Protected)`
                    }
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* OpenAI Translation Tab */}
      {translationSubTab === 'openai' && (
        <div className="space-y-4">
          {/* Sub-tab navigation: Settings | Queue | Approved */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setAiSubTab(prev => ({...prev, openai: 'settings'}))}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                aiSubTab.openai === 'settings'
                  ? 'bg-purple-600 text-white'
                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >Settings</button>
            <button
              onClick={() => { setAiSubTab(prev => ({...prev, openai: 'queue'})); loadProviderPending('openai'); }}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                aiSubTab.openai === 'queue'
                  ? 'bg-purple-600 text-white'
                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >OpenAI Queue{pendingCounts.openai > 0 ? ` (${pendingCounts.openai})` : ''}</button>
            <button
              onClick={() => { setAiSubTab(prev => ({...prev, openai: 'approved'})); loadProviderApproved('openai'); }}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                aiSubTab.openai === 'approved'
                  ? 'bg-green-600 text-white'
                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >Approved{approvedCounts.openai > 0 ? ` (${approvedCounts.openai})` : ''}</button>
          </div>

          {/* QUEUE sub-tab */}
          {aiSubTab.openai === 'queue' && (
            <div className={`p-5 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>📥 Pending OpenAI Translations</h3>
                <div className="flex gap-2">
                  <button onClick={() => loadProviderPending('openai')} className={`px-3 py-1 text-sm rounded-lg ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}>🔄 Refresh</button>
                  {openaiPending.total > 0 && (
                    <button onClick={() => clearPendingForProvider('openai')} className="px-3 py-1 text-sm rounded-lg bg-red-500 hover:bg-red-600 text-white">🗑️ Clear All</button>
                  )}
                </div>
              </div>
              {pendingLoading.openai ? (
                <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading…</div>
              ) : openaiPending.items.length === 0 ? (
                <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No pending OpenAI translations.</div>
              ) : (
                <>
                  <div className="space-y-3">
                    {openaiPending.items.map(item => (
                      <div key={item.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                        <div className={`text-xs uppercase font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>EN</div>
                        <div className={`mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.source_text_original || item.source_text_normalized}</div>
                        {pendingEditing === item.id ? (
                          <div className="space-y-2">
                            <input type="text" value={pendingEditData.latin_script || ''} onChange={(e) => setPendingEditData(prev => ({ ...prev, latin_script: e.target.value }))} className={`w-full px-2 py-1 text-sm rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Latin script" />
                            <input type="text" value={pendingEditData.arabic_script || ''} onChange={(e) => setPendingEditData(prev => ({ ...prev, arabic_script: e.target.value }))} className={`w-full px-2 py-1 text-sm rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Arabic script" dir="rtl" />
                            <div className="flex gap-2">
                              <button onClick={() => savePendingEdit(item.id, 'openai')} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Save</button>
                              <button onClick={() => setPendingEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                            </div>
                          </div>
                        ) : pendingEditing === `reject_${item.id}` ? (
                          <div className="flex gap-2">
                            <button onClick={() => confirmRejectPending(item.id, 'openai')} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Yes, Reject</button>
                            <button onClick={() => setPendingEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                          </div>
                        ) : (
                          <>
                            <div className="text-sm font-medium mb-1">{item.latin_script}</div>
                            <div className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} dir="rtl">{item.arabic_script}</div>
                            <div className="flex gap-2">
                              <button onClick={() => approvePending(item.id, 'openai')} className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 font-medium">Approve</button>
                              <button onClick={() => { setPendingEditing(item.id); setPendingEditData({ latin_script: item.latin_script, arabic_script: item.arabic_script }); }} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Edit</button>
                              <button onClick={() => rejectPending(item.id, 'openai')} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium">Reject</button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  {openaiPending.pages > 1 && (
                    <div className="flex justify-center gap-1 mt-4">
                      {Array.from({ length: openaiPending.pages }, (_, i) => (
                        <button key={i} onClick={() => loadProviderPending('openai', i + 1)} className={`px-3 py-1 text-sm rounded ${openaiPending.page === i + 1 ? 'bg-purple-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>{i + 1}</button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* APPROVED sub-tab */}
          {aiSubTab.openai === 'approved' && (
            <div className={`p-5 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>✅ Approved OpenAI Translations</h3>
                <button onClick={() => loadProviderApproved('openai')} className={`px-3 py-1 text-sm rounded-lg ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}>🔄 Refresh</button>
              </div>
              {approvedLoading.openai ? (
                <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading…</div>
              ) : openaiApproved.items.length === 0 ? (
                <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No approved OpenAI translations yet.</div>
              ) : (
                <>
                  <div className="space-y-3">
                    {openaiApproved.items.map(item => (
                      <div key={item.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                        <div className={`text-xs uppercase font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>EN</div>
                        <div className={`mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.source_text_original || item.source_text_normalized}</div>
                        {approvedEditing === item.id ? (
                          <div className="space-y-2">
                            <input type="text" value={approvedEditData.latin_script || ''} onChange={(e) => setApprovedEditData(prev => ({ ...prev, latin_script: e.target.value }))} className={`w-full px-2 py-1 text-sm rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                            <input type="text" value={approvedEditData.arabic_script || ''} onChange={(e) => setApprovedEditData(prev => ({ ...prev, arabic_script: e.target.value }))} className={`w-full px-2 py-1 text-sm rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} dir="rtl" />
                            <div className="flex gap-2">
                              <button onClick={() => saveApprovedEdit(item.id, 'openai')} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Save</button>
                              <button onClick={() => setApprovedEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="text-sm font-medium mb-1">{item.latin_script}</div>
                            <div className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} dir="rtl">{item.arabic_script}</div>
                            <button onClick={() => { setApprovedEditing(item.id); setApprovedEditData({ latin_script: item.latin_script, arabic_script: item.arabic_script }); }} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Edit</button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  {openaiApproved.pages > 1 && (
                    <div className="flex justify-center gap-1 mt-4">
                      {Array.from({ length: openaiApproved.pages }, (_, i) => (
                        <button key={i} onClick={() => loadProviderApproved('openai', i + 1)} className={`px-3 py-1 text-sm rounded ${openaiApproved.page === i + 1 ? 'bg-purple-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>{i + 1}</button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* SETTINGS sub-tab — original OpenAI settings UI */}
          {aiSubTab.openai === 'settings' && (
          <div className={`p-5 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>⚡ OpenAI</h3>
              <span className={`text-xs px-2 py-1 rounded ${
                openaiSettings.apiKeySet && openaiSettings.enabled
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
                  : openaiSettings.apiKeySet && !openaiSettings.enabled
                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
              }`}>
                {openaiSettings.apiKeySet && openaiSettings.enabled ? '● Active' : openaiSettings.apiKeySet ? '○ Disabled' : '✕ No key'}
              </span>
            </div>

            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Cheap, reliable fallback for DeepSeek. <code className="px-1 bg-gray-200 dark:bg-gray-800 rounded">gpt-4o-mini</code> ~$0.02/month at typical traffic. Get a key at{' '}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">platform.openai.com/api-keys</a>.
            </p>

            {/* API Key */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>API Key</label>
              {openaiSettings.apiKeySet && !openaiSettings.showApiKeyInput ? (
                <div className="flex items-center gap-2">
                  <code className={`flex-1 px-3 py-2 rounded ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                    {openaiSettings.apiKeyMasked}
                  </code>
                  <button
                    onClick={() => setOpenaiSettings(prev => ({ ...prev, showApiKeyInput: true, newApiKey: '' }))}
                    className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                  >Replace</button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="password"
                    value={openaiSettings.newApiKey}
                    onChange={(e) => setOpenaiSettings(prev => ({ ...prev, newApiKey: e.target.value }))}
                    placeholder="sk-..."
                    className={`flex-1 px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                  <button
                    onClick={saveOpenaiApiKey}
                    disabled={!openaiSettings.newApiKey || openaiSettings.isLoading}
                    className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm disabled:opacity-50"
                  >{openaiSettings.isLoading ? 'Saving…' : 'Save'}</button>
                  {openaiSettings.apiKeySet && (
                    <button
                      onClick={() => setOpenaiSettings(prev => ({ ...prev, showApiKeyInput: false, newApiKey: '' }))}
                      className="px-3 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 text-sm"
                    >Cancel</button>
                  )}
                </div>
              )}
            </div>

            {/* Model selector */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Model</label>
              <select
                value={openaiSettings.model}
                onChange={(e) => saveOpenaiModel(e.target.value)}
                className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                <option value="gpt-5.4-mini">gpt-5.4-mini (recommended — current gen, cheap, fast)</option>
                <option value="gpt-5.4-nano">gpt-5.4-nano (cheapest, ok quality)</option>
                <option value="gpt-5.4">gpt-5.4 (highest quality, more expensive)</option>
                <option value="gpt-4o-mini">gpt-4o-mini (older, very cheap)</option>
                <option value="gpt-4o">gpt-4o (older flagship)</option>
              </select>
            </div>

            {/* Enable toggle */}
            <div className="flex items-center justify-between mb-4">
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Enabled</span>
              <button
                onClick={toggleOpenai}
                disabled={!openaiSettings.apiKeySet}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  openaiSettings.enabled ? 'bg-green-500' : 'bg-gray-300'
                } disabled:opacity-50`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  openaiSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Custom prompt + safety notice */}
            <div className={`mt-6 p-4 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Custom translation prompt (optional)</label>
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{openaiSettings.customPrompt.length} / 8000</span>
              </div>
              <div className={`text-xs mb-2 px-3 py-2 rounded space-y-2 ${isDarkMode ? 'bg-amber-900/30 text-amber-200 border border-amber-700' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                <div>🔒 <strong>Safety rules are hardcoded</strong> — your custom prompt cannot disable them. Users typing "ignore previous instructions" or "act as a chatbot" will still only get translations.</div>
                <div className={`pt-2 border-t ${isDarkMode ? 'border-amber-700/50' : 'border-amber-200'}`}>
                  ✨ <strong>Leave this empty to share DeepSeek's prompt</strong> (Persona, Grammar, Substitutions, Examples — edit them once in the DeepSeek tab, both AI providers use them).
                  <br />OpenAI also auto-loads <strong>central rules</strong> from the Grammar admin tab (pronouns, grammar rules, negation patterns) on top of whatever prompt is active.
                  <br />Use this textarea ONLY if you want OpenAI to use a different prompt than DeepSeek.
                </div>
              </div>
              <textarea
                value={openaiSettings.customPrompt}
                onChange={(e) => setOpenaiSettings(prev => ({ ...prev, customPrompt: e.target.value.slice(0, 8000) }))}
                placeholder="Leave blank to use default (DeepSeek's curated Darija prompt). Or paste your own style guide: persona, vocabulary preferences, examples of good translations, etc."
                rows={8}
                className={`w-full px-3 py-2 rounded border font-mono text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
              <button
                onClick={saveOpenaiCustomPrompt}
                disabled={openaiSettings.isPromptSaving}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm disabled:opacity-50"
              >{openaiSettings.isPromptSaving ? 'Saving…' : 'Save custom prompt'}</button>
            </div>

            {/* Test */}
            <div className={`mt-6 p-4 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Test translation</label>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={openaiSettings.testText}
                  onChange={(e) => setOpenaiSettings(prev => ({ ...prev, testText: e.target.value }))}
                  placeholder="Type English to test OpenAI…"
                  className={`flex-1 px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
                <button
                  onClick={testOpenai}
                  disabled={!openaiSettings.apiKeySet || openaiSettings.isTesting || !openaiSettings.testText.trim()}
                  className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
                >{openaiSettings.isTesting ? 'Testing…' : 'Test'}</button>
              </div>
              {openaiSettings.testResult && (
                <div className={`mt-2 p-3 rounded ${openaiSettings.testResult.error ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-800'}`}>
                  {openaiSettings.testResult.error ? (
                    <div><strong>Error:</strong> {openaiSettings.testResult.error}</div>
                  ) : (
                    <div className="whitespace-pre-wrap text-sm">
                      <div className="text-xs text-gray-600 mb-1">Model: {openaiSettings.testResult.model}</div>
                      {openaiSettings.testResult.translation}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      )}

      {/* DeepSeek AI Translation Tab */}
      {translationSubTab === 'deepseek' && (<>

      {/* Sub-tab navigation */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setAiSubTab(prev => ({...prev, deepseek: 'settings'}))}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            aiSubTab.deepseek === 'settings'
              ? 'bg-emerald-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Settings
        </button>
        <button
          onClick={() => { setAiSubTab(prev => ({...prev, deepseek: 'queue'})); loadProviderPending('deepseek'); }}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            aiSubTab.deepseek === 'queue'
              ? 'bg-emerald-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          DeepSeek Queue{pendingCounts.deepseek > 0 ? ` (${pendingCounts.deepseek})` : ''}
        </button>
        <button
          onClick={() => { setAiSubTab(prev => ({...prev, deepseek: 'approved'})); loadProviderApproved('deepseek'); }}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            aiSubTab.deepseek === 'approved'
              ? 'bg-green-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Approved{approvedCounts.deepseek > 0 ? ` (${approvedCounts.deepseek})` : ''}
        </button>
      </div>

      {aiSubTab.deepseek === 'settings' && (<>
      <div className={`p-3 rounded-lg border mb-4 ${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
          Word-by-word database lookup is limited to <strong>{settings.maxWordsForDatabase || 4} words</strong>. Phrases longer than {settings.maxWordsForDatabase || 4} words skip the database and go directly to AI for a natural translation. You can change this limit in <strong>Translations → Limits</strong> tab.
        </p>
      </div>
      <div className={`p-4 rounded-lg border-2 mb-4 ${
        isDarkMode ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border-blue-500' : 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🔑</span>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              DeepSeek API Key
            </h3>
            {deepseekSettings.apiKeySet && deepseekSettings.enabled ? (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">Active</span>
            ) : deepseekSettings.apiKeySet && !deepseekSettings.enabled ? (
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>Disabled</span>
            ) : (
              <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full font-medium">Not Set</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Enable/Disable Toggle */}
            <button
              onClick={toggleDeepseek}
              disabled={!deepseekSettings.apiKeySet}
              className={`px-3 py-1 text-sm rounded-lg font-medium transition-all ${
                !deepseekSettings.apiKeySet ? 'bg-gray-400 cursor-not-allowed text-gray-200' :
                deepseekSettings.enabled
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-300' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
              }`}
            >
              {deepseekSettings.enabled ? 'Enabled' : 'Disabled'}
            </button>
            <button
              onClick={() => {
                setDeepseekSettings(prev => ({ ...prev, showApiKeyInput: !prev.showApiKeyInput }));
              }}
              className={`px-3 py-1 text-sm rounded-lg ${
                isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
              }`}
            >
              {deepseekSettings.showApiKeyInput ? 'Close' : 'Configure'}
            </button>
          </div>
        </div>

        <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          AI-powered Darija translation fallback. ~$0.01 per 100 translations.
        </p>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          Get your API key from{' '}
          <a href="https://platform.deepseek.com/api_keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
            platform.deepseek.com/api_keys
          </a>
        </p>

        {deepseekSettings.showApiKeyInput && (
          <div className="space-y-3">
            {deepseekSettings.apiKeySet && (
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Current key: <span className="font-mono">{deepseekSettings.apiKeyMasked}</span>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="password"
                value={deepseekSettings.newApiKey}
                onChange={(e) => setDeepseekSettings(prev => ({ ...prev, newApiKey: e.target.value, message: null }))}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
                placeholder="Enter your DeepSeek API key (sk-...)"
              />
              <button
                onClick={saveDeepseekApiKey}
                disabled={!deepseekSettings.newApiKey.trim() || deepseekSettings.isLoading}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  !deepseekSettings.newApiKey.trim() || deepseekSettings.isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white'
                }`}
              >
                {deepseekSettings.isLoading ? 'Saving...' : 'Save Key'}
              </button>
            </div>
            {deepseekSettings.apiKeySet && (
              <button
                onClick={async () => {
                  if (!window.confirm('Remove DeepSeek API Key?\nDeepSeek translation will stop working until a new key is added.')) return;
                  setDeepseekSettings(prev => ({ ...prev, isLoading: true }));
                  try {
                    const formData = new FormData();
                    formData.append('api_key', '');
                    const res = await fetch(`${API}/admin/deepseek/settings`, {
                      method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
                    });
                    if (res.ok) {
                      setDeepseekSettings(prev => ({ ...prev, apiKeySet: false, apiKeyMasked: '', enabled: false, newApiKey: '', showApiKeyInput: false, isLoading: false, message: { type: 'success', text: 'API key removed' } }));
                      showNotification('DeepSeek API key removed', 'success');
                      await loadDeepseekSettings();
                    }
                  } catch (err) {
                    setDeepseekSettings(prev => ({ ...prev, isLoading: false }));
                    showNotification('Failed to remove key', 'error');
                  }
                }}
                disabled={deepseekSettings.isLoading}
                className="w-full px-3 py-2 text-sm rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-all font-medium"
              >
                Remove API Key
              </button>
            )}
            {deepseekSettings.message && (
              <p className={`text-sm ${deepseekSettings.message.type === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                {deepseekSettings.message.type === 'success' ? 'OK' : 'Error'}: {deepseekSettings.message.text}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Prompt Settings — Editable from Admin */}
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-500' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚙️</span>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Prompt Settings
            </h3>
          </div>
          <button
            onClick={() => setDeepseekPrompt(prev => ({ ...prev, showPromptEditor: !prev.showPromptEditor }))}
            className={`px-3 py-1 text-sm rounded-lg ${
              isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
            }`}
          >{deepseekPrompt.showPromptEditor ? 'Collapse' : 'Edit Prompt'}</button>
        </div>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Customize the AI translation prompt — persona, grammar rules, word substitutions, and examples.
        </p>

        {deepseekPrompt.showPromptEditor && (
          <div className="mt-4 space-y-4">
            {/* Temperature */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Temperature: {deepseekPrompt.temperature}
              </label>
              <input
                type="range" min="0" max="1" step="0.05"
                value={deepseekPrompt.temperature}
                onChange={e => setDeepseekPrompt(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                className="w-full"
              />
              <div className={`flex justify-between text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <span>0.0 (consistent)</span>
                <span>1.0 (creative)</span>
              </div>
            </div>

            {/* Persona */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                AI Persona
              </label>
              <textarea
                value={deepseekPrompt.persona}
                onChange={e => setDeepseekPrompt(prev => ({ ...prev, persona: e.target.value }))}
                rows={3}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
                placeholder="You are a native Moroccan Darija translator from Casablanca..."
              />
            </div>

            {/* Grammar Rules */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Grammar Rules
              </label>
              <textarea
                value={deepseekPrompt.grammarRules}
                onChange={e => setDeepseekPrompt(prev => ({ ...prev, grammarRules: e.target.value }))}
                rows={6}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
                placeholder="- Present tense: ka- prefix..."
              />
            </div>

            {/* MSA → Darija Substitutions */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  MSA → Darija Substitutions ({deepseekPrompt.substitutions.length})
                </label>
                <button
                  onClick={() => setDeepseekPrompt(prev => ({
                    ...prev, substitutions: [...prev.substitutions, { msa: '', darija: '', meaning: '' }]
                  }))}
                  className="px-2 py-1 text-xs bg-amber-500 text-white rounded hover:bg-amber-600"
                >+ Add</button>
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {deepseekPrompt.substitutions.map((sub, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <input
                      value={sub.msa}
                      onChange={e => {
                        const updated = [...deepseekPrompt.substitutions];
                        updated[idx] = { ...updated[idx], msa: e.target.value };
                        setDeepseekPrompt(prev => ({ ...prev, substitutions: updated }));
                      }}
                      className={`flex-1 px-2 py-1 text-xs border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                      placeholder="MSA word"
                    />
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>→</span>
                    <input
                      value={sub.darija}
                      onChange={e => {
                        const updated = [...deepseekPrompt.substitutions];
                        updated[idx] = { ...updated[idx], darija: e.target.value };
                        setDeepseekPrompt(prev => ({ ...prev, substitutions: updated }));
                      }}
                      className={`flex-1 px-2 py-1 text-xs border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                      placeholder="Darija word"
                    />
                    <input
                      value={sub.meaning}
                      onChange={e => {
                        const updated = [...deepseekPrompt.substitutions];
                        updated[idx] = { ...updated[idx], meaning: e.target.value };
                        setDeepseekPrompt(prev => ({ ...prev, substitutions: updated }));
                      }}
                      className={`w-24 px-2 py-1 text-xs border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                      placeholder="meaning"
                    />
                    <button
                      onClick={() => {
                        const updated = deepseekPrompt.substitutions.filter((_, i) => i !== idx);
                        setDeepseekPrompt(prev => ({ ...prev, substitutions: updated }));
                      }}
                      className="text-red-500 hover:text-red-700 text-xs px-1"
                    >×</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Example Translations */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Example Translations ({deepseekPrompt.examples.length})
                </label>
                <button
                  onClick={() => setDeepseekPrompt(prev => ({
                    ...prev, examples: [...prev.examples, { input: '', latin: '', arabic: '' }]
                  }))}
                  className="px-2 py-1 text-xs bg-amber-500 text-white rounded hover:bg-amber-600"
                >+ Add</button>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {deepseekPrompt.examples.map((ex, idx) => (
                  <div key={idx} className={`p-2 rounded border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-1 mb-1">
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>EN:</span>
                      <input
                        value={ex.input}
                        onChange={e => {
                          const updated = [...deepseekPrompt.examples];
                          updated[idx] = { ...updated[idx], input: e.target.value };
                          setDeepseekPrompt(prev => ({ ...prev, examples: updated }));
                        }}
                        className={`flex-1 px-2 py-1 text-xs border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                        placeholder="English sentence"
                      />
                      <button
                        onClick={() => {
                          const updated = deepseekPrompt.examples.filter((_, i) => i !== idx);
                          setDeepseekPrompt(prev => ({ ...prev, examples: updated }));
                        }}
                        className="text-red-500 hover:text-red-700 text-xs px-1"
                      >×</button>
                    </div>
                    <div className="flex items-center gap-1 mb-1">
                      <span className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Latin:</span>
                      <input
                        value={ex.latin}
                        onChange={e => {
                          const updated = [...deepseekPrompt.examples];
                          updated[idx] = { ...updated[idx], latin: e.target.value };
                          setDeepseekPrompt(prev => ({ ...prev, examples: updated }));
                        }}
                        className={`flex-1 px-2 py-1 text-xs border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                        placeholder="Darija in latin"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Arabic:</span>
                      <input
                        value={ex.arabic}
                        onChange={e => {
                          const updated = [...deepseekPrompt.examples];
                          updated[idx] = { ...updated[idx], arabic: e.target.value };
                          setDeepseekPrompt(prev => ({ ...prev, examples: updated }));
                        }}
                        className={`flex-1 px-2 py-1 text-xs border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                        dir="rtl" placeholder="Darija in arabic"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Save / Reset buttons */}
            <div className="flex gap-2">
              <button
                onClick={saveDeepseekPrompt}
                disabled={deepseekPrompt.isSaving}
                className={`flex-1 px-4 py-2 rounded-lg font-medium text-white transition-all ${
                  deepseekPrompt.isSaving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                }`}
              >{deepseekPrompt.isSaving ? 'Saving...' : 'Save Prompt Settings'}</button>
              <button
                onClick={() => {
                  if (!window.confirm('Reset all prompt settings to defaults?')) return;
                  loadDeepseekSettings();
                  // Clear DB by saving empty values — backend will use defaults
                  fetch(`${API}/admin/deepseek/prompt`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ persona: '', grammar_rules: '', substitutions: [], examples: [], temperature: 0.2 })
                  }).then(() => {
                    loadDeepseekSettings();
                    showNotification('Reset to defaults', 'success');
                  });
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >Reset to Defaults</button>
            </div>
          </div>
        )}
      </div>

      {/* Test Translation */}
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border-indigo-500' : 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-300'
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">🧪</span>
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Test Translation
          </h3>
          {!deepseekSettings.apiKeySet && (
            <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-medium">
              API Key Required
            </span>
          )}
        </div>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Test DeepSeek Darija translation quality before enabling it for users.
        </p>

        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={deepseekSettings.testText}
            onChange={(e) => setDeepseekSettings(prev => ({ ...prev, testText: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') testDeepseek(); }}
            className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
            }`}
            placeholder="Enter English text to translate..."
            disabled={!deepseekSettings.apiKeySet}
          />
          <button
            onClick={testDeepseek}
            disabled={!deepseekSettings.apiKeySet || !deepseekSettings.testText.trim() || deepseekSettings.isTesting}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              !deepseekSettings.apiKeySet || !deepseekSettings.testText.trim() || deepseekSettings.isTesting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white'
            }`}
          >
            {deepseekSettings.isTesting ? 'Translating...' : 'Translate'}
          </button>
        </div>

        {deepseekSettings.testResult && (
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            {deepseekSettings.testResult.error ? (
              <p className="text-red-500 text-sm">{deepseekSettings.testResult.error}</p>
            ) : (
              <div className="space-y-2">
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  EN: {deepseekSettings.testResult.input}
                </div>
                <div className={`text-base font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  LATIN: {deepseekSettings.testResult.latin}
                </div>
                <div className={`text-base font-medium ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`} dir="rtl">
                  ARABIC: {deepseekSettings.testResult.arabic}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Tokens: {deepseekSettings.testResult.tokens?.input} in / {deepseekSettings.testResult.tokens?.output} out
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      </>)}

      {aiSubTab.deepseek === 'queue' && (
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border-emerald-500' : 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📋</span>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Pending DeepSeek Translations
            </h3>
            {deepseekPending.total > 0 && (
              <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-bold">{deepseekPending.total}</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => loadProviderPending('deepseek')} className={`px-3 py-1 text-sm rounded-lg ${
              isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
            }`}>Refresh</button>
            <button
              onClick={() => clearPendingForProvider('deepseek')}
              disabled={!deepseekPending.total}
              className={`px-3 py-1 text-sm rounded-lg font-medium ${
                !deepseekPending.total
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
              title="Delete all pending DeepSeek translations"
            >
              🗑 Delete All
            </button>
          </div>
        </div>

        {pendingLoading.deepseek ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading...</p>
        ) : deepseekPending.items.length === 0 ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No pending DeepSeek translations.
          </p>
        ) : (
          <div className="space-y-2">
            {deepseekPending.items.map(item => (
              <div key={item.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {new Date(item.created_at).toLocaleDateString()}
                    </div>
                    <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      EN: {item.source_text_original}
                    </div>
                    {pendingEditing === item.id ? (
                      <div className="space-y-1 mt-2">
                        <input value={pendingEditData.latin_script || ''} onChange={e => setPendingEditData(prev => ({ ...prev, latin_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} placeholder="Latin script" />
                        <input value={pendingEditData.arabic_script || ''} onChange={e => setPendingEditData(prev => ({ ...prev, arabic_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} dir="rtl" placeholder="Arabic script" />
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => savePendingEdit(item.id, 'deepseek')} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Save</button>
                          <button onClick={() => setPendingEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                        </div>
                      </div>
                    ) : pendingEditing === `reject_${item.id}` ? (
                      <div className="flex items-center gap-2 mt-2 p-2 bg-red-50 rounded border border-red-200">
                        <span className="text-sm text-red-700">Reject this translation?</span>
                        <button onClick={() => confirmRejectPending(item.id, 'deepseek')} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Yes, Reject</button>
                        <button onClick={() => setPendingEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                      </div>
                    ) : (
                      <>
                        <div className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>DARIJA: {item.latin_script}</div>
                        <div className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>ARABIC: <span dir="rtl">{item.arabic_script?.trim() || <span className={`italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(no arabic script)</span>}</span></div>
                      </>
                    )}
                  </div>
                  {pendingEditing !== item.id && pendingEditing !== `reject_${item.id}` && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => approvePending(item.id, 'deepseek')} className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 font-medium">Approve</button>
                      <button onClick={() => { setPendingEditing(item.id); setPendingEditData({ latin_script: item.latin_script, arabic_script: item.arabic_script }); }}
                        className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 font-medium">Edit</button>
                      <button onClick={() => rejectPending(item.id, 'deepseek')} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium">Reject</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {deepseekPending.pages > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                {Array.from({ length: deepseekPending.pages }, (_, i) => (
                  <button key={i} onClick={() => loadProviderPending('deepseek', i + 1)}
                    className={`px-3 py-1 text-sm rounded ${deepseekPending.page === i + 1 ? 'bg-emerald-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>{i + 1}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {aiSubTab.deepseek === 'approved' && (
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Approved DeepSeek Translations
            </h3>
            {deepseekApproved.total > 0 && (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-bold">{deepseekApproved.total}</span>
            )}
          </div>
          <button onClick={() => loadProviderApproved('deepseek')} className={`px-3 py-1 text-sm rounded-lg ${
            isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
          }`}>Refresh</button>
        </div>

        {approvedLoading.deepseek ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading...</p>
        ) : deepseekApproved.items.length === 0 ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No approved DeepSeek translations yet. Approve translations from the Queue tab.
          </p>
        ) : (
          <div className="space-y-2">
            {deepseekApproved.items.map(item => (
              <div key={item.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {item.reviewed_at ? new Date(item.reviewed_at).toLocaleDateString() : new Date(item.created_at).toLocaleDateString()}
                      {item.reviewed_by && ` by ${item.reviewed_by}`}
                    </div>
                    <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      EN: {item.source_text_original}
                    </div>
                    {approvedEditing === item.id ? (
                      <div className="space-y-1 mt-2">
                        <input value={approvedEditData.latin_script || ''} onChange={e => setApprovedEditData(prev => ({ ...prev, latin_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} placeholder="Latin script" />
                        <input value={approvedEditData.arabic_script || ''} onChange={e => setApprovedEditData(prev => ({ ...prev, arabic_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} dir="rtl" placeholder="Arabic script" />
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => saveApprovedEdit(item.id, 'deepseek')} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Save</button>
                          <button onClick={() => setApprovedEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>DARIJA: {item.latin_script}</div>
                        <div className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>ARABIC: <span dir="rtl">{item.arabic_script?.trim() || <span className={`italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(no arabic script)</span>}</span></div>
                      </>
                    )}
                  </div>
                  {approvedEditing !== item.id && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => { setApprovedEditing(item.id); setApprovedEditData({ latin_script: item.latin_script, arabic_script: item.arabic_script }); }}
                        className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 font-medium">Edit</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {deepseekApproved.pages > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                {Array.from({ length: deepseekApproved.pages }, (_, i) => (
                  <button key={i} onClick={() => loadProviderApproved('deepseek', i + 1)}
                    className={`px-3 py-1 text-sm rounded ${deepseekApproved.page === i + 1 ? 'bg-green-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>{i + 1}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}
      </>)}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* GEMINI AI TAB */}
      {/* ═══════════════════════════════════════════════════════ */}
      {translationSubTab === 'gemini' && (<>

      {/* Sub-tab navigation */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setAiSubTab(prev => ({...prev, gemini: 'settings'}))}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            aiSubTab.gemini === 'settings'
              ? 'bg-purple-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Settings
        </button>
        <button
          onClick={() => { setAiSubTab(prev => ({...prev, gemini: 'queue'})); loadProviderPending('gemini'); }}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            aiSubTab.gemini === 'queue'
              ? 'bg-purple-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Gemini Queue{pendingCounts.gemini > 0 ? ` (${pendingCounts.gemini})` : ''}
        </button>
        <button
          onClick={() => { setAiSubTab(prev => ({...prev, gemini: 'approved'})); loadProviderApproved('gemini'); }}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            aiSubTab.gemini === 'approved'
              ? 'bg-green-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Approved{approvedCounts.gemini > 0 ? ` (${approvedCounts.gemini})` : ''}
        </button>
      </div>

      {/* AI Cache Progress — all providers */}
      {aiCacheStats && (
        <div className={`p-4 rounded-lg border mb-4 ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>AI Translation Progress</h4>
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {(aiCacheStats.main_db_total || 0).toLocaleString()} in database
            </span>
          </div>
          {['gemini', 'deepseek', 'groq'].map(provider => {
            const s = aiCacheStats[provider] || {};
            const total = s.total || 0;
            if (total === 0) return null;
            const approvedPct = total > 0 ? Math.round((s.approved / total) * 100) : 0;
            const pendingPct = total > 0 ? Math.round((s.pending / total) * 100) : 0;
            const rejectedPct = total > 0 ? Math.round((s.rejected / total) * 100) : 0;
            return (
              <div key={provider} className="mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-medium capitalize ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {provider === 'gemini' ? 'Gemini' : provider === 'deepseek' ? 'DeepSeek' : 'Groq'}
                  </span>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {s.approved || 0} approved / {s.pending || 0} pending{s.rejected ? ` / ${s.rejected} rejected` : ''}
                  </span>
                </div>
                <div className={`w-full h-3 rounded-full overflow-hidden flex ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  {approvedPct > 0 && <div className="bg-green-500 h-full transition-all" style={{ width: `${approvedPct}%` }}></div>}
                  {pendingPct > 0 && <div className="bg-yellow-500 h-full transition-all" style={{ width: `${pendingPct}%` }}></div>}
                  {rejectedPct > 0 && <div className="bg-red-500 h-full transition-all" style={{ width: `${rejectedPct}%` }}></div>}
                </div>
              </div>
            );
          })}
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-xs"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span> Approved</span>
            <span className="flex items-center gap-1 text-xs"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block"></span> Pending</span>
            <span className="flex items-center gap-1 text-xs"><span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span> Rejected</span>
          </div>
        </div>
      )}

      {aiSubTab.gemini === 'settings' && (<>
      <div className={`p-3 rounded-lg border mb-4 ${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
          Word-by-word database lookup is limited to <strong>{settings.maxWordsForDatabase || 4} words</strong>. Phrases longer than {settings.maxWordsForDatabase || 4} words skip the database and go directly to AI for a natural translation. You can change this limit in <strong>Translations → Limits</strong> tab.
        </p>
      </div>

      {/* API Key Card */}
      <div className={`p-4 rounded-lg border-2 mb-4 ${
        isDarkMode ? 'bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border-purple-500' : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🔑</span>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Gemini API Key
            </h3>
            {geminiSettings.apiKeySet && geminiSettings.enabled ? (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">Active</span>
            ) : geminiSettings.apiKeySet && !geminiSettings.enabled ? (
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>Disabled</span>
            ) : (
              <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full font-medium">Not Set</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => toggleGemini('enabled')}
              disabled={!geminiSettings.apiKeySet}
              className={`px-3 py-1 text-sm rounded-lg font-medium transition-all ${
                !geminiSettings.apiKeySet ? 'bg-gray-400 cursor-not-allowed text-gray-200' :
                geminiSettings.enabled
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-300' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
              }`}
            >
              {geminiSettings.enabled ? 'Enabled' : 'Disabled'}
            </button>
            <button
              onClick={() => {
                loadGeminiSettings();
                setGeminiSettings(prev => ({ ...prev, showApiKeyInput: !prev.showApiKeyInput }));
              }}
              className={`px-3 py-1 text-sm rounded-lg ${
                isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
              }`}
            >
              {geminiSettings.showApiKeyInput ? 'Close' : 'Configure'}
            </button>
          </div>
        </div>

        <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Google Gemini AI translation + TTS audio. Free tier: 10 RPM, Paid: ~$2.40/month for 3K/day.
        </p>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          Get your API key from{' '}
          <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-purple-500 hover:underline">
            aistudio.google.com/apikey
          </a>
        </p>

        {geminiSettings.showApiKeyInput && (
          <div className="space-y-3">
            {geminiSettings.apiKeySet && (
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Current key: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{geminiSettings.apiKeyMasked}</code>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="password"
                value={geminiSettings.newApiKey}
                onChange={(e) => setGeminiSettings(prev => ({ ...prev, newApiKey: e.target.value }))}
                placeholder="Enter Gemini API key..."
                className={`flex-1 px-3 py-2 rounded-lg border ${
                  isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'
                }`}
              />
              <button
                onClick={saveGeminiApiKey}
                disabled={!geminiSettings.newApiKey.trim() || geminiSettings.isLoading}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {geminiSettings.isLoading ? 'Saving...' : 'Save Key'}
              </button>
            </div>
            {geminiSettings.apiKeySet && (
              <button
                onClick={removeGeminiApiKey}
                className="text-sm text-red-500 hover:text-red-600 hover:underline"
              >
                Remove Key
              </button>
            )}
            {geminiSettings.message && (
              <div className={`text-sm px-3 py-2 rounded ${
                geminiSettings.message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {geminiSettings.message.text}
              </div>
            )}
          </div>
        )}
      </div>

      {/* TTS Toggle */}
      <div className={`p-4 rounded-lg border mb-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Text-to-Speech (TTS)</h4>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Enable Darija audio generation (separate from translation)</p>
          </div>
          <div className="flex items-center gap-2">
            {geminiSettings.ttsEnabled ? (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">TTS Active</span>
            ) : (
              <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>TTS Off</span>
            )}
            <button
              onClick={() => toggleGemini('tts_enabled')}
              disabled={!geminiSettings.apiKeySet}
              className={`px-3 py-1 text-sm rounded-lg font-medium transition-all ${
                !geminiSettings.apiKeySet ? 'bg-gray-400 cursor-not-allowed text-gray-200' :
                geminiSettings.ttsEnabled
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-300' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
              }`}
            >
              {geminiSettings.ttsEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Model Selector */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Translation Model</label>
          <select
            value={geminiSettings.model}
            onChange={(e) => {
              setGeminiSettings(prev => ({ ...prev, model: e.target.value }));
              saveGeminiSettings({ model: e.target.value });
            }}
            className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
          >
            <option value="gemini-2.5-flash">Gemini 2.5 Flash (Recommended)</option>
            <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
            <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
          </select>
        </div>

        {/* Temperature */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Temperature: {geminiSettings.temperature}
          </label>
          <input
            type="range"
            min="0.1"
            max="1.0"
            step="0.1"
            value={geminiSettings.temperature}
            onChange={(e) => setGeminiSettings(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
            className="w-full"
          />
          <div className="flex justify-between text-xs mt-1">
            <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Strict (0.1)</span>
            <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Creative (1.0)</span>
          </div>
        </div>

        {/* Pipeline Priority */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Pipeline Priority</label>
          <select
            value={geminiSettings.pipelinePriority}
            onChange={(e) => {
              setGeminiSettings(prev => ({ ...prev, pipelinePriority: e.target.value }));
              saveGeminiSettings({ pipeline_priority: e.target.value });
            }}
            className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
          >
            <option value="gemini_openai_deepseek">🥇 Gemini → 🥈 OpenAI → 🥉 DeepSeek (recommended quality)</option>
            <option value="openai_gemini_deepseek">🥇 OpenAI → 🥈 Gemini → 🥉 DeepSeek</option>
            <option value="openai_deepseek">🥇 OpenAI → 🥈 DeepSeek (skip Gemini)</option>
            <option value="before_deepseek">Gemini → DeepSeek → OpenAI</option>
            <option value="after_deepseek">DeepSeek → Gemini → OpenAI</option>
            <option value="disabled">DeepSeek → OpenAI (Gemini disabled)</option>
          </select>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Controls which AI tries first when translating
          </p>
        </div>

        {/* Voice Picker */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>TTS Voice</label>
          <select
            value={geminiSettings.voice}
            onChange={(e) => {
              setGeminiSettings(prev => ({ ...prev, voice: e.target.value }));
              saveGeminiSettings({ voice: e.target.value });
            }}
            className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
          >
            <optgroup label="Female Voices">
              <option value="Aoede">Aoede — Breezy (Default)</option>
              <option value="Leda">Leda — Youthful</option>
              <option value="Callirrhoe">Callirrhoe — Casual</option>
              <option value="Autonoe">Autonoe — Bright</option>
              <option value="Despina">Despina — Smooth</option>
              <option value="Erinome">Erinome — Clear</option>
              <option value="Laomedeia">Laomedeia — Upbeat</option>
              <option value="Achernar">Achernar — Soft</option>
              <option value="Algieba">Algieba — Calm</option>
              <option value="Rasalgethi">Rasalgethi — Informed</option>
            </optgroup>
            <optgroup label="Male Voices">
              <option value="Kore">Kore — Firm</option>
              <option value="Zephyr">Zephyr — Bright</option>
              <option value="Fenrir">Fenrir — Excitable</option>
              <option value="Puck">Puck — Upbeat</option>
              <option value="Charon">Charon — Informative</option>
              <option value="Orus">Orus — Firm</option>
              <option value="Enceladus">Enceladus — Breathy</option>
              <option value="Iapetus">Iapetus — Clear</option>
              <option value="Umbriel">Umbriel — Easy-going</option>
              <option value="Algenib">Algenib — Gravelly</option>
            </optgroup>
          </select>
        </div>

        {/* Max Audio Length */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Max Audio Text Length</label>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              min="10"
              max="500"
              value={geminiSettings.maxAudioLength}
              onChange={(e) => setGeminiSettings(prev => ({ ...prev, maxAudioLength: parseInt(e.target.value) || 200 }))}
              className={`w-24 px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
            />
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>characters</span>
            <button
              onClick={() => saveGeminiSettings({ max_audio_length: geminiSettings.maxAudioLength })}
              className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
            >
              Save
            </button>
          </div>
        </div>

        {/* Max Output Tokens */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Max Output Tokens (Translation Length)</label>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              min="100"
              max="8000"
              value={geminiSettings.maxOutputTokens}
              onChange={(e) => setGeminiSettings(prev => ({ ...prev, maxOutputTokens: parseInt(e.target.value) || 2000 }))}
              className={`w-24 px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
            />
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>tokens</span>
            <button
              onClick={() => saveGeminiSettings({ max_output_tokens: geminiSettings.maxOutputTokens })}
              className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
            >
              Save
            </button>
          </div>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Controls how long Gemini's response can be (100-8000). Higher = longer translations won't get cut off.</p>
        </div>
      </div>

      {/* System Prompt Editor */}
      <div className={`p-4 rounded-lg border mb-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>System Prompt</h4>
          <button
            onClick={() => setGeminiSettings(prev => ({ ...prev, showPromptEditor: !prev.showPromptEditor }))}
            className={`text-sm px-3 py-1 rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
          >
            {geminiSettings.showPromptEditor ? 'Hide' : 'Edit Prompt'}
          </button>
        </div>
        {geminiSettings.showPromptEditor && (
          <div className="space-y-3">
            <textarea
              value={geminiSettings.systemPrompt}
              onChange={(e) => setGeminiSettings(prev => ({ ...prev, systemPrompt: e.target.value }))}
              rows={8}
              className={`w-full px-3 py-2 rounded-lg border font-mono text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
              placeholder="System prompt for Gemini translations..."
            />
            <div className="flex gap-2">
              <button
                onClick={saveGeminiPrompt}
                disabled={geminiSettings.isSavingPrompt}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {geminiSettings.isSavingPrompt ? 'Saving...' : 'Save Prompt'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Test Translation */}
      <div className={`p-4 rounded-lg border mb-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Test Translation</h4>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={geminiSettings.testText}
            onChange={(e) => setGeminiSettings(prev => ({ ...prev, testText: e.target.value }))}
            placeholder="Enter text to translate..."
            onKeyDown={(e) => e.key === 'Enter' && testGemini()}
            className={`flex-1 px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
          />
          <button
            onClick={testGemini}
            disabled={!geminiSettings.testText.trim() || geminiSettings.isTesting}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {geminiSettings.isTesting ? 'Testing...' : 'Test'}
          </button>
        </div>
        {geminiSettings.testResult && (
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            {geminiSettings.testResult.error ? (
              <p className="text-red-500 text-sm">{geminiSettings.testResult.error}</p>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Input:</span>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{geminiSettings.testResult.input}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Latin:</span>
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>{geminiSettings.testResult.latin || '—'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Arabic:</span>
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`} dir="rtl">{geminiSettings.testResult.arabic || '—'}</span>
                </div>
                {geminiSettings.testResult.tokens && (
                  <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Tokens: {geminiSettings.testResult.tokens.input} in / {geminiSettings.testResult.tokens.output} out
                  </div>
                )}
                {/* Audio test button */}
                {geminiSettings.ttsEnabled && geminiSettings.testResult.latin && (
                  <button
                    onClick={() => testGeminiAudio(geminiSettings.testResult.latin)}
                    disabled={geminiSettings.isTestingAudio}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50"
                  >
                    {geminiSettings.isTestingAudio ? 'Generating...' : '🔊 Play Audio'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Usage Stats & Cache Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Usage Stats */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Usage This Month</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Translations:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{geminiSettings.usageStats.translation_count}</span>
            </div>
            <div className="flex justify-between">
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Audio Generated:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{geminiSettings.usageStats.audio_count}</span>
            </div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Month: {geminiSettings.usageStats.month || 'N/A'}
            </div>
            <button
              onClick={resetGeminiUsage}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
            >
              Reset Counters
            </button>
          </div>
        </div>

        {/* Cache Stats */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Audio Cache</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Cached Files:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{geminiSettings.cacheStats.files}</span>
            </div>
            <div className="flex justify-between">
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Size:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{geminiSettings.cacheStats.size_mb} MB</span>
            </div>
            <button
              onClick={clearGeminiCache}
              className="text-xs px-2 py-1 rounded bg-red-100 hover:bg-red-200 text-red-700"
            >
              Clear Cache
            </button>
          </div>
        </div>
      </div>


      </>)}

      {aiSubTab.gemini === 'queue' && (
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border-purple-500' : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📋</span>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Pending Gemini Translations
            </h3>
            {geminiPending.total > 0 && (
              <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-bold">{geminiPending.total}</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => loadProviderPending('gemini')} className={`px-3 py-1 text-sm rounded-lg ${
              isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
            }`}>Refresh</button>
            <button
              onClick={() => clearPendingForProvider('gemini')}
              disabled={!geminiPending.total}
              className={`px-3 py-1 text-sm rounded-lg font-medium ${
                !geminiPending.total
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
              title="Delete all pending Gemini translations"
            >
              🗑 Delete All
            </button>
          </div>
        </div>

        {pendingLoading.gemini ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading...</p>
        ) : geminiPending.items.length === 0 ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No pending Gemini translations. Translate on the main page — they'll appear here for review.
          </p>
        ) : (
          <div className="space-y-2">
            {geminiPending.items.map(item => (
              <div key={item.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {new Date(item.created_at).toLocaleDateString()}
                    </div>
                    <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      EN: {item.source_text_original}
                    </div>
                    {pendingEditing === item.id ? (
                      <div className="space-y-1 mt-2">
                        <input value={pendingEditData.latin_script || ''} onChange={e => setPendingEditData(prev => ({ ...prev, latin_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} placeholder="Latin script" />
                        <input value={pendingEditData.arabic_script || ''} onChange={e => setPendingEditData(prev => ({ ...prev, arabic_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} dir="rtl" placeholder="Arabic script" />
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => savePendingEdit(item.id, 'gemini')} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Save</button>
                          <button onClick={() => setPendingEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                        </div>
                      </div>
                    ) : pendingEditing === `reject_${item.id}` ? (
                      <div className={`flex items-center gap-2 mt-2 p-2 rounded border ${isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
                        <span className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>Reject this translation?</span>
                        <button onClick={() => confirmRejectPending(item.id, 'gemini')} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Yes, Reject</button>
                        <button onClick={() => setPendingEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                      </div>
                    ) : (
                      <>
                        <div className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>DARIJA: {item.latin_script}</div>
                        <div className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>ARABIC: <span dir="rtl">{item.arabic_script?.trim() || <span className={`italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(no arabic script)</span>}</span></div>
                      </>
                    )}
                  </div>
                  {pendingEditing !== item.id && pendingEditing !== `reject_${item.id}` && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => approvePending(item.id, 'gemini')} className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 font-medium">Approve</button>
                      <button onClick={() => { setPendingEditing(item.id); setPendingEditData({ latin_script: item.latin_script, arabic_script: item.arabic_script }); }}
                        className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 font-medium">Edit</button>
                      <button onClick={() => rejectPending(item.id, 'gemini')} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium">Reject</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {geminiPending.pages > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                {Array.from({ length: geminiPending.pages }, (_, i) => (
                  <button key={i} onClick={() => loadProviderPending('gemini', i + 1)}
                    className={`px-3 py-1 text-sm rounded ${geminiPending.page === i + 1 ? 'bg-purple-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>{i + 1}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {aiSubTab.gemini === 'approved' && (
      <div className={`p-4 rounded-lg border-2 ${
        isDarkMode ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Approved Gemini Translations
            </h3>
            {geminiApproved.total > 0 && (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-bold">{geminiApproved.total}</span>
            )}
          </div>
          <button onClick={() => loadProviderApproved('gemini')} className={`px-3 py-1 text-sm rounded-lg ${
            isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
          }`}>Refresh</button>
        </div>

        {approvedLoading.gemini ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading...</p>
        ) : geminiApproved.items.length === 0 ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No approved Gemini translations yet. Approve translations from the Queue tab.
          </p>
        ) : (
          <div className="space-y-2">
            {geminiApproved.items.map(item => (
              <div key={item.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {item.reviewed_at ? new Date(item.reviewed_at).toLocaleDateString() : new Date(item.created_at).toLocaleDateString()}
                      {item.reviewed_by && ` by ${item.reviewed_by}`}
                    </div>
                    <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      EN: {item.source_text_original}
                    </div>
                    {approvedEditing === item.id ? (
                      <div className="space-y-1 mt-2">
                        <input value={approvedEditData.latin_script || ''} onChange={e => setApprovedEditData(prev => ({ ...prev, latin_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} placeholder="Latin script" />
                        <input value={approvedEditData.arabic_script || ''} onChange={e => setApprovedEditData(prev => ({ ...prev, arabic_script: e.target.value }))}
                          className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} dir="rtl" placeholder="Arabic script" />
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => saveApprovedEdit(item.id, 'gemini')} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Save</button>
                          <button onClick={() => setApprovedEditing(null)} className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>DARIJA: {item.latin_script}</div>
                        <div className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>ARABIC: <span dir="rtl">{item.arabic_script?.trim() || <span className={`italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(no arabic script)</span>}</span></div>
                      </>
                    )}
                  </div>
                  {approvedEditing !== item.id && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => { setApprovedEditing(item.id); setApprovedEditData({ latin_script: item.latin_script, arabic_script: item.arabic_script }); }}
                        className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 font-medium">Edit</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {geminiApproved.pages > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                {Array.from({ length: geminiApproved.pages }, (_, i) => (
                  <button key={i} onClick={() => loadProviderApproved('gemini', i + 1)}
                    className={`px-3 py-1 text-sm rounded ${geminiApproved.page === i + 1 ? 'bg-green-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>{i + 1}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}
      </>)}


      {/* Bulk Import Section */}
      {translationSubTab === 'importexport' && (
      <div className={`p-4 rounded-lg border ${
        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            📦 Bulk Import Translations
          </h3>
          <button
            type="button"
            onClick={() => setAiHelper({...aiHelper, showBulkImport: !aiHelper.showBulkImport})}
            className={`px-3 py-1 text-sm rounded-lg ${
              aiHelper.showBulkImport
                ? 'bg-red-500 text-white'
                : 'bg-blue-500 text-white'
            }`}
          >
            {aiHelper.showBulkImport ? '✕ Close' : '+ Add Bulk'}
          </button>
        </div>
        
        {aiHelper.showBulkImport && (
          <div className="space-y-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Paste multiple translations in this format (separate each entry with ---):
            </p>
            
            <div className={`p-3 rounded-lg text-xs font-mono ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
              <pre>{`---
English: go
Darija Latin: ghadi
Arabic Script: غادي
Gender: male
Number: singular
Audio URL:

Variant Latin: ghadya
Variant Arabic: غادية
Variant Gender: female
Variant Number: singular

Variant Latin: ghadyin
Variant Arabic: غاديين
Variant Gender: both
Variant Number: plural
---`}</pre>
            </div>

            <textarea
              value={aiHelper.bulkText || ''}
              onChange={(e) => setAiHelper({...aiHelper, bulkText: e.target.value, bulkPreview: null, bulkError: null})}
              className={`w-full h-48 px-3 py-2 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
              placeholder="Paste your bulk translations here..."
            />
            
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  // Parse bulk text — each --- block = 1 word + its variants
                  const text = aiHelper.bulkText || '';
                  const blocks = text.split('---').filter(e => e.trim());
                  const parsed = [];

                  // Auto-parse (S)/(F)/(M)/(P) tags from Latin text
                  const extractTags = (latin) => {
                    let gender = '';
                    let number = '';
                    const upper = (latin || '').toUpperCase();
                    if (upper.includes('(F)')) gender = 'female';
                    else if (upper.includes('(M)')) gender = 'male';
                    if (upper.includes('(S)')) number = 'singular';
                    else if (upper.includes('(P)')) number = 'plural';
                    // Strip tags from Latin text
                    const clean = (latin || '').replace(/\s*\([SFMP]\)\s*/gi, ' ').trim();
                    return { clean, gender, number };
                  };

                  for (const block of blocks) {
                    const lines = block.trim().split('\n');
                    const main = {};
                    let currentVariant = null;
                    const variants = [];

                    for (const line of lines) {
                      const colonIndex = line.indexOf(':');
                      if (colonIndex === -1) continue;
                      const key = line.substring(0, colonIndex).trim().toLowerCase();
                      const value = line.substring(colonIndex + 1).trim();

                      if (key.startsWith('variant latin') || key === 'variant latin') {
                        // Start a new variant
                        if (currentVariant && (currentVariant.latin || currentVariant.arabic)) {
                          variants.push(currentVariant);
                        }
                        currentVariant = { latin: value, arabic: '', gender: '', number: '' };
                      } else if (currentVariant && (key.startsWith('variant arabic') || key === 'variant arabic')) {
                        currentVariant.arabic = value;
                      } else if (currentVariant && (key.startsWith('variant gender') || key === 'variant gender')) {
                        currentVariant.gender = value;
                      } else if (currentVariant && (key.startsWith('variant number') || key === 'variant number')) {
                        currentVariant.number = value;
                      } else if (key.includes('english')) {
                        main.english = value;
                      } else if (key.includes('darija latin') || key === 'latin') {
                        main.latin = value;
                      } else if (key.includes('arabic')) {
                        main.arabic = value;
                      } else if (key.includes('gender')) {
                        main.gender = value;
                      } else if (key.includes('number')) {
                        main.number = value;
                      } else if (key.includes('audio')) {
                        main.audioUrl = value;
                      }
                    }
                    // Push last variant if exists
                    if (currentVariant && (currentVariant.latin || currentVariant.arabic)) {
                      variants.push(currentVariant);
                    }

                    if (main.english && (main.latin || main.arabic)) {
                      // Auto-extract tags from main Latin if gender/number not explicitly set
                      if (main.latin) {
                        const tags = extractTags(main.latin);
                        main.latin = tags.clean;
                        if (!main.gender && tags.gender) main.gender = tags.gender;
                        if (!main.number && tags.number) main.number = tags.number;
                      }
                      // Add main entry
                      parsed.push({ ...main, isVariant: false });
                      // Add each variant
                      for (const v of variants) {
                        // Auto-extract tags from variant Latin if gender/number not explicitly set
                        if (v.latin) {
                          const tags = extractTags(v.latin);
                          v.latin = tags.clean;
                          if (!v.gender && tags.gender) v.gender = tags.gender;
                          if (!v.number && tags.number) v.number = tags.number;
                        }
                        parsed.push({
                          english: main.english,
                          latin: v.latin,
                          arabic: v.arabic,
                          gender: v.gender,
                          number: v.number,
                          isVariant: true,
                          variantOf: main.english
                        });
                      }
                    }
                  }

                  if (parsed.length === 0) {
                    setAiHelper({...aiHelper, bulkError: 'No valid entries found. Check the format.'});
                  } else {
                    setAiHelper({...aiHelper, bulkPreview: parsed, bulkError: null});
                  }
                }}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
              >
                👁️ Preview ({((aiHelper.bulkText || '').match(/---/g) || []).length} entries)
              </button>
              
              {aiHelper.bulkPreview && aiHelper.bulkPreview.length > 0 && (
                <button
                  type="button"
                  onClick={async () => {
                    setAiHelper({...aiHelper, isLoading: true});
                    let saved = 0;
                    let errors = 0;
                    
                    for (const entry of aiHelper.bulkPreview) {
                      try {
                        const formData = new FormData();
                        formData.append('sourceText', entry.isVariant ? entry.variantOf : entry.english);
                        formData.append('sourceLang', 'en');
                        formData.append('translatedText', entry.arabic || entry.latin);
                        formData.append('latinScript', entry.latin || '');
                        formData.append('gender', entry.gender || '');
                        formData.append('number', entry.number || '');
                        formData.append('status', 'verified');
                        if (entry.audioUrl) formData.append('audioUrl', entry.audioUrl);

                        const endpoint = entry.isVariant
                          ? `${API}/admin/translations/variant`
                          : `${API}/admin/translations`;

                        const res = await fetch(endpoint, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${adminToken}` },
                          body: formData
                        });
                        if (res.ok) saved++;
                        else errors++;
                      } catch (e) {
                        errors++;
                      }
                    }
                    
                    showNotification(`✅ Bulk import complete: ${saved} saved, ${errors} errors`, 'success');
                    setAiHelper({
                      ...aiHelper,
                      isLoading: false,
                      bulkText: '',
                      bulkPreview: null,
                      showBulkImport: false
                    });
                    loadData();
                  }}
                  disabled={aiHelper.isLoading}
                  className={`px-4 py-2 rounded-lg text-white ${
                    aiHelper.isLoading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {aiHelper.isLoading ? '⏳ Saving...' : `💾 Save All (${aiHelper.bulkPreview.length})`}
                </button>
              )}
            </div>
            
            {aiHelper.bulkError && (
              <p className="text-red-500 text-sm">❌ {aiHelper.bulkError}</p>
            )}
            
            {/* Preview Table */}
            {aiHelper.bulkPreview && aiHelper.bulkPreview.length > 0 && (
              <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                <div className={`px-3 py-2 font-medium text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-800'}`}>
                  📋 Preview ({aiHelper.bulkPreview.length} entries)
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <tr>
                        <th className="px-2 py-1 text-left">English</th>
                        <th className="px-2 py-1 text-left">Latin</th>
                        <th className="px-2 py-1 text-right">Arabic</th>
                        <th className="px-2 py-1 text-center">Gender</th>
                        <th className="px-2 py-1 text-center">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiHelper.bulkPreview.map((entry, i) => (
                        <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'} ${entry.isVariant ? (isDarkMode ? 'bg-gray-750' : 'bg-blue-50') : ''}`}>
                          <td className="px-2 py-1">{entry.isVariant ? '  ↳' : entry.english}</td>
                          <td className="px-2 py-1">{entry.latin}</td>
                          <td className="px-2 py-1 text-right" dir="rtl">{entry.arabic}</td>
                          <td className="px-2 py-1 text-center">{entry.gender || '-'}</td>
                          <td className="px-2 py-1 text-center">{entry.isVariant ? '🔀 variant' : '✅ primary'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Delete-All Pending Confirmation Modal (per AI provider) */}
      {clearAllConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-900/50' : 'bg-red-100'}`}>
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Delete All {clearAllConfirm.provider === 'gemini' ? 'Gemini' : clearAllConfirm.provider === 'deepseek' ? 'DeepSeek' : 'Groq'} Pending?
              </h3>
            </div>
            <p className={`mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              This will remove every pending translation from this provider's queue.
            </p>
            <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {clearAllConfirm.count} pending {clearAllConfirm.count === 1 ? 'entry' : 'entries'} will be deleted
              </p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Approved and rejected entries are not affected. This cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setClearAllConfirm(null)}
                disabled={clearAllConfirm.isLoading}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${clearAllConfirm.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Cancel
              </button>
              <button
                onClick={executeClearPending}
                disabled={clearAllConfirm.isLoading || !clearAllConfirm.count}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${clearAllConfirm.isLoading || !clearAllConfirm.count ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {clearAllConfirm.isLoading ? 'Deleting…' : 'Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-900/50' : 'bg-red-100'}`}>
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Delete Translation</h3>
            </div>
            <p className={`mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Are you sure you want to delete this translation?
            </p>
            <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                "{deleteConfirm.sourceText}"
              </p>
              {deleteConfirm.latinScript && (
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {deleteConfirm.latinScript}
                </p>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteTranslation(deleteConfirm.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Translation Form (Manual) */}
      {translationSubTab === 'translations' && (<>
      {/* Info Card: How Subtitles Work */}
      <div className={`p-4 rounded-lg border mb-4 ${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
        <h4 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
          How Subtitles Work
        </h4>
        <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-blue-200/80' : 'text-blue-700'}`}>
          Some words have multiple meanings (e.g. <strong>turkey</strong> = animal or country, <strong>iron</strong> = metal or clothes iron).
          When you add a word that already exists with a <strong>different meaning</strong>, the system will ask you to add a <strong>subtitle</strong> for both
          the existing and new entries to distinguish them. Users will then see <strong>"Did you mean?"</strong> when searching these words on the translation page,
          letting them pick the correct meaning before seeing the translation.
        </p>
        <p className={`text-xs mt-2 ${isDarkMode ? 'text-blue-200/60' : 'text-blue-600'}`}>
          Subtitles are managed here — no hard coding needed. Just fill in the subtitle fields when prompted.
        </p>
      </div>

      <div className={`p-4 rounded-lg border ${
        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
      }`}>
        <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          Add New Translation
        </h3>
        <div className={`text-xs font-semibold mb-3 px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-red-900/30 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-600'}`}>
          Do not add any punctuation in the source text: no ? ! . , ; : ' — the system handles punctuation automatically.
        </div>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Format: {newTranslation.sourceLang ? `${sourceLanguages.find(l => l.code === newTranslation.sourceLang)?.flag} ${sourceLanguages.find(l => l.code === newTranslation.sourceLang)?.name}` : 'Select Language'} → {newTranslation.sourceLang === 'ma' ? '🇬🇧 English' : '🇲🇦 Darija'}
        </p>
        {/* Duplicate Word Modal */}
        {duplicateCheck.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`p-6 rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Word Already Exists
              </h3>
              {/* Show all existing entries for this word */}
              <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-300'}`}>
                <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                  "{newTranslation.sourceText}" already exists ({duplicateCheck.entries?.length || 1} {(duplicateCheck.entries?.length || 1) === 1 ? 'entry' : 'entries'}):
                </p>
                {(duplicateCheck.entries || [duplicateCheck.existing]).map((entry, idx) => (
                  <div key={idx} className={`text-sm space-y-1 ${idx > 0 ? 'mt-2 pt-2 border-t' : ''} ${isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'}`}>
                    <p><strong>Translation:</strong> {entry?.translatedText} {entry?.latinScript ? `(${entry.latinScript})` : ''}</p>
                    {entry?.gender && <p><strong>Gender:</strong> {entry.gender === 'male' ? 'Male' : entry.gender === 'female' ? 'Female' : 'Both'}</p>}
                    {entry?.note && <p><strong>Subtitle:</strong> <span className="text-blue-400" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.note) }} /></p>}
                  </div>
                ))}
              </div>
              <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>YOUR NEW ENTRY:</p>
                <div className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <p><strong>Translation:</strong> {newTranslation.translatedText}</p>
                  {newTranslation.latinScript && <p><strong>Latin:</strong> {newTranslation.latinScript}</p>}
                  {newTranslation.gender && <p><strong>Gender:</strong> {newTranslation.gender === 'male' ? 'Male' : newTranslation.gender === 'female' ? 'Female' : 'Both'}</p>}
                </div>
              </div>

              {/* New Meaning mode — subtitle fields + primary pick */}
              {newMeaningMode ? (
                <div className="space-y-3 mb-4">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                    Add subtitles to distinguish the two meanings:
                  </p>
                  {/* Existing entry subtitle + primary radio */}
                  <div className={`p-3 rounded-lg border ${newMeaningPrimary === 'existing' ? (isDarkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-400 bg-blue-50') : (isDarkMode ? 'border-gray-600' : 'border-gray-200')}`}>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Subtitle for existing "{duplicateCheck.existing?.translatedText || ''}" {duplicateCheck.existing?.latinScript ? `(${duplicateCheck.existing.latinScript})` : ''}:
                    </label>
                    <input
                      type="text"
                      value={existingEntrySubtitle}
                      onChange={(e) => setExistingEntrySubtitle(e.target.value)}
                      placeholder="e.g., animal"
                      className={`w-full rounded-lg p-2 border text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                    />
                    <label className={`flex items-center gap-2 mt-2 cursor-pointer text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <input
                        type="radio"
                        name="primaryChoice"
                        checked={newMeaningPrimary === 'existing'}
                        onChange={() => setNewMeaningPrimary('existing')}
                        className="accent-blue-500"
                      />
                      Primary (shows first when users search)
                    </label>
                  </div>
                  {/* New entry subtitle + primary radio */}
                  <div className={`p-3 rounded-lg border ${newMeaningPrimary === 'new' ? (isDarkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-400 bg-blue-50') : (isDarkMode ? 'border-gray-600' : 'border-gray-200')}`}>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Subtitle for new "{newTranslation.translatedText}" {newTranslation.latinScript ? `(${newTranslation.latinScript})` : ''}:
                    </label>
                    <input
                      type="text"
                      value={newEntrySubtitle}
                      onChange={(e) => setNewEntrySubtitle(e.target.value)}
                      placeholder="e.g., country"
                      className={`w-full rounded-lg p-2 border text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                    />
                    <label className={`flex items-center gap-2 mt-2 cursor-pointer text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <input
                        type="radio"
                        name="primaryChoice"
                        checked={newMeaningPrimary === 'new'}
                        onChange={() => setNewMeaningPrimary('new')}
                        className="accent-blue-500"
                      />
                      Primary (shows first when users search)
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        if (!existingEntrySubtitle.trim() || !newEntrySubtitle.trim()) {
                          showNotification('Both subtitles are required to distinguish meanings', 'error');
                          return;
                        }
                        setDuplicateCheck({ show: false, existing: duplicateCheck.existing, entries: duplicateCheck.entries });
                        await saveTranslation(true);
                      }}
                      className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                    >
                      Save as New Meaning
                    </button>
                    <button
                      onClick={() => { setNewMeaningMode(false); setNewMeaningPrimary('existing'); }}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                    >
                      Back
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Choose an action:
                  </p>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => setNewMeaningMode(true)}
                      className="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                    >
                      Add as New Meaning (Different Word)
                    </button>
                    <button
                      onClick={() => { setDuplicateCheck({ show: false, existing: null, entries: [] }); saveTranslation(); }}
                      className="w-full px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700"
                    >
                      Override Existing
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const variantFormData = new FormData();
                          variantFormData.append('sourceText', newTranslation.sourceText);
                          variantFormData.append('sourceLang', newTranslation.sourceLang);
                          variantFormData.append('translatedText', newTranslation.translatedText);
                          variantFormData.append('latinScript', newTranslation.latinScript || '');
                          variantFormData.append('gender', newTranslation.gender || '');
                          variantFormData.append('number', newTranslation.number || '');
                          variantFormData.append('audioUrl', newTranslation.audioUrl || '');
                          const res = await fetch(`${API}/admin/translations/add-variant`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${adminToken}` },
                            body: variantFormData
                          });
                          if (res.ok) {
                            showNotification('Added as variant!', 'success');
                            setNewTranslation({ sourceText: "", sourceLang: "en", translatedText: "", latinScript: "", gender: "", number: "", status: "verified", audioUrl: "", audioEnabled: true, note: "", variantRows: [] });
                            loadData();
                          } else {
                            showNotification('Failed to add variant', 'error');
                          }
                        } catch (err) {
                          showNotification('Failed to add variant', 'error');
                        }
                        setDuplicateCheck({ show: false, existing: null, entries: [] });
                      }}
                      className="w-full px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700"
                    >
                      Add as Variant (Same Word, Different Form)
                    </button>
                    <button
                      onClick={() => { setDuplicateCheck({ show: false, existing: null, entries: [] }); setNewMeaningMode(false); }}
                      className={`w-full px-4 py-2 text-sm font-medium rounded-lg border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Edit Duplicate Check Modal — shown when editing sourceText to a word that already exists */}
        {editDuplicateCheck.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`p-6 rounded-xl shadow-xl max-w-lg w-full mx-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Word Already Exists
              </h3>
              <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-300'}`}>
                <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                  "{editDuplicateCheck.pendingArgs?.sourceText}" already exists:
                </p>
                {editDuplicateCheck.existingEntries.map((entry, idx) => (
                  <div key={idx} className={`text-sm ${idx > 0 ? 'mt-2 pt-2 border-t' : ''} ${isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'}`}>
                    <p><strong>Translation:</strong> {entry?.translatedText} {entry?.latinScript ? `(${entry.latinScript})` : ''}</p>
                    {entry?.gender && <p><strong>Gender:</strong> {entry.gender === 'male' ? 'Male' : entry.gender === 'female' ? 'Female' : 'Both'}</p>}
                  </div>
                ))}
              </div>
              <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>YOUR EDIT:</p>
                <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <p><strong>Translation:</strong> {editDuplicateCheck.pendingArgs?.translatedText}</p>
                  {editDuplicateCheck.pendingArgs?.latinScript && <p><strong>Latin:</strong> {editDuplicateCheck.pendingArgs.latinScript}</p>}
                  {editDuplicateCheck.pendingArgs?.gender && <p><strong>Gender:</strong> {editDuplicateCheck.pendingArgs.gender}</p>}
                </div>
              </div>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Do you want to save as an alternative variant or cancel?
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={async () => {
                    // Save as variant instead
                    const args = editDuplicateCheck.pendingArgs;
                    try {
                      const variantFormData = new FormData();
                      variantFormData.append('sourceText', args.sourceText);
                      variantFormData.append('sourceLang', 'en');
                      variantFormData.append('translatedText', args.translatedText);
                      variantFormData.append('latinScript', args.latinScript || '');
                      variantFormData.append('gender', args.gender || '');
                      variantFormData.append('number', args.number || '');
                      variantFormData.append('audioUrl', args.audioUrl || '');
                      const res = await fetch(`${API}/admin/translations/add-variant`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${adminToken}` },
                        body: variantFormData
                      });
                      if (res.ok) {
                        // Delete the old entry since we converted it to a variant
                        await fetch(`${API}/admin/translations/${args.id}`, {
                          method: 'DELETE',
                          headers: { 'Authorization': `Bearer ${adminToken}` }
                        });
                        showNotification('Converted to variant of existing word!', 'success');
                        loadData();
                        setEditingTranslation(null);
                      } else {
                        showNotification('Failed to add as variant', 'error');
                      }
                    } catch (err) {
                      showNotification('Failed to add as variant', 'error');
                    }
                    setEditDuplicateCheck({ show: false, pendingArgs: null, existingEntries: [] });
                  }}
                  className="w-full px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700"
                >
                  Save as Variant (Alternative)
                </button>
                <button
                  onClick={() => {
                    // Force save anyway (skip duplicate check)
                    const args = editDuplicateCheck.pendingArgs;
                    setEditDuplicateCheck({ show: false, pendingArgs: null, existingEntries: [] });
                    handleSaveTranslation(args.id, args.sourceText, args.translatedText, args.latinScript, args.gender, args.number, args.audioUrl, args.audioEnabled, args.status, args.note, true);
                  }}
                  className="w-full px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700"
                >
                  Save Anyway (Create Duplicate)
                </button>
                <button
                  onClick={() => {
                    setEditDuplicateCheck({ show: false, pendingArgs: null, existingEntries: [] });
                  }}
                  className={`w-full px-4 py-2 text-sm font-medium rounded-lg border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleCreateTranslation} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Source Language
              </label>
              <select
                value={newTranslation.sourceLang}
                onChange={(e) => setNewTranslation({...newTranslation, sourceLang: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                }`}
                required
              >
                <option value="">Select Language</option>
                {sourceLanguages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {newTranslation.sourceLang === 'ma' ? '🇲🇦 Darija Text (Source)' : `Source Text (${newTranslation.sourceLang ? sourceLanguages.find(l => l.code === newTranslation.sourceLang)?.name : 'Language'})`}
              </label>
              <input
                type="text"
                value={newTranslation.sourceText}
                onChange={(e) => setNewTranslation({...newTranslation, sourceText: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                }`}
                placeholder={newTranslation.sourceLang === 'ma' ? 'Enter Darija text' : 'Enter text to translate'}
                required
              />
            </div>
            
            {/* Latin Script Field (Romanized Darija) - First position to match display order */}
            {newTranslation.sourceLang !== 'ma' && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  🔤 Latin Darija Translation
                </label>
                <input
                  type="text"
                  value={newTranslation.latinScript}
                  onChange={(e) => setNewTranslation({...newTranslation, latinScript: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                  }`}
                  placeholder="Enter romanized version (e.g., marhaba)"
                  required
                />
                <div className="flex items-center justify-between mt-1">
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Romanized/Latin script of Darija translation
                  </p>
                  <button
                    type="button"
                    onClick={addNewTranslationVariant}
                    className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                  >
                    + Add Variant
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {newTranslation.sourceLang === 'ma' ? '🇬🇧 English Translation' : '🇲🇦 Arabic Script Darija (Optional)'}
              </label>
              <input
                type="text"
                value={newTranslation.translatedText}
                onChange={(e) => setNewTranslation({...newTranslation, translatedText: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                }`}
                placeholder={newTranslation.sourceLang === 'ma' ? 'Enter English translation' : 'Enter Arabic script (e.g., سلام)'}
                dir={newTranslation.sourceLang === 'ma' ? 'ltr' : 'rtl'}
              />
            </div>
            
            {/* Variant Rows for Add New Translation */}
            {newTranslation.sourceLang !== 'ma' && newTranslation.variantRows && newTranslation.variantRows.length > 0 && (
              <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-purple-50 border-purple-200'}`}>
                <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                  📝 Additional Variants ({newTranslation.variantRows.length}):
                </p>
                {newTranslation.variantRows.map((row, index) => (
                  <div key={index} className={`p-2 rounded mb-2 ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Variant #{index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeNewTranslationVariant(index)}
                        className="text-xs px-2 py-0.5 bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        ✕ Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={row.latin}
                        onChange={(e) => updateNewTranslationVariant(index, 'latin', e.target.value)}
                        className={`px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                        }`}
                        placeholder="Latin variant"
                      />
                      <input
                        type="text"
                        value={row.arabic}
                        onChange={(e) => updateNewTranslationVariant(index, 'arabic', e.target.value)}
                        className={`px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                        }`}
                        placeholder="Arabic variant"
                        dir="rtl"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <select
                        value={row.gender || ""}
                        onChange={(e) => updateNewTranslationVariant(index, 'gender', e.target.value)}
                        className={`px-2 py-1 text-xs border rounded ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                        }`}
                      >
                        <option value="">No Gender</option>
                        <option value="male">♂ Male (M)</option>
                        <option value="female">♀ Female (F)</option>
                        <option value="both">⚥ Both</option>
                      </select>
                      <select
                        value={row.number || ""}
                        onChange={(e) => updateNewTranslationVariant(index, 'number', e.target.value)}
                        className={`px-2 py-1 text-xs border rounded ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                        }`}
                      >
                        <option value="">No Number</option>
                        <option value="singular">Singular (S)</option>
                        <option value="plural">Plural (P)</option>
                      </select>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addNewTranslationVariant}
                  className={`w-full mt-2 py-1 text-xs rounded border-dashed border-2 ${
                    isDarkMode ? 'border-gray-500 text-gray-400 hover:border-purple-400 hover:text-purple-400' : 'border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-600'
                  }`}
                >
                  + Add Another Variant
                </button>
              </div>
            )}
            
            {/* Gender Selection - Only show when translating TO Darija */}
            {newTranslation.sourceLang !== 'ma' && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Gender (Optional) 👥
                </label>
                <select
                  value={newTranslation.gender}
                  onChange={(e) => setNewTranslation({...newTranslation, gender: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                  }`}
                >
                  <option value="">None (No gender icon)</option>
                  <option value="male">♂ Male (M icon)</option>
                  <option value="female">♀ Female (F icon)</option>
                  <option value="both">⚥ Both (👥 icon)</option>
                </select>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Shows gender icon next to translation in frontend
                </p>
              </div>
            )}
            
            {/* Number Selection (Singular/Plural) */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Number (Optional) 🔢
              </label>
              <select
                value={newTranslation.number}
                onChange={(e) => setNewTranslation({...newTranslation, number: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                }`}
              >
                <option value="">None (No number icon)</option>
                <option value="singular">Singular (S icon)</option>
                <option value="plural">Plural (P icon)</option>
              </select>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Shows S or P icon for singular/plural form
              </p>
            </div>
            
            {/* Audio URL Field */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Audio URL (Optional) 🔊
              </label>
              <input
                type="url"
                value={newTranslation.audioUrl || ''}
                onChange={(e) => setNewTranslation({...newTranslation, audioUrl: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                }`}
                placeholder="https://example.com/audio.mp3"
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Link to audio pronunciation file
              </p>
            </div>
            
            {/* Audio Enabled Toggle */}
            <div className="flex items-center gap-3">
              <label className={`text-sm font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Enable Audio
              </label>
              <button
                type="button"
                onClick={() => setNewTranslation({...newTranslation, audioEnabled: !newTranslation.audioEnabled})}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  newTranslation.audioEnabled ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    newTranslation.audioEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {newTranslation.audioEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            {/* Note Field - Rich Text Editor */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Note (Optional)
              </label>
              {/* Toolbar */}
              <div className={`flex items-center gap-1 px-2 py-1 border border-b-0 rounded-t-lg ${
                isDarkMode ? 'bg-gray-700 border-gray-500' : 'bg-gray-100 border-gray-300'
              }`}>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }} className={`px-2 py-0.5 rounded text-sm font-bold hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Bold">B</button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }} className={`px-2 py-0.5 rounded text-sm italic hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Italic">I</button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('underline'); }} className={`px-2 py-0.5 rounded text-sm underline hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Underline">U</button>
                <span className={`mx-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-300'}`}>|</span>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '1'); }} className={`px-1.5 py-0.5 rounded hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Small text" style={{fontSize:'10px'}}>S</button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '4'); }} className={`px-1.5 py-0.5 rounded hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Normal text" style={{fontSize:'14px'}}>M</button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '6'); }} className={`px-1.5 py-0.5 rounded hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Large text" style={{fontSize:'18px'}}>L</button>
                <span className={`mx-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-300'}`}>|</span>
                {['#ef4444','#f97316','#22c55e','#3b82f6','#a855f7','#6b7280'].map(color => (
                  <button key={color} type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('foreColor', false, color); }} className="w-5 h-5 rounded-full border border-gray-400 hover:scale-125 transition-transform" style={{backgroundColor: color}} title={color} />
                ))}
                <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('removeFormat'); }} className={`ml-1 px-1.5 py-0.5 rounded text-xs hover:bg-opacity-20 hover:bg-red-500 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} title="Clear formatting">✕</button>
              </div>
              {/* Editable area */}
              <div
                dir="ltr"
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => { const html = e.currentTarget?.innerHTML ?? ''; setNewTranslation({...newTranslation, note: html}); }}
                dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(newTranslation.note || '')}}
                className={`w-full px-3 py-2 border rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[50px] ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300 bg-white'
                }`}
                style={{minHeight: '50px', resize: 'vertical', overflow: 'auto', lineHeight: '1.8', fontSize: '16px'}}
              />
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Select text then click a button to format</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Translation
            </button>
          </div>
        </form>
      </div>

      
      {/* Search and Filter Section */}
      <div className={`p-4 rounded-lg border ${
        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
      }`}>
        <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🔍 Search & Filter Translations
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Search Input */}
          <div className="md:col-span-2">
            <input
              type="text"
              placeholder="Search in sourceText or Darija..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchAndFilter();
                }
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300 placeholder-gray-500'
              }`}
            />
          </div>
          
          {/* Language Filter */}
          <div>
            <select
              value={langFilter}
              onChange={(e) => setLangFilter(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
            >
              <option value="">All Languages</option>
              {sourceLanguages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
            >
              <option value="">All Status</option>
              <option value="verified">✓ Verified</option>
              <option value="auto">⚡ Auto</option>
            </select>
          </div>
        </div>
        
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={handleSearchAndFilter}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Search
          </button>
          
          <button
            onClick={() => {
              setSearchQuery('');
              setLangFilter('');
              setStatusFilter('');
              setCurrentPage(1);
              loadData();
            }}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Clear
          </button>
          
          <span className={`text-sm ml-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {pagination.total_count} results • Page {pagination.page} of {pagination.total_pages}
          </span>
        </div>
      </div>

      {/* Existing Translations */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            All Translations ({translations.filter(t => languageFilter === 'all' || t.sourceLang === languageFilter).length})
          </h3>
          
          {/* Language Filter */}
          <div className="flex items-center gap-2">
            <label className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Filter by language:
            </label>
            <select
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              className={`px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
            >
              <option value="all">All Languages</option>
              {sourceLanguages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
            {Object.keys(cardEdits).length > 0 && (
              <button
                onClick={handleQuickUpdateAll}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
              >
                Save All ({Object.keys(cardEdits).length})
              </button>
            )}
          </div>
        </div>

        {translations.filter(t => languageFilter === 'all' || t.sourceLang === languageFilter).length === 0 ? (
          <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No translations found. Add some translations to get started.
          </p>
        ) : (
          <div className="space-y-3">
            {(() => {
              // Group translations: primary first, then its variants directly underneath
              const filtered = translations.filter(t => languageFilter === 'all' || t.sourceLang === languageFilter);
              // Build groups by sourceText — primary always first
              const groups = {};
              const order = [];
              filtered.forEach(t => {
                const key = (t.sourceText || '').toLowerCase();
                if (!groups[key]) {
                  groups[key] = { primaries: [], variants: [] };
                  order.push(key);
                }
                if (t.isPrimary !== false) {
                  groups[key].primaries.push(t);
                } else {
                  groups[key].variants.push(t);
                }
              });
              // Flatten: primaries first, then variants for each group
              const grouped = [];
              order.forEach(key => {
                groups[key].primaries.forEach(t => grouped.push(t));
                groups[key].variants.forEach(t => grouped.push(t));
              });
              return grouped;
            })().map((translation) => (
              <div key={translation.id} className={`border rounded-lg p-4 ${
                isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-white'
              } ${translation.isPrimary !== false
                ? 'border-l-4 border-l-yellow-400'
                : 'border-l-4 border-l-purple-400'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs rounded font-medium ${
                        translation.status === 'verified' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {translation.status === 'verified' ? '✓ Verified' : '⚡ Auto'}
                      </span>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>
                        {sourceLanguages.find(l => l.code === translation.sourceLang)?.flag} {sourceLanguages.find(l => l.code === translation.sourceLang)?.name || translation.sourceLang} → {translation.sourceLang === 'ma' ? '🇬🇧 English' : '🇲🇦 Darija'}
                      </span>
                      {translation.isPrimary !== false ? (
                        <span className={`px-2 py-1 text-xs rounded font-medium ${
                          isDarkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
                        }`}>★ Primary</span>
                      ) : (
                        <span className={`px-2 py-1 text-xs rounded font-medium ${
                          isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                        }`}>↳ Variant</span>
                      )}
                    </div>
                    
                    {editingTranslation === translation.id ? (
                      <div className="space-y-3 p-3 bg-blue-50 dark:bg-blue-50 rounded">
                        {/* Source Text - Now Editable */}
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">
                            {translation.sourceLang === 'ma' ? '🇲🇦 Darija Text (Source)' : `Source Text (${sourceLanguages.find(l => l.code === translation.sourceLang)?.name})`} - Editable
                          </label>
                          <input
                            type="text"
                            value={editFormData.sourceText}
                            onChange={(e) => setEditFormData({...editFormData, sourceText: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                            placeholder={translation.sourceLang === 'ma' ? 'Enter Darija text' : 'Enter source text'}
                          />
                        </div>

                        {/* Latin Script - First position to match display order */}
                        {translation.sourceLang !== 'ma' && (
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-700">
                              🔤 Latin Darija Translation
                            </label>
                            <input
                              type="text"
                              value={editFormData.latinScript}
                              onChange={(e) => setEditFormData({...editFormData, latinScript: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                              placeholder="Enter romanized version (e.g., marhaba)"
                            />
                          </div>
                        )}

                        {/* Arabic Script - Second position */}
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">
                            {translation.sourceLang === 'ma' ? '🇬🇧 English Translation' : '🇲🇦 Arabic Script Darija (Optional)'}
                          </label>
                          <input
                            type="text"
                            value={editFormData.translatedText}
                            onChange={(e) => setEditFormData({...editFormData, translatedText: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                            placeholder={translation.sourceLang === 'ma' ? 'Enter English translation' : 'Enter Arabic script (e.g., سلام)'}
                            dir={translation.sourceLang === 'ma' ? 'ltr' : 'rtl'}
                          />
                        </div>

                        {/* Gender Selection - Only show when translating TO Darija */}
                        {translation.sourceLang !== 'ma' && (
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-700">
                              Gender (Optional) 👥
                            </label>
                            <select
                              value={editFormData.gender}
                              onChange={(e) => setEditFormData({...editFormData, gender: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                            >
                              <option value="">None</option>
                              <option value="male">♂ Male</option>
                              <option value="female">♀ Female</option>
                              <option value="both">⚥ Both</option>
                            </select>
                          </div>
                        )}

                        {/* Number Selection (Singular/Plural) */}
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">
                            Number (Optional) 🔢
                          </label>
                          <select
                            value={editFormData.number}
                            onChange={(e) => setEditFormData({...editFormData, number: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                          >
                            <option value="">None</option>
                            <option value="singular">Singular (S)</option>
                            <option value="plural">Plural (P)</option>
                          </select>
                        </div>

                        {/* Audio URL */}
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">
                            Audio URL (Optional) 🔊
                          </label>
                          <input
                            type="url"
                            value={editFormData.audioUrl}
                            onChange={(e) => setEditFormData({...editFormData, audioUrl: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                            placeholder="https://example.com/audio.mp3"
                          />
                        </div>

                        {/* Audio Enabled Toggle */}
                        <div className="flex items-center gap-2">
                          <label className="text-xs font-medium text-gray-700">
                            Audio Enabled:
                          </label>
                          <button
                            type="button"
                            onClick={() => setEditFormData({...editFormData, audioEnabled: !editFormData.audioEnabled})}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              editFormData.audioEnabled ? 'bg-green-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                editFormData.audioEnabled ? 'translate-x-5' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <span className="text-xs text-gray-500">
                            {editFormData.audioEnabled ? 'Yes' : 'No'}
                          </span>
                        </div>

                        {/* Note - Rich Text Editor */}
                        <div>
                          <label className="text-xs font-medium text-gray-700">Note (shown to users)</label>
                          {/* Toolbar */}
                          <div className="flex items-center gap-1 px-2 py-1 border border-b-0 rounded-t-lg bg-gray-100 border-gray-300">
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }} className="px-2 py-0.5 rounded text-xs font-bold hover:bg-opacity-20 hover:bg-blue-500 text-gray-700" title="Bold">B</button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }} className="px-2 py-0.5 rounded text-xs italic hover:bg-opacity-20 hover:bg-blue-500 text-gray-700" title="Italic">I</button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('underline'); }} className="px-2 py-0.5 rounded text-xs underline hover:bg-opacity-20 hover:bg-blue-500 text-gray-700" title="Underline">U</button>
                            <span className="mx-0.5 text-gray-300">|</span>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '1'); }} className="px-1 py-0.5 rounded hover:bg-opacity-20 hover:bg-blue-500 text-gray-700" title="Small" style={{fontSize:'9px'}}>S</button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '4'); }} className="px-1 py-0.5 rounded hover:bg-opacity-20 hover:bg-blue-500 text-gray-700" title="Normal" style={{fontSize:'13px'}}>M</button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '6'); }} className="px-1 py-0.5 rounded hover:bg-opacity-20 hover:bg-blue-500 text-gray-700" title="Large" style={{fontSize:'17px'}}>L</button>
                            <span className="mx-0.5 text-gray-300">|</span>
                            {['#ef4444','#f97316','#22c55e','#3b82f6','#a855f7','#6b7280'].map(color => (
                              <button key={color} type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('foreColor', false, color); }} className="w-4 h-4 rounded-full border border-gray-400 hover:scale-125 transition-transform" style={{backgroundColor: color}} title={color} />
                            ))}
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('removeFormat'); }} className="ml-1 px-1 py-0.5 rounded text-xs hover:bg-opacity-20 hover:bg-red-500 text-gray-500" title="Clear formatting">✕</button>
                          </div>
                          {/* Editable area */}
                          <div
                            dir="ltr"
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={(e) => { const html = e.currentTarget?.innerHTML ?? ''; setEditFormData({...editFormData, note: html}); }}
                            dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(editFormData.note || '')}}
                            className="w-full px-3 py-2 border border-gray-300 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[40px] overflow-auto bg-white text-gray-900"
                            style={{resize: 'vertical', lineHeight: '1.8', fontSize: '16px'}}
                          />
                        </div>

                        {/* Variants Section */}
                        <div className={`border-t pt-3 mt-3 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <label className={`text-xs font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                              📝 Variants (Other meanings for "{editFormData.sourceText}")
                            </label>
                            <button
                              type="button"
                              onClick={addEditVariant}
                              className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                            >
                              + Add Variant
                            </button>
                          </div>
                          
                          {/* Loading indicator */}
                          {editFormData.isLoadingVariants && (
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              ⏳ Loading variants...
                            </p>
                          )}
                          
                          {/* Existing Variants */}
                          {editFormData.variants && editFormData.variants.length > 0 && (
                            <div className="space-y-2 mb-2">
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Existing variants ({editFormData.variants.length}):
                              </p>
                              {editFormData.variants.map((v, index) => (
                                <div key={`existing-${index}`} className={`p-2 rounded border ${v.modified ? (isDarkMode ? 'border-yellow-500 bg-gray-700' : 'border-yellow-400 bg-yellow-50') : (isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200')}`}>
                                  {v.modified && <span className={`text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>✏️ Modified</span>}
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Latin</label>
                                      <input
                                        type="text"
                                        value={v.latinScript || ''}
                                        onChange={(e) => updateEditVariant(index, 'latinScript', e.target.value, false)}
                                        className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                        placeholder="Latin script"
                                      />
                                    </div>
                                    <div>
                                      <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Arabic</label>
                                      <input
                                        type="text"
                                        value={v.translatedText || ''}
                                        onChange={(e) => updateEditVariant(index, 'translatedText', e.target.value, false)}
                                        className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                        placeholder="Arabic script"
                                        dir="rtl"
                                      />
                                    </div>
                                    <div>
                                      <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Gender</label>
                                      <select
                                        value={v.gender || ''}
                                        onChange={(e) => updateEditVariant(index, 'gender', e.target.value, false)}
                                        className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                      >
                                        <option value="">—</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="both">Both</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Number</label>
                                      <select
                                        value={v.number || ''}
                                        onChange={(e) => updateEditVariant(index, 'number', e.target.value, false)}
                                        className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                      >
                                        <option value="">—</option>
                                        <option value="singular">Singular</option>
                                        <option value="plural">Plural</option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* New Variants to Add */}
                          {editFormData.newVariants && editFormData.newVariants.length > 0 && (
                            <div className="space-y-2">
                              <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                New variants to add ({editFormData.newVariants.length}):
                              </p>
                              {editFormData.newVariants.map((v, index) => (
                                <div key={`new-${index}`} className={`p-2 rounded border-2 border-dashed ${isDarkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-300'}`}>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`text-xs font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                      New Variant #{index + 1}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => removeEditVariant(index, true)}
                                      className="text-xs px-1 text-red-500 hover:text-red-700"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <input
                                      type="text"
                                      value={v.latin || ''}
                                      onChange={(e) => updateEditVariant(index, 'latin', e.target.value, true)}
                                      className={`px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                      placeholder="Latin script"
                                    />
                                    <input
                                      type="text"
                                      value={v.arabic || ''}
                                      onChange={(e) => updateEditVariant(index, 'arabic', e.target.value, true)}
                                      className={`px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                      placeholder="Arabic script"
                                      dir="rtl"
                                    />
                                    <select
                                      value={v.gender || ''}
                                      onChange={(e) => updateEditVariant(index, 'gender', e.target.value, true)}
                                      className={`px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                    >
                                      <option value="">Gender —</option>
                                      <option value="male">Male</option>
                                      <option value="female">Female</option>
                                      <option value="both">Both</option>
                                    </select>
                                    <select
                                      value={v.number || ''}
                                      onChange={(e) => updateEditVariant(index, 'number', e.target.value, true)}
                                      className={`px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                    >
                                      <option value="">Number —</option>
                                      <option value="singular">Singular</option>
                                      <option value="plural">Plural</option>
                                    </select>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* No variants message */}
                          {!editFormData.isLoadingVariants && 
                           (!editFormData.variants || editFormData.variants.length === 0) && 
                           (!editFormData.newVariants || editFormData.newVariants.length === 0) && (
                            <p className={`text-xs italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              No variants yet. Click "+ Add Variant" to add other meanings.
                            </p>
                          )}
                        </div>
                        
                        {/* Buttons */}
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={async () => {
                              // Save main translation
                              await handleSaveTranslation(
                                translation.id,
                                editFormData.sourceText,
                                editFormData.translatedText,
                                editFormData.latinScript,
                                editFormData.gender,
                                editFormData.number,
                                editFormData.audioUrl,
                                editFormData.audioEnabled,
                                "verified",
                                editFormData.note
                              );
                              
                              // Save new variants
                              const validNewVariants = (editFormData.newVariants || []).filter(v => v.latin?.trim() || v.arabic?.trim());
                              for (const variant of validNewVariants) {
                                try {
                                  const formData = new FormData();
                                  formData.append('sourceText', editFormData.sourceText);
                                  formData.append('sourceLang', 'en');
                                  formData.append('translatedText', variant.arabic || '');
                                  formData.append('latinScript', variant.latin || '');
                                  formData.append('gender', variant.gender || '');
                                  formData.append('number', variant.number || '');

                                  await fetch(`${API}/admin/translations/add-variant`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${adminToken}` },
                                    body: formData
                                  });
                                } catch (err) {
                                  console.error('Error saving variant:', err);
                                }
                              }
                              
                              // Save modified existing variants
                              const modifiedVariants = (editFormData.variants || []).filter(v => v.modified && v.id);
                              for (const variant of modifiedVariants) {
                                try {
                                  const formData = new FormData();
                                  formData.append('translatedText', variant.translatedText || '');
                                  formData.append('latinScript', variant.latinScript || '');
                                  formData.append('gender', variant.gender || '');
                                  formData.append('number', variant.number || '');

                                  await fetch(`${API}/admin/translations/${variant.id}`, {
                                    method: 'PUT',
                                    headers: { 'Authorization': `Bearer ${adminToken}` },
                                    body: formData
                                  });
                                } catch (err) {
                                  console.error('Error updating variant:', err);
                                }
                              }

                              const totalChanges = validNewVariants.length + modifiedVariants.length;
                              if (totalChanges > 0) {
                                showNotification(`Saved${modifiedVariants.length > 0 ? ` ${modifiedVariants.length} updated` : ''}${validNewVariants.length > 0 ? ` + ${validNewVariants.length} new` : ''} variant(s)`, 'success');
                              }
                            }}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                          >
                            💾 Save {editFormData.newVariants?.length > 0 ? `+ ${editFormData.newVariants.length} variant(s)` : ''}
                          </button>
                          <button
                            onClick={() => setEditingTranslation(null)}
                            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {sourceLanguages.find(l => l.code === translation.sourceLang)?.flag} <span className="font-medium">{translation.sourceText}</span> → {translation.sourceLang === 'ma' ? '🇬🇧' : '🇲🇦'} <span className="font-medium">{translation.translatedText}</span>
                        </p>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          Created: {new Date(translation.createdAt).toLocaleDateString()}
                          {translation.apiSource && ` • Source: ${translation.apiSource}`}
                        </p>

                        {/* Inline Gender & Number */}
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          <div className="flex items-center gap-1">
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Gender:</span>
                            <select
                              value={cardEdits[translation.id]?.gender !== undefined ? cardEdits[translation.id].gender : (translation.gender || '')}
                              onChange={(e) => handleCardFieldChange(translation.id, 'gender', e.target.value)}
                              className={`px-1.5 py-0.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                              } ${cardEdits[translation.id]?.gender !== undefined ? (isDarkMode ? 'ring-1 ring-yellow-500' : 'ring-1 ring-yellow-400') : ''}`}
                            >
                              <option value="">—</option>
                              <option value="male">♂ Male</option>
                              <option value="female">♀ Female</option>
                              <option value="both">⚥ Both</option>
                            </select>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Number:</span>
                            <select
                              value={cardEdits[translation.id]?.number !== undefined ? cardEdits[translation.id].number : (translation.number || '')}
                              onChange={(e) => handleCardFieldChange(translation.id, 'number', e.target.value)}
                              className={`px-1.5 py-0.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                              } ${cardEdits[translation.id]?.number !== undefined ? (isDarkMode ? 'ring-1 ring-yellow-500' : 'ring-1 ring-yellow-400') : ''}`}
                            >
                              <option value="">—</option>
                              <option value="singular">Singular</option>
                              <option value="plural">Plural</option>
                            </select>
                          </div>
                          {/* Variants badge */}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            (translation.variantCount || 1) > 1
                              ? (isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-700')
                              : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                          }`}>
                            {(translation.variantCount || 1) > 1 ? `${translation.variantCount} variants` : 'No variants'}
                          </span>
                        </div>

                        {/* Audio row */}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {(translation.audioUrl || cardEdits[translation.id]?.audioUrl) ? (
                            <>
                              <button
                                onClick={() => {
                                  const url = cardEdits[translation.id]?.audioUrl || translation.audioUrl;
                                  if (url) { const a = new Audio(url); a.play().catch(() => showNotification('Audio failed to play', 'error')); }
                                }}
                                className={`px-1.5 py-0.5 text-xs rounded ${isDarkMode ? 'bg-blue-800 text-blue-200 hover:bg-blue-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                              >
                                ▶ Play
                              </button>
                              <input
                                type="text"
                                value={cardEdits[translation.id]?.audioUrl !== undefined ? cardEdits[translation.id].audioUrl : (translation.audioUrl || '')}
                                onChange={(e) => handleCardFieldChange(translation.id, 'audioUrl', e.target.value)}
                                placeholder="Audio URL..."
                                className={`flex-1 min-w-[120px] px-2 py-0.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                                } ${cardEdits[translation.id]?.audioUrl !== undefined ? (isDarkMode ? 'ring-1 ring-yellow-500' : 'ring-1 ring-yellow-400') : ''}`}
                              />
                            </>
                          ) : (
                            <>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Audio:</span>
                              <input
                                type="text"
                                value={cardEdits[translation.id]?.audioUrl || ''}
                                onChange={(e) => handleCardFieldChange(translation.id, 'audioUrl', e.target.value)}
                                placeholder="Add audio URL..."
                                className={`flex-1 min-w-[120px] px-2 py-0.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                                } ${cardEdits[translation.id]?.audioUrl ? (isDarkMode ? 'ring-1 ring-yellow-500' : 'ring-1 ring-yellow-400') : ''}`}
                              />
                            </>
                          )}
                        </div>

                        {/* Show parent primary link on variant cards */}
                        {translation.isPrimary === false && translation.variantGroup && (
                          <div className={`mt-2 text-xs px-2 py-1 rounded ${
                            isDarkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-50 text-purple-600'
                          }`}>
                            Belongs to primary: "{translation.variantGroup}"
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    {cardEdits[translation.id] && Object.keys(cardEdits[translation.id]).length > 0 && (
                      <button
                        onClick={() => handleQuickUpdate(translation.id)}
                        className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                      >
                        Save
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        setEditingTranslation(translation.id);
                        setEditFormData({
                          sourceText: translation.sourceText,
                          translatedText: translation.translatedText || '',
                          latinScript: translation.latinScript || '',
                          gender: translation.gender || '',
                          number: translation.number || '',
                          audioUrl: translation.audioUrl || '',
                          audioEnabled: translation.audioEnabled !== false,
                          note: translation.note || '',
                          variants: [],
                          newVariants: [],
                          isLoadingVariants: true
                        });
                        const existingVariants = await fetchVariantsForEdit(translation.sourceText);
                        setEditFormData(prev => ({
                          ...prev,
                          variants: existingVariants,
                          isLoadingVariants: false
                        }));
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteConfirm({ id: translation.id, sourceText: translation.sourceText, latinScript: translation.latinScript });
                      }}
                      type="button"
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    >
                      Delete
                    </button>
                    {/* Variant-only actions: Set Primary & New Meaning */}
                    {translation.isPrimary === false && (
                      <>
                        <button
                          onClick={() => handleSetPrimary(translation.id)}
                          disabled={setPrimaryLoading === translation.id}
                          className={`px-3 py-1 text-white rounded text-sm ${
                            setPrimaryLoading === translation.id
                              ? 'bg-yellow-400 cursor-wait'
                              : 'bg-yellow-600 hover:bg-yellow-700'
                          }`}
                        >
                          {setPrimaryLoading === translation.id ? '...' : '★ Set Primary'}
                        </button>
                        <button
                          onClick={() => setNewMeaningForm(
                            newMeaningForm?.id === translation.id
                              ? null
                              : { id: translation.id, variantNote: '', primaryNote: '' }
                          )}
                          className={`px-3 py-1 text-white rounded text-sm ${
                            newMeaningForm?.id === translation.id
                              ? 'bg-gray-500 hover:bg-gray-600'
                              : 'bg-purple-600 hover:bg-purple-700'
                          }`}
                        >
                          {newMeaningForm?.id === translation.id ? 'Cancel' : '+ New Meaning'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {/* Inline New Meaning form */}
                {newMeaningForm?.id === translation.id && (
                  <div className={`mt-3 p-3 rounded border ${
                    isDarkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'
                  }`}>
                    <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                      Convert to separate meaning — add notes for disambiguation:
                    </p>
                    <div className="space-y-2">
                      <div>
                        <label className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Note for this entry ({translation.latinScript || translation.translatedText}):
                        </label>
                        <input
                          type="text"
                          value={newMeaningForm.variantNote}
                          onChange={(e) => setNewMeaningForm(prev => ({ ...prev, variantNote: e.target.value }))}
                          placeholder="e.g. clothes"
                          className={`w-full px-2 py-1 text-sm border rounded ${
                            isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                        />
                      </div>
                      <div>
                        <label className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Note for the current primary:
                        </label>
                        <input
                          type="text"
                          value={newMeaningForm.primaryNote}
                          onChange={(e) => setNewMeaningForm(prev => ({ ...prev, primaryNote: e.target.value }))}
                          placeholder="e.g. small / height"
                          className={`w-full px-2 py-1 text-sm border rounded ${
                            isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                        />
                      </div>
                      <button
                        onClick={() => handleConvertToMeaning(translation.id)}
                        disabled={!newMeaningForm.variantNote.trim() || !newMeaningForm.primaryNote.trim()}
                        className={`px-3 py-1 text-white rounded text-sm ${
                          !newMeaningForm.variantNote.trim() || !newMeaningForm.primaryNote.trim()
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-purple-600 hover:bg-purple-700'
                        }`}
                      >
                        Convert to New Meaning
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination Controls */}
        {pagination.total_pages > 1 && (
          <div className={`mt-6 flex items-center justify-between border-t pt-4 ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Showing {translations.length} of {pagination.total_count} translations
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.has_prev}
                className={`px-3 py-1 rounded transition-colors ${
                  pagination.has_prev
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              
              <span className={`px-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Page {pagination.page} of {pagination.total_pages}
              </span>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.has_next}
                className={`px-3 py-1 rounded transition-colors ${
                  pagination.has_next
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      </>)}

      {/* === TAB 5: Settings === */}
      {translationSubTab === 'settings' && (
      <div className="space-y-4">
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            ⚙️ Translation Languages
          </h3>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Languages users can translate FROM when translating to Darija.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {adminLanguages.map((language) => (
              <div key={language.id} className={`border rounded-lg p-4 transition-all ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} ${!language.is_active ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-2xl">{language.flag}</span>
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{language.name}</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Code: {language.code}</p>
                    </div>
                  </div>
                  <button onClick={() => handleToggleLanguage(language.id)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${language.is_active ? 'bg-green-600' : 'bg-gray-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${language.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className="mt-2">
                  <span className={`inline-block px-2 py-1 text-xs rounded ${language.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'}`}>
                    {language.is_active ? '✓ Active' : '✗ Disabled'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* === TAB 6: Audio & Voice === */}
      {translationSubTab === 'audiovoice' && (<>
      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🔊 Audio Settings
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Global Audio
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Enable or disable audio playback across the entire app
              </p>
            </div>

            <button
              onClick={() => {
                console.log('Toggle clicked! Current value:', settings.audioGloballyEnabled);
                handleToggleSetting('audioGloballyEnabled', !settings.audioGloballyEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.audioGloballyEnabled ? 'bg-green-600' : 'bg-gray-300'
              }`}
              title="Toggle audio setting"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.audioGloballyEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {settings.audioGloballyEnabled
              ? '✓ Audio is enabled globally. Users can play pronunciation audio.'
              : '✗ Audio is disabled globally. No audio buttons will show in the frontend.'}
          </p>
        </div>
      </div>

      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🎤 Voice Input Settings
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Voice Input Feature
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Allow users to speak into microphone for speech-to-text
              </p>
            </div>

            <button
              onClick={() => {
                console.log('Voice toggle clicked! Current value:', settings.voiceInputEnabled);
                handleToggleSetting('voiceInputEnabled', !settings.voiceInputEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.voiceInputEnabled ? 'bg-green-600' : 'bg-gray-300'
              }`}
              title="Toggle voice input setting"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.voiceInputEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {settings.voiceInputEnabled
              ? '✓ Voice input is enabled. Microphone button will appear in the input area.'
              : '✗ Voice input is disabled. Users cannot use speech-to-text feature.'}
          </p>
        </div>
      </div>
      </>)}

      {/* === TAB 7: Favorites === */}
      {translationSubTab === 'favorites' && (
      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          ❤️ Favorites Settings
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable Favorites
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Allow users to save favorite translations
              </p>
            </div>

            <button
              onClick={() => {
                handleToggleSetting('favoritesEnabled', !settings.favoritesEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.favoritesEnabled ? 'bg-green-600' : 'bg-gray-300'
              }`}
              title="Toggle favorites setting"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.favoritesEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Max Favorites Per User
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Maximum number of favorites each user can save (10-100)
              </p>
            </div>

            <input
              type="number"
              min="10"
              max="100"
              value={settings.maxFavoritesPerUser || 20}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 10 && value <= 100) {
                  handleToggleSetting('maxFavoritesPerUser', value);
                }
              }}
              className={`w-20 px-3 py-2 border rounded-lg ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
            />
          </div>

          {/* Favorites Button Icon */}
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Button Icon</p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Custom icon for Favorites button on translation page</p>
            </div>
            <div className="flex items-center gap-2">
              {settings.favorites_icon_url ? (
                <>
                  <img src={`${API.replace('/api', '')}${settings.favorites_icon_url}`} alt="" className="w-8 h-8 object-contain rounded" />
                  <button onClick={async () => {
                    setSettings(prev => ({...prev, favorites_icon_url: ''}));
                    try { await fetch(`${API}/admin/settings`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ favorites_icon_url: '' }) }); showNotification('Icon cleared', 'success'); if (onSettingsUpdate) onSettingsUpdate(); } catch {}
                  }} className="text-red-400 text-xs hover:text-red-300">✕</button>
                </>
              ) : <span className="text-2xl">❤️</span>}
              <button onClick={async () => {
                setIconPickerOpen('favorites'); setIconSearchQuery(''); setAvailableIcons([]);
                try { const r = await fetch(`${API}/icons?t=${Date.now()}`, { headers: authHeadersForForm, cache: 'no-store' }); if (r.ok) { const d = await r.json(); setAvailableIcons(d.icons || []); } } catch {}
              }} className={`px-3 py-1 rounded text-xs ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>Pick Icon</button>
            </div>
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {settings.favoritesEnabled
              ? `✓ Favorites enabled. Users can save up to ${settings.maxFavoritesPerUser || 20} translations.`
              : '✗ Favorites disabled. Users cannot save translations.'}
          </p>
        </div>
      </div>
      )}

      {/* === TAB 9: Gender Icons === */}
      {translationSubTab === 'gendericons' && (<>
      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          👥 Gender Icons Settings
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable Gender Icons
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Show Male (M), Female (F), or Both icons on translations
              </p>
            </div>

            <button
              onClick={() => {
                handleToggleSetting('genderIconsEnabled', !settings.genderIconsEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.genderIconsEnabled ? 'bg-green-600' : 'bg-gray-300'
              }`}
              title="Toggle gender icons"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.genderIconsEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {settings.genderIconsEnabled
              ? '✓ Gender icons enabled. M/F/Both badges will appear on translations.'
              : '✗ Gender icons disabled. No gender badges will be shown.'}
          </p>

          {settings.genderIconsEnabled && (
            <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600 space-y-4">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Customize Gender Icons & Colors
              </p>

              {/* Male Icon Settings */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  ♂ Male Icon
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Icon Text</label>
                    <input
                      type="text"
                      value={settings.genderIconConfig?.male?.icon || '♂ M'}
                      onChange={(e) => {
                        const newConfig = { ...settings.genderIconConfig };
                        newConfig.male = { ...newConfig.male, icon: e.target.value };
                        handleUpdateIconConfig('genderIconConfig', newConfig);
                      }}
                      className={`w-full mt-1 px-2 py-1 text-sm border rounded ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                      placeholder="♂ M"
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Background Color</label>
                    <input
                      type="color"
                      value={settings.genderIconConfig?.male?.bgColor || '#3b82f6'}
                      onChange={(e) => {
                        const newConfig = { ...settings.genderIconConfig };
                        newConfig.male = { ...newConfig.male, bgColor: e.target.value };
                        handleUpdateIconConfig('genderIconConfig', newConfig);
                      }}
                      className="w-full mt-1 h-8 border rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Text Color</label>
                    <input
                      type="color"
                      value={settings.genderIconConfig?.male?.textColor || '#1e40af'}
                      onChange={(e) => {
                        const newConfig = { ...settings.genderIconConfig };
                        newConfig.male = { ...newConfig.male, textColor: e.target.value };
                        handleUpdateIconConfig('genderIconConfig', newConfig);
                      }}
                      className="w-full mt-1 h-8 border rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Female Icon Settings */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  ♀ Female Icon
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Icon Text</label>
                    <input
                      type="text"
                      value={settings.genderIconConfig?.female?.icon || '♀ F'}
                      onChange={(e) => {
                        const newConfig = { ...settings.genderIconConfig };
                        newConfig.female = { ...newConfig.female, icon: e.target.value };
                        handleUpdateIconConfig('genderIconConfig', newConfig);
                      }}
                      className={`w-full mt-1 px-2 py-1 text-sm border rounded ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                      placeholder="♀ F"
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Background Color</label>
                    <input
                      type="color"
                      value={settings.genderIconConfig?.female?.bgColor || '#ec4899'}
                      onChange={(e) => {
                        const newConfig = { ...settings.genderIconConfig };
                        newConfig.female = { ...newConfig.female, bgColor: e.target.value };
                        handleUpdateIconConfig('genderIconConfig', newConfig);
                      }}
                      className="w-full mt-1 h-8 border rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Text Color</label>
                    <input
                      type="color"
                      value={settings.genderIconConfig?.female?.textColor || '#9f1239'}
                      onChange={(e) => {
                        const newConfig = { ...settings.genderIconConfig };
                        newConfig.female = { ...newConfig.female, textColor: e.target.value };
                        handleUpdateIconConfig('genderIconConfig', newConfig);
                      }}
                      className="w-full mt-1 h-8 border rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Both Icon Settings */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  👥 Both Icon
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Icon Text</label>
                    <input
                      type="text"
                      value={settings.genderIconConfig?.both?.icon || '👥 Both'}
                      onChange={(e) => {
                        const newConfig = { ...settings.genderIconConfig };
                        newConfig.both = { ...newConfig.both, icon: e.target.value };
                        handleUpdateIconConfig('genderIconConfig', newConfig);
                      }}
                      className={`w-full mt-1 px-2 py-1 text-sm border rounded ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                      placeholder="👥 Both"
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Background Color</label>
                    <input
                      type="color"
                      value={settings.genderIconConfig?.both?.bgColor || '#a855f7'}
                      onChange={(e) => {
                        const newConfig = { ...settings.genderIconConfig };
                        newConfig.both = { ...newConfig.both, bgColor: e.target.value };
                        handleUpdateIconConfig('genderIconConfig', newConfig);
                      }}
                      className="w-full mt-1 h-8 border rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Text Color</label>
                    <input
                      type="color"
                      value={settings.genderIconConfig?.both?.textColor || '#6b21a8'}
                      onChange={(e) => {
                        const newConfig = { ...settings.genderIconConfig };
                        newConfig.both = { ...newConfig.both, textColor: e.target.value };
                        handleUpdateIconConfig('genderIconConfig', newConfig);
                      }}
                      className="w-full mt-1 h-8 border rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Number Icons Settings (Singular/Plural) */}
      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🔢 Number Icons Settings (Singular/Plural)
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable Number Icons
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Show S (Singular) or P (Plural) icons on translations
              </p>
            </div>

            <button
              onClick={() => {
                handleToggleSetting('numberIconsEnabled', !settings.numberIconsEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.numberIconsEnabled ? 'bg-green-600' : 'bg-gray-300'
              }`}
              title="Toggle number icons"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.numberIconsEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {settings.numberIconsEnabled
              ? '✓ Number icons enabled. S/P badges will appear on translations.'
              : '✗ Number icons disabled. No singular/plural badges will be shown.'}
          </p>

          {settings.numberIconsEnabled && (
            <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600 space-y-4">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Customize Number Icons & Colors
              </p>

              {/* Singular Icon Settings */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Singular (S)
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Icon Text</label>
                    <input
                      type="text"
                      value={settings.numberIconConfig?.singular?.icon || 'S'}
                      onChange={(e) => {
                        const newConfig = { ...settings.numberIconConfig };
                        newConfig.singular = { ...newConfig.singular, icon: e.target.value };
                        handleUpdateIconConfig('numberIconConfig', newConfig);
                      }}
                      className={`w-full mt-1 px-2 py-1 text-sm border rounded ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                      placeholder="S"
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Background Color</label>
                    <input
                      type="color"
                      value={settings.numberIconConfig?.singular?.bgColor || '#10b981'}
                      onChange={(e) => {
                        const newConfig = { ...settings.numberIconConfig };
                        newConfig.singular = { ...newConfig.singular, bgColor: e.target.value };
                        handleUpdateIconConfig('numberIconConfig', newConfig);
                      }}
                      className="w-full mt-1 h-8 border rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Text Color</label>
                    <input
                      type="color"
                      value={settings.numberIconConfig?.singular?.textColor || '#065f46'}
                      onChange={(e) => {
                        const newConfig = { ...settings.numberIconConfig };
                        newConfig.singular = { ...newConfig.singular, textColor: e.target.value };
                        handleUpdateIconConfig('numberIconConfig', newConfig);
                      }}
                      className="w-full mt-1 h-8 border rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Plural Icon Settings */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Plural (P)
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Icon Text</label>
                    <input
                      type="text"
                      value={settings.numberIconConfig?.plural?.icon || 'P'}
                      onChange={(e) => {
                        const newConfig = { ...settings.numberIconConfig };
                        newConfig.plural = { ...newConfig.plural, icon: e.target.value };
                        handleUpdateIconConfig('numberIconConfig', newConfig);
                      }}
                      className={`w-full mt-1 px-2 py-1 text-sm border rounded ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                      placeholder="P"
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Background Color</label>
                    <input
                      type="color"
                      value={settings.numberIconConfig?.plural?.bgColor || '#f97316'}
                      onChange={(e) => {
                        const newConfig = { ...settings.numberIconConfig };
                        newConfig.plural = { ...newConfig.plural, bgColor: e.target.value };
                        handleUpdateIconConfig('numberIconConfig', newConfig);
                      }}
                      className="w-full mt-1 h-8 border rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Text Color</label>
                    <input
                      type="color"
                      value={settings.numberIconConfig?.plural?.textColor || '#9a3412'}
                      onChange={(e) => {
                        const newConfig = { ...settings.numberIconConfig };
                        newConfig.plural = { ...newConfig.plural, textColor: e.target.value };
                        handleUpdateIconConfig('numberIconConfig', newConfig);
                      }}
                      className="w-full mt-1 h-8 border rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </>)}

      {/* === TAB 10: Limits === */}
      {translationSubTab === 'limits' && (<>
      {/* Translation Page Visibility — top-of-tab toggle to hide from user menu */}
      <div className={`p-6 rounded-lg border-2 mb-4 ${
        settings.translationPageVisible !== false
          ? (isDarkMode ? 'border-green-600 bg-green-900/20' : 'border-green-300 bg-green-50')
          : (isDarkMode ? 'border-red-600 bg-red-900/20' : 'border-red-300 bg-red-50')
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <h4 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              👁️ Translation Page Visibility
            </h4>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {settings.translationPageVisible !== false
                ? 'Visible — users see the Translation menu item.'
                : 'Hidden — Translation menu item is not shown to users (admins still see it).'}
            </p>
          </div>
          <button
            onClick={() => handleToggleSetting('translationPageVisible', settings.translationPageVisible === false)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
              settings.translationPageVisible !== false ? 'bg-green-500' : 'bg-gray-400'
            }`}
            aria-label="Toggle translation page visibility"
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
              settings.translationPageVisible !== false ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>

      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🔢 Translation Limits
        </h4>

        <div className="space-y-4">
          {/* Free Translations Per Day */}
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Free Translations Per Day
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Daily translation limit for non-logged and free users (1-100)
              </p>
            </div>

            <input
              type="number"
              min="1"
              max="100"
              value={settings.freeTranslationsPerDay || 5}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 1 && value <= 100) {
                  handleToggleSetting('freeTranslationsPerDay', value);
                }
              }}
              className={`w-20 px-3 py-2 border rounded-lg ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
            />
          </div>

          {/* Free Preview Translations */}
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Free Preview Translations
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Number of free preview translations before counting starts (0-10)
              </p>
            </div>

            <input
              type="number"
              min="0"
              max="10"
              value={settings.freePreviewTranslations || 2}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 0 && value <= 10) {
                  handleToggleSetting('freePreviewTranslations', value);
                }
              }}
              className={`w-20 px-3 py-2 border rounded-lg ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
            />
          </div>

          {/* Min Words for Auto-Translate */}
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Min Words for Auto-Translate
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Minimum number of words before auto-translate triggers (1-5)
              </p>
            </div>

            <input
              type="number"
              min="1"
              max="5"
              value={settings.minWordsForAutoTranslate || 2}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 1 && value <= 5) {
                  handleToggleSetting('minWordsForAutoTranslate', value);
                }
              }}
              className={`w-20 px-3 py-2 border rounded-lg ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
            />
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {`✓ First ${settings.freePreviewTranslations || 2} translations are FREE preview (don't count). Then users get ${settings.freeTranslationsPerDay || 5} translations/day. Auto-translate triggers after ${settings.minWordsForAutoTranslate || 2} words.`}
          </p>

        </div>
      </div>

      {/* AI Translation Pipeline Card */}
      <div className={`p-6 rounded-lg border mt-4 ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          AI Translation Pipeline — Database vs AI
        </h4>

        <div className="space-y-4">
          {/* Max Words for Database Lookup */}
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Max Words for Database Lookup
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Maximum number of words that use word-by-word database translation (2-10)
              </p>
            </div>

            <input
              type="number"
              min="2"
              max="10"
              value={settings.maxWordsForDatabase || 4}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 2 && value <= 10) {
                  handleToggleSetting('maxWordsForDatabase', value);
                }
              }}
              className={`w-20 px-3 py-2 border rounded-lg ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
            />
          </div>

          {/* Max Translation Characters (anti-DOS / cost protection) */}
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Max Translation Characters
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Maximum characters per translation request — anything longer is rejected with HTTP 422 (50-5000)
              </p>
            </div>
            <input
              type="number"
              min="50"
              max="5000"
              step="50"
              value={settings.maxTranslationChars || 500}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 50 && value <= 5000) {
                  handleToggleSetting('maxTranslationChars', value);
                }
              }}
              className={`w-24 px-3 py-2 border rounded-lg ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
            />
          </div>

          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              This controls how the translation system decides between using your <strong>database</strong> or sending to <strong>AI</strong> (Gemini, DeepSeek, Groq).
            </p>
            <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <strong>How it works:</strong> When a user types a phrase, the system first tries to translate it word-by-word from your database. This is fast and works great for short phrases where every word exists in the database.
            </p>
            <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              But for longer sentences, word-by-word translation loses grammar and natural flow. So phrases longer than this limit skip the database and go to the smart translation system instead — first trying local pattern matching, and if that fails, sending to whichever AI provider is enabled.
            </p>
            <div className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <strong>Example (set to {settings.maxWordsForDatabase || 4}):</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>"give me water" (3 words) → Database word-by-word (instant)</li>
                <li>"I want to eat" (4 words) → {(settings.maxWordsForDatabase || 4) >= 4 ? 'Database word-by-word (instant)' : 'Smart system → AI (natural sentence)'}</li>
                <li>"I want to visit Morocco" (5 words) → {(settings.maxWordsForDatabase || 4) >= 5 ? 'Database word-by-word (instant)' : 'Smart system → AI (natural sentence)'}</li>
              </ul>
            </div>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              If any word in the phrase is missing from the database, the system automatically falls through to the smart system and then AI — even for short phrases. This setting only applies when ALL words exist in the database. Works for ALL AI providers.
            </p>
          </div>
        </div>
      </div>

      {/* Spell Check & Grammar Correction Toggle */}
      <div className={`p-6 rounded-lg border mt-4 ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          Spell Check & Grammar Correction
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable Spell Check & Grammar
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                When ON, the system checks and corrects English spelling and grammar before translating. When OFF, the input goes directly to the translation pipeline as the user typed it.
              </p>
            </div>

            <button
              onClick={() => {
                handleToggleSetting('spellCheckEnabled', !settings.spellCheckEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.spellCheckEnabled !== false ? 'bg-green-600' : 'bg-gray-300'
              }`}
              title="Toggle spell check and grammar correction"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.spellCheckEnabled !== false ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <strong>What it does:</strong> Before translating, the system runs two checks on the English input:
            </p>
            <ul className={`text-sm list-disc list-inside space-y-1 mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li><strong>Spell Check</strong> — fixes typos instantly (e.g. "helo" → "hello")</li>
              <li><strong>Grammar Correction</strong> — fixes broken grammar using LanguageTool (e.g. "he go store" → "he goes to the store")</li>
            </ul>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <strong>Why turn it OFF:</strong> Sometimes the correction changes the English words, which causes a different (wrong) Darija translation to show up. If you see "Searched:" showing a different sentence than what you typed, this is the cause. Turn it OFF so sentences go straight to translation without being modified.
            </p>
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {settings.spellCheckEnabled !== false
              ? 'Spell check & grammar correction is ACTIVE. English input may be corrected before translation.'
              : 'Spell check & grammar correction is OFF. Input goes directly to translation as typed.'}
          </p>
        </div>
      </div>

      {/* Brain vs Brain Corrections — visual comparison so admin doesn't confuse them */}
      <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-amber-700 bg-amber-900/10' : 'border-amber-300 bg-amber-50'}`}>
        <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-amber-200' : 'text-amber-900'}`}>
          ℹ️ Brain vs Brain Corrections — these are TWO DIFFERENT things
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Brain (buggy) — RED side */}
          <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'border-red-700 bg-red-900/20' : 'border-red-300 bg-red-50'}`}>
            <h5 className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
              🧠 Brain
            </h5>
            <ul className={`text-sm space-y-1 list-disc list-inside ${isDarkMode ? 'text-red-100' : 'text-red-900'}`}>
              <li>The COMPOSITION engine</li>
              <li>Tries to algorithmically build verbs (<code>stem + prefix + ending</code>)</li>
              <li>Buggy with Darija — produces wrong forms (<em>kanmcha, kanbgha</em>, etc.)</li>
              <li>Where bugs come from</li>
              <li><strong>What the toggle below DISABLES</strong></li>
            </ul>
          </div>
          {/* Brain Corrections (good) — GREEN side */}
          <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'border-green-700 bg-green-900/20' : 'border-green-300 bg-green-50'}`}>
            <h5 className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-green-200' : 'text-green-800'}`}>
              💡 Brain Corrections
            </h5>
            <ul className={`text-sm space-y-1 list-disc list-inside ${isDarkMode ? 'text-green-100' : 'text-green-900'}`}>
              <li>A phrasebook lookup (NOT composition)</li>
              <li>Stored in <code>brain_corrections</code> collection — direct DB read</li>
              <li>Where YOUR manual fixes live (click-to-fix from Brain Activity Log)</li>
              <li>Reliable — no algorithm, just exact-match lookup</li>
              <li><strong>STILL RUNS regardless of toggle. Doesn't interfere — only helps.</strong></li>
            </ul>
          </div>
        </div>
        <p className={`text-xs mt-3 ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
          <strong>How they relate:</strong> Brain shows you a translation in Brain Activity Log. If it's wrong, you click "Fix" → enter correct version → that one phrase gets saved to <code>brain_corrections</code>. Next time anyone types the EXACT same input, they get YOUR fix (Brain Corrections returns it before brain even runs).
        </p>

        {/* Flow diagram — order of execution when Simple Pipeline is ON */}
        <div className={`mt-4 p-3 rounded ${isDarkMode ? 'bg-gray-900/40 border border-amber-700' : 'bg-white border border-amber-300'}`}>
          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-amber-200' : 'text-amber-900'}`}>
            The flow when ON
          </p>
          <pre className={`text-xs leading-5 font-mono whitespace-pre overflow-x-auto ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{`User types something
    ↓
1. Brain Corrections check (phrasebook) → hit → return
    ↓ (no correction)
2. DB exact match in translations + verb_conjugations → hit → return
    ↓ (no DB match)
3. AI cache approved → hit → return
    ↓ (no cache)
4. AI cascade (Gemini → OpenAI → DeepSeek) → return`}</pre>
        </div>
      </div>

      {/* Simple Pipeline Toggle (skip brain + grammar engines) */}
      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-purple-700 bg-purple-900/20' : 'border-purple-200 bg-purple-50'
      }`}>
        <h4 className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          ⚡ Simple Pipeline (skip brain)
          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Recommended</span>
        </h4>

        {/* RED LABEL: Brain is hardcoded disabled — double-lock protection */}
        <div className={`mb-4 p-3 rounded border-2 ${isDarkMode ? 'border-red-700 bg-red-900/30' : 'border-red-400 bg-red-50'}`}>
          <p className={`text-sm font-bold flex items-center gap-2 ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
            🚫 Brain is HARDCODED DISABLED in code (server.py)
          </p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>
            For maximum safety, brain is locked off by a hardcoded <code>False and</code> guard, in addition to this toggle. Even if the toggle below is flipped OFF, brain still won't run. To re-enable brain, you must <strong>both</strong> remove the hardcode from <code>server.py</code> AND turn this toggle OFF. This double-lock prevents accidental re-enable.
          </p>
        </div>

        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          <strong>OFF (default):</strong> Brain pipeline runs. Brain tries to translate using composition rules, sentence patterns, etc.<br/>
          <strong>ON:</strong> Brain SKIPPED. Translation flow becomes <strong>Brain Corrections → DB exact match → AI cascade</strong>.
        </p>

        <div className={`text-xs mb-4 p-3 rounded ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-purple-200'}`}>
          <p className="font-semibold mb-2">When ON, ALL 8 middle systems are skipped:</p>
          <ul className="list-disc list-inside space-y-0.5 ml-2">
            <li>Brain (darija_brain composition + breakdown)</li>
            <li>AI Review with RAG (brain-as-context AI prompt)</li>
            <li>DB pattern templates (translation_patterns engine)</li>
            <li>Darija pattern translator (hardcoded patterns)</li>
            <li>Smart grammar translator (verb+object split, sentence patterns + grammar rules)</li>
            <li>Word-by-word fallback (per-word DB lookup + assembly)</li>
            <li>Spell check (pyspellchecker)</li>
            <li>LanguageTool grammar correction</li>
          </ul>
          <p className="font-semibold mt-3 mb-2">Still runs (regardless of toggle):</p>
          <ul className="list-disc list-inside space-y-0.5 ml-2">
            <li>Brain Corrections (your phrasebook fixes — always win)</li>
            <li>DB exact match in <code>translations</code> + <code>verb_conjugations</code></li>
            <li>AI cache approved entries</li>
            <li>AI cascade (Gemini → OpenAI → DeepSeek per dropdown)</li>
          </ul>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable Simple Pipeline
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ON = brain SKIPPED, DB match → AI only | OFF = brain runs (today's pipeline)
              </p>
            </div>

            <button
              onClick={() => {
                handleToggleSetting('simplePipelineEnabled', !settings.simplePipelineEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                settings.simplePipelineEnabled ? 'bg-purple-600' : 'bg-gray-300'
              }`}
              title="Toggle simple pipeline (skip brain)"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.simplePipelineEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {settings.simplePipelineEnabled
              ? '✅ Simple pipeline ACTIVE. Brain DISABLED. All 8 middle systems skipped. DB match → AI cascade only.'
              : '⚙️ Simple pipeline OFF. Brain ENABLED. Today\'s full pipeline runs (brain + grammar + sentence patterns + word-by-word). Toggle ON to disable brain.'}
          </p>
        </div>
      </div>

      {/* Smart Grammar / Pattern Engines — second hardcoded-off system with admin toggle */}
      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-orange-700 bg-orange-900/20' : 'border-orange-300 bg-orange-50'
      }`}>
        <h4 className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🧪 Smart Grammar / Pattern Engines
          <span className="text-xs px-2 py-1 bg-orange-200 text-orange-900 rounded-full">Currently DISABLED</span>
        </h4>

        {/* RED LABEL — Smart Grammar is hardcoded disabled */}
        <div className={`mb-4 p-3 rounded border-2 ${isDarkMode ? 'border-red-700 bg-red-900/30' : 'border-red-400 bg-red-50'}`}>
          <p className={`text-sm font-bold flex items-center gap-2 ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
            🚫 Smart Grammar System is HARDCODED DISABLED in code (server.py)
          </p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>
            For maximum safety, all algorithmic translation engines are locked off by a hardcoded <code>False and</code> guard, in addition to this toggle. Even if the toggle below is flipped ON, these engines still won't run. To re-enable, you must <strong>both</strong> remove the hardcode from <code>server.py</code> AND turn this toggle ON. Same double-lock pattern as brain.
          </p>
        </div>

        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <strong>What's disabled:</strong> The OLD pipeline algorithmic engines that try to compose translations from pieces.
        </p>

        <div className={`text-xs mb-4 p-3 rounded ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-orange-200'}`}>
          <p className="font-semibold mb-2">Includes:</p>
          <ul className="list-disc list-inside space-y-0.5 ml-2">
            <li><code>smart_grammar_translate</code> — verb+object split, sentence_patterns + grammar_rules consumption</li>
            <li><code>darija_pattern_translate</code> — hardcoded Darija pattern matching</li>
            <li><code>db_pattern_translate</code> — translation_patterns template engine</li>
            <li><code>_translate_sentence_words</code> — word-by-word DB lookup + assembly</li>
            <li>Multi-sentence splitter (splits on . ! ?)</li>
          </ul>
          <p className="font-semibold mt-3 mb-2">Why disabled:</p>
          <p className="ml-2">
            All these engines try to ALGORITHMICALLY translate by composing pieces. They produce unreliable Darija (wrong forms, broken word order, missing context). User decision 2026-05-02: stop fixing broken algorithms. Grow DB via AI cache approval instead. AI handles novel sentences cleanly; DB grows over time.
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable Smart Grammar / Patterns
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ON = OLD pipeline runs (if hardcode also removed) | OFF = AI handles novel input
              </p>
            </div>

            <button
              onClick={() => {
                handleToggleSetting('smartGrammarEnabled', !settings.smartGrammarEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                settings.smartGrammarEnabled ? 'bg-orange-600' : 'bg-gray-300'
              }`}
              title="Toggle smart grammar / pattern engines (hardcoded off in code)"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.smartGrammarEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            ❌ Smart Grammar OFF (hardcoded). All algorithmic engines disabled regardless of toggle. AI handles novel input. To grow your DB: review AI translations in the cache queue → approve good ones → next time same input is instant DB hit.
          </p>
        </div>
      </div>
      </>)}

      {/* === TAB 11: Auto-Translation === */}
      {translationSubTab === 'autotranslation' && (<>
      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🤖 Auto-Translation Settings
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable Auto-Translation (EN→Darija only)
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Allow automatic translations for English words not in database using external APIs
              </p>
            </div>

            <button
              onClick={() => {
                console.log('Auto-translation toggle clicked! Current value:', settings.autoTranslationEnabled);
                handleToggleSetting('autoTranslationEnabled', !settings.autoTranslationEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.autoTranslationEnabled ? 'bg-green-600' : 'bg-gray-300'
              }`}
              title="Toggle auto-translation setting"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.autoTranslationEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {settings.autoTranslationEnabled
              ? '✓ Auto-translation is enabled. External APIs will be used for English→Darija when not found in database.'
              : '✗ Auto-translation is disabled. Only manual translations in database will be shown.'}
          </p>
        </div>
      </div>

      {/* DB-Driven Translation Pipeline Toggle */}
      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          DB-Driven Translation Pipeline
          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">New</span>
        </h4>

        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Uses translations from the database instead of hardcoded pattern engines. Toggle OFF to fall back to the old system.
        </p>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable New DB Pipeline
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ON = DB-driven translation | OFF = old hardcoded engines
              </p>
            </div>

            <button
              onClick={() => {
                handleToggleSetting('newPipelineEnabled', !settings.newPipelineEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.newPipelineEnabled ? 'bg-green-600' : 'bg-gray-300'
              }`}
              title="Toggle DB-driven translation pipeline"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.newPipelineEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {settings.newPipelineEnabled
              ? 'New DB pipeline is ACTIVE. All translations come from database entries.'
              : 'Old hardcoded engines are active. Toggle ON to switch to DB-driven pipeline.'}
          </p>
        </div>
      </div>

      <div className={`p-6 rounded-lg border ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <h4 className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🌍 Multi-Language Translation Pipeline
          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">New!</span>
        </h4>

        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Allow users to type in ANY language (Spanish, French, etc.) and get your manual Darija translations via automatic English conversion.
        </p>

        <div className="space-y-4">
          {/* Enable Multi-Language */}
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable Multi-Language Pipeline
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                User Input (Any Lang) → English → Your Darija Translations
              </p>
            </div>

            <button
              onClick={() => {
                handleToggleSetting('multiLanguageEnabled', !settings.multiLanguageEnabled);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.multiLanguageEnabled !== false ? 'bg-green-600' : 'bg-gray-300'
              }`}
              title="Toggle multi-language pipeline"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.multiLanguageEnabled !== false ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Lingva API URL */}
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Lingva API URL
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Lingva (primary) + MyMemory (backup). Default: https://lingva.ml
              </p>
            </div>

            <input
              type="url"
              value={settings.lingvaApiUrl || 'https://lingva.ml'}
              onChange={(e) => {
                handleToggleSetting('lingvaApiUrl', e.target.value);
              }}
              className={`w-64 px-3 py-2 border rounded-lg ${
                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
              }`}
              placeholder="https://lingva.ml"
            />
          </div>

          {/* Status Indicator */}
          <div className={`p-3 rounded-lg ${
            settings.multiLanguageEnabled !== false
              ? 'bg-green-100 dark:bg-green-900/30'
              : 'bg-gray-100 dark:bg-gray-800'
          }`}>
            <p className={`text-sm ${
              settings.multiLanguageEnabled !== false
                ? 'text-green-800 dark:text-green-200'
                : 'text-gray-600 dark:text-gray-400'
            }`}>
              {settings.multiLanguageEnabled !== false
                ? '✅ Multi-language pipeline ACTIVE (Lingva + MyMemory backup). Users can type in any language!'
                : '❌ Multi-language pipeline DISABLED. Users can only use English or Darija.'}
            </p>
          </div>
        </div>
      </div>
      </>)}

      {/* === TAB: Feedback === */}
      {translationSubTab === 'feedback' && (
      <div className="space-y-4">
        {/* Inner sub-tab navigation */}
        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'requests', label: 'User Requests', icon: '🔍' },
            { id: 'suggestions', label: 'Word Suggestions', icon: '💡' },
            { id: 'feedback', label: 'Feedback', icon: '👍' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFeedbackSubTab(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                feedbackSubTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* User Requests */}
        {feedbackSubTab === 'requests' && (
        <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            User Requests ({userRequests.length})
          </h3>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {userRequestsCount.total} / {userRequestsCount.limit} records (stops at limit)
          </p>
        </div>
        {userRequests.length > 0 && (
          <button
            onClick={() => setShowFlushRequestsModal(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete All
          </button>
        )}
      </div>
      
      {userRequests.length === 0 ? (
        <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No user requests found.
        </p>
      ) : (
        <div className="space-y-3">
          {userRequests.map((request) => (
            <div key={request.id} className={`border rounded-lg p-4 ${
              isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    "{request.sourceText}"
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Language: {sourceLanguages.find(l => l.code === request.sourceLang)?.name || request.sourceLang}
                    {request.responseTimeMs && ` • Response: ${request.responseTimeMs}ms`}
                  </p>
                </div>
                <div className="text-right flex items-center gap-2">
                  <span className={`inline-block px-2 py-1 text-xs rounded ${
                    request.found 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {request.found ? 'Found' : 'Not Found'}
                  </span>
                  <button
                    onClick={() => {
                      if (editingRequestId === request.id) {
                        setEditingRequestId(null);
                      } else {
                        setEditingRequestId(request.id);
                        setRequestEditForm({
                          sourceText: request.sourceText,
                          translatedText: '',
                          latinScript: '',
                          gender: '',
                          number: '',
                          audioUrl: ''
                        });
                      }
                    }}
                    className={`px-3 py-1 text-xs rounded transition-colors ${
                      editingRequestId === request.id
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {editingRequestId === request.id ? 'Cancel' : '+ Add to DB'}
                  </button>
                </div>
              </div>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {new Date(request.timestamp).toLocaleString()}
              </p>

              {/* CRUD Form for adding translation */}
              {editingRequestId === request.id && (
                <div className={`mt-4 p-4 rounded-lg border-2 ${
                  isDarkMode ? 'border-blue-500 bg-gray-800' : 'border-blue-300 bg-blue-50'
                }`}>
                  <h5 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    Add Translation to Database
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Source Text (English) *
                      </label>
                      <input
                        type="text"
                        placeholder="Edit source text if needed"
                        value={requestEditForm.sourceText || request.sourceText}
                        onChange={(e) => setRequestEditForm({...requestEditForm, sourceText: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Darija Translation *
                      </label>
                      <input
                        type="text"
                        placeholder="Enter Darija translation"
                        value={requestEditForm.translatedText}
                        onChange={(e) => setRequestEditForm({...requestEditForm, translatedText: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Latin Script (Optional)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., salam"
                        value={requestEditForm.latinScript}
                        onChange={(e) => setRequestEditForm({...requestEditForm, latinScript: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Gender (Optional)
                      </label>
                      <select
                        value={requestEditForm.gender}
                        onChange={(e) => setRequestEditForm({...requestEditForm, gender: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Any</option>
                        <option value="male">Male (👨)</option>
                        <option value="female">Female (👩)</option>
                        <option value="both">Both/Plural (👥)</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Number (Optional)
                      </label>
                      <select
                        value={requestEditForm.number}
                        onChange={(e) => setRequestEditForm({...requestEditForm, number: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Any</option>
                        <option value="singular">Singular</option>
                        <option value="plural">Plural</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Audio URL (Optional)
                      </label>
                      <input
                        type="text"
                        placeholder="https://..."
                        value={requestEditForm.audioUrl}
                        onChange={(e) => setRequestEditForm({...requestEditForm, audioUrl: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                        }`}
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => saveRequestAsTranslation(request)}
                      disabled={!requestEditForm.translatedText}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        requestEditForm.translatedText
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      }`}
                    >
                      💾 Save to Database
                    </button>
                    <button
                      onClick={() => setEditingRequestId(null)}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Flush Requests Confirmation Modal */}
      {showFlushRequestsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl p-6 max-w-md w-full shadow-2xl ${
            isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🗑️</div>
              <h3 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Delete All User Requests?
              </h3>
              <p className={`text-base mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to delete all {userRequests.length} user requests?
              </p>
              <p className={`text-sm font-semibold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                ⚠️ This action cannot be undone!
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowFlushRequestsModal(false)}
                className={`flex-1 px-6 py-3 rounded-lg font-bold transition-all ${
                  isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={flushUserRequests}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-bold hover:from-red-700 hover:to-red-800 transition-all"
              >
                🗑️ Delete All
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
        )}

        {/* Word Suggestions */}
        {feedbackSubTab === 'suggestions' && (
        <div className="space-y-6">
      {/* Request Word Button Icon */}
      <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Request Word Button Icon</p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Custom icon for Request Word button on translation page</p>
          </div>
          <div className="flex items-center gap-2">
            {settings.request_word_icon_url ? (
              <>
                <img src={`${API.replace('/api', '')}${settings.request_word_icon_url}`} alt="" className="w-8 h-8 object-contain rounded" />
                <button onClick={async () => {
                  setSettings(prev => ({...prev, request_word_icon_url: ''}));
                  try { await fetch(`${API}/admin/settings`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ request_word_icon_url: '' }) }); showNotification('Icon cleared', 'success'); if (onSettingsUpdate) onSettingsUpdate(); } catch {}
                }} className="text-red-400 text-xs hover:text-red-300">✕</button>
              </>
            ) : <span className="text-2xl">➕</span>}
            <button onClick={async () => {
              setIconPickerOpen('request_word'); setIconSearchQuery(''); setAvailableIcons([]);
              try { const r = await fetch(`${API}/icons?t=${Date.now()}`, { headers: authHeadersForForm, cache: 'no-store' }); if (r.ok) { const d = await r.json(); setAvailableIcons(d.icons || []); } } catch {}
            }} className={`px-3 py-1 rounded text-xs ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>Pick Icon</button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          💡 Word Suggestions ({wordSuggestions.length})
        </h3>
      </div>
      
      {wordSuggestions.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <p>No word suggestions yet.</p>
          <p className="text-sm mt-2">Users can submit word requests from the translator page.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {wordSuggestions.map((suggestion) => (
            <div key={suggestion.id} className={`border rounded-lg p-4 ${
              isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-white'
            } ${suggestion.status === 'approved' ? 'border-green-500 border-l-4' : suggestion.status === 'rejected' ? 'border-red-500 border-l-4' : ''}`}>
              
              {editingSuggestion === suggestion.id ? (
                // Edit Mode
                <div className="space-y-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Word
                    </label>
                    <input
                      type="text"
                      value={editSuggestionData.word}
                      onChange={(e) => setEditSuggestionData({...editSuggestionData, word: e.target.value})}
                      className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Language
                    </label>
                    <select
                      value={editSuggestionData.language}
                      onChange={(e) => setEditSuggestionData({...editSuggestionData, language: e.target.value})}
                      className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                    >
                      {sourceLanguages.map(lang => (
                        <option key={lang.code} value={lang.code}>
                          {lang.flag} {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Message
                    </label>
                    <textarea
                      value={editSuggestionData.message}
                      onChange={(e) => setEditSuggestionData({...editSuggestionData, message: e.target.value})}
                      className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                      rows="2"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSaveSuggestion(suggestion.id)}
                      className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    >
                      💾 Save
                    </button>
                    <button
                      onClick={() => setEditingSuggestion(null)}
                      className="px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode - Card/Box Layout (Option A Format)
                <div className="space-y-3">
                  {/* Word Title with Language */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Word:
                      </p>
                      <div className="flex items-center gap-2">
                        <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          "{suggestion.word}"
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {sourceLanguages.find(l => l.code === suggestion.language)?.flag} {sourceLanguages.find(l => l.code === suggestion.language)?.name || suggestion.language}
                        </span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-semibold rounded ${
                      suggestion.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      suggestion.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {suggestion.status.toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Submitted Date */}
                  <div>
                    <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Submitted:
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {new Date(suggestion.createdAt).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                  </div>
                  
                  {/* User Info */}
                  <div>
                    <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      User:
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {suggestion.user_name || 'Anonymous'} ({suggestion.user_email || 'N/A'})
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      IP: {suggestion.userIP || 'N/A'}
                    </p>
                  </div>
                  
                  {/* Message (if provided) */}
                  {suggestion.message && (
                    <div>
                      <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Message:
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {suggestion.message}
                      </p>
                    </div>
                  )}

                  {/* Quick Add Translation */}
                  {suggestion.status === 'pending' && (
                    <div className={`mt-3 p-3 rounded-lg border ${
                      isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-green-50 border-green-200'
                    }`}>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        🚀 Quick Add Translation:
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={quickTranslations[suggestion.id] || ''}
                          onChange={(e) => setQuickTranslations({
                            ...quickTranslations,
                            [suggestion.id]: e.target.value
                          })}
                          placeholder={`Enter Darija translation for "${suggestion.word}"...`}
                          className={`flex-1 px-3 py-2 border rounded ${
                            isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'
                          }`}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleQuickAddTranslation(suggestion);
                            }
                          }}
                        />
                        <button
                          onClick={() => handleQuickAddTranslation(suggestion)}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-bold text-lg"
                          title="Add translation to database"
                        >
                          ✓
                        </button>
                      </div>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Type translation and press Enter or click ✓ to add to database
                      </p>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => handleUpdateSuggestionStatus(suggestion.id, 'approved')}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        suggestion.status === 'approved' 
                          ? 'bg-green-100 text-green-800 cursor-default' 
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                      disabled={suggestion.status === 'approved'}
                      title="Approve suggestion"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleUpdateSuggestionStatus(suggestion.id, 'rejected')}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        suggestion.status === 'rejected' 
                          ? 'bg-red-100 text-red-800 cursor-default' 
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                      disabled={suggestion.status === 'rejected'}
                      title="Reject suggestion"
                    >
                      Reject
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteSuggestion(suggestion.id);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
                      title="Delete suggestion"
                      type="button"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
        </div>
        )}

        {/* Feedback */}
        {feedbackSubTab === 'feedback' && (
        <div className="space-y-6">
      {/* Feedback Settings - Toggle */}
      <div className={`p-4 rounded-lg border ${
        isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              👍 Enable Like/Dislike Feedback
            </h4>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {settings.feedbackEnabled
                ? '✓ Users can like/dislike translations on the frontend.'
                : '✗ Like/dislike buttons are hidden from users.'}
            </p>
          </div>

          <button
            onClick={async () => {
              const newValue = !settings.feedbackEnabled;
              // Update both settings and appSettings for immediate effect
              setSettings(prev => ({...prev, feedbackEnabled: newValue}));
              setAppSettings(prev => ({...prev, feedbackEnabled: newValue}));

              try {
                const response = await fetch(`${API}/admin/settings?feedbackEnabled=${newValue}`, {
                  method: "PUT",
                  headers: authHeaders
                });
                if (response.ok) {
                  showNotification(newValue ? 'Feedback enabled' : 'Feedback disabled', 'success');
                  if (onSettingsUpdate) await onSettingsUpdate();
                } else {
                  // Revert on error
                  setSettings(prev => ({...prev, feedbackEnabled: !newValue}));
                  setAppSettings(prev => ({...prev, feedbackEnabled: !newValue}));
                  showNotification('Failed to update setting', 'error');
                }
              } catch (error) {
                setSettings(prev => ({...prev, feedbackEnabled: !newValue}));
                setAppSettings(prev => ({...prev, feedbackEnabled: !newValue}));
                showNotification('Failed to update setting', 'error');
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              settings.feedbackEnabled ? 'bg-green-600' : 'bg-gray-300'
            }`}
            title="Toggle feedback setting"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.feedbackEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          👍 User Feedback ({feedback.length})
        </h3>
      </div>

      {feedback.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <p>No feedback yet.</p>
          <p className="text-sm mt-2">Users can like/dislike translations from the translator page.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {feedback.map((fb) => (
            <div key={fb.id} className={`border rounded-lg p-4 ${
              isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-white'
            } ${fb.feedbackType === 'like' ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'}`}>
              
              {editingFeedback === fb.id ? (
                // Edit Mode
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Source Text
                      </label>
                      <input
                        type="text"
                        value={editFeedbackData.sourceText}
                        onChange={(e) => setEditFeedbackData({...editFeedbackData, sourceText: e.target.value})}
                        className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Translated Text
                      </label>
                      <input
                        type="text"
                        value={editFeedbackData.translatedText}
                        onChange={(e) => setEditFeedbackData({...editFeedbackData, translatedText: e.target.value})}
                        className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Language
                      </label>
                      <select
                        value={editFeedbackData.sourceLang}
                        onChange={(e) => setEditFeedbackData({...editFeedbackData, sourceLang: e.target.value})}
                        className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                      >
                        {languages.filter(l => l.code !== 'auto').map(lang => (
                          <option key={lang.code} value={lang.code}>{lang.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Feedback Type
                      </label>
                      <select
                        value={editFeedbackData.feedbackType}
                        onChange={(e) => setEditFeedbackData({...editFeedbackData, feedbackType: e.target.value})}
                        className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                      >
                        <option value="like">👍 Like</option>
                        <option value="dislike">👎 Dislike</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Message
                    </label>
                    <textarea
                      value={editFeedbackData.message}
                      onChange={(e) => setEditFeedbackData({...editFeedbackData, message: e.target.value})}
                      className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                      rows="2"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Status
                    </label>
                    <select
                      value={editFeedbackData.status}
                      onChange={(e) => setEditFeedbackData({...editFeedbackData, status: e.target.value})}
                      className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="fixed">Fixed</option>
                    </select>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const formData = new FormData();
                          formData.append('sourceText', editFeedbackData.sourceText);
                          formData.append('translatedText', editFeedbackData.translatedText);
                          formData.append('sourceLang', editFeedbackData.sourceLang);
                          formData.append('feedbackType', editFeedbackData.feedbackType);
                          formData.append('message', editFeedbackData.message || '');
                          formData.append('status', editFeedbackData.status);
                          
                          const response = await fetch(`${API}/admin/feedback/${fb.id}`, {
                            method: 'PUT',
                            headers: { 'Authorization': `Bearer ${adminToken}` },
                            body: formData
                          });
                          
                          if (response.ok) {
                            showNotification('Feedback updated successfully!', 'success');
                            setEditingFeedback(null);
                            loadFeedbackSubTabData('feedback');
                          }
                        } catch (error) {
                          console.error('Error updating feedback:', error);
                          showNotification('Failed to update feedback', 'error');
                        }
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setEditingFeedback(null)}
                      className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{fb.feedbackType === 'like' ? '👍' : '👎'}</span>
                      <span className={`px-2 py-1 text-xs rounded ${
                        fb.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : fb.status === 'reviewed'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}>
                        {fb.status}
                      </span>
                    </div>
                    
                    <div className="space-y-1">
                      <p className={`font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {fb.sourceText}
                      </p>
                      <p className={`${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                        → {fb.translatedText}
                      </p>
                      {fb.message && (
                        <p className={`text-sm italic ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Message: "{fb.message}"
                        </p>
                      )}
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {fb.sourceLang.toUpperCase()} • IP: {fb.userIP} • {new Date(fb.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    {/* Edit Button */}
                    <button
                      onClick={() => {
                        setEditingFeedback(fb.id);
                        setEditFeedbackData({
                          sourceText: fb.sourceText,
                          translatedText: fb.translatedText,
                          sourceLang: fb.sourceLang,
                          feedbackType: fb.feedbackType,
                          message: fb.message || '',
                          status: fb.status
                        });
                      }}
                      className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                      title="Edit feedback"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                          const response = await fetch(`${API}/admin/feedback/${fb.id}`, {
                            method: 'DELETE',
                            headers: { 'Authorization': `Bearer ${adminToken}` }
                          });
                          
                          if (response.ok) {
                            showNotification('Feedback deleted', 'success');
                            loadFeedbackSubTabData('feedback');
                          } else {
                            const error = await response.json();
                            showNotification(error.detail || 'Failed to delete feedback', 'error');
                          }
                        } catch (error) {
                          console.error('Error deleting feedback:', error);
                          showNotification('Failed to delete feedback', 'error');
                        }
                      }}
                      type="button"
                      className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      title="Delete feedback"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
        </div>
        )}

      </div>
      )}

      {/* === TAB: Special Words === */}
      {translationSubTab === 'specialwords' && (
      <div className="space-y-4">
        {/* Header */}
        <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'border-yellow-600 bg-gray-800' : 'border-yellow-400 bg-yellow-50'}`}>
          <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
            ✨ Special Words
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Mark translations with animated styles. When users search these words, Darija text appears with eye-catching effects.
          </p>
        </div>

        {/* ── LIVE EFFECT PREVIEW ── */}
        <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'border-purple-600 bg-gray-900' : 'border-purple-300 bg-gradient-to-br from-gray-50 to-purple-50'}`}>
          <h4 className={`font-bold mb-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
            Effect Preview
          </h4>
          <div className="flex gap-3 mb-4 flex-wrap">
            <select
              value={specialPreviewStyle}
              onChange={(e) => setSpecialPreviewStyle(e.target.value)}
              className={`px-3 py-2 rounded-lg border font-medium ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}
            >
              <option value="gold-shimmer">Gold Shimmer</option>
              <option value="soft-glow">Soft Glow</option>
              <option value="neon-blue">Neon Blue</option>
              <option value="neon-green">Neon Green</option>
              <option value="rainbow">Rainbow</option>
              <option value="fire">Fire</option>
              <option value="silver-shine">Silver Shine</option>
              <option value="purple-pulse">Purple Pulse</option>
              <option value="ocean-wave">Ocean Wave</option>
              <option value="sunset">Sunset</option>
              <option value="electric">Electric</option>
              <option value="emerald">Emerald</option>
              <option value="party">Party</option>
              <option value="confetti">Confetti</option>
              <option value="pulse">Pulse</option>
              <option value="voltage">Voltage</option>
            </select>
            <select
              value={specialPreviewSize}
              onChange={(e) => setSpecialPreviewSize(e.target.value)}
              className={`px-3 py-2 rounded-lg border font-medium ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}
            >
              <option value="small">Small</option>
              <option value="normal">Normal</option>
              <option value="large">Large</option>
              <option value="xl">Extra Large</option>
            </select>
          </div>
          <div
            className={`p-8 rounded-xl text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
          >
            {['party','confetti','pulse','voltage'].includes(specialPreviewStyle) ? (
              <span className={specialPreviewStyle === 'party' ? 'party-wrap' : specialPreviewStyle === 'confetti' ? 'confetti-wrap' : specialPreviewStyle === 'pulse' ? 'pulse-wrap' : 'voltage-wrap'}>
                <span className={`relative z-10 special-size-${specialPreviewSize} ${isDarkMode ? 'text-green-400' : 'text-green-700'}`} style={{fontWeight: 700}}>
                  meghrib
                </span>
                <span className="mx-3 relative z-10 text-gray-500">|</span>
                <span className={`relative z-10 special-size-${specialPreviewSize} ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`} dir="rtl" style={{fontWeight: 700}}>
                  المغرب
                </span>
                {specialPreviewStyle === 'party' ? (
                  adminPartyDots.map((dot, i) => (
                    <span key={i} className="party-dot" style={dot.style} />
                  ))
                ) : specialPreviewStyle === 'confetti' ? (
                  adminConfettiPieces.map((piece, i) => (
                    <span key={i} className="confetti-piece" style={piece.style} />
                  ))
                ) : specialPreviewStyle === 'voltage' ? (
                  adminVoltageDots.map((dot, i) => (
                    <span key={i} className="voltage-dot" style={dot.style} />
                  ))
                ) : null}
              </span>
            ) : (
              <>
                <span className={`special-text-${specialPreviewStyle} special-size-${specialPreviewSize}`}>
                  meghrib
                </span>
                <span className={`mx-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>|</span>
                <span className={`special-text-${specialPreviewStyle} special-size-${specialPreviewSize}`} dir="rtl">
                  المغرب
                </span>
              </>
            )}
          </div>
          {/* All effects grid preview */}
          <div className="mt-4">
            <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>All Effects:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {[
                { id: 'gold-shimmer', name: 'Gold Shimmer' },
                { id: 'soft-glow', name: 'Soft Glow' },
                { id: 'neon-blue', name: 'Neon Blue' },
                { id: 'neon-green', name: 'Neon Green' },
                { id: 'rainbow', name: 'Rainbow' },
                { id: 'fire', name: 'Fire' },
                { id: 'silver-shine', name: 'Silver Shine' },
                { id: 'purple-pulse', name: 'Purple Pulse' },
                { id: 'ocean-wave', name: 'Ocean Wave' },
                { id: 'sunset', name: 'Sunset' },
                { id: 'electric', name: 'Electric' },
                { id: 'emerald', name: 'Emerald' },
                { id: 'party', name: 'Party' },
                { id: 'confetti', name: 'Confetti' },
                { id: 'pulse', name: 'Pulse' },
                { id: 'voltage', name: 'Voltage' }
              ].map(effect => (
                <div
                  key={effect.id}
                  onClick={() => setSpecialPreviewStyle(effect.id)}
                  className={`p-3 rounded-lg cursor-pointer text-center border transition-all ${
                    specialPreviewStyle === effect.id
                      ? (isDarkMode ? 'border-yellow-500 bg-gray-700' : 'border-yellow-500 bg-yellow-50')
                      : (isDarkMode ? 'border-gray-700 bg-gray-800 hover:border-gray-500' : 'border-gray-200 bg-white hover:border-gray-400')
                  }`}
                >
                  <span className={`special-text-${effect.id} special-size-normal`}>
                    {effect.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── SEARCH TO ADD SPECIAL WORD ── */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-white'}`}>
          <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Add Special Word
          </h4>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search translation by English word..."
              className={`flex-1 px-4 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`}
              id="specialWordSearch"
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  const query = e.target.value.trim();
                  if (!query) return;
                  try {
                    const res = await fetch(`${API}/admin/translations?search=${encodeURIComponent(query)}&per_page=20`, {
                      headers: { 'Authorization': `Bearer ${adminToken}` }
                    });
                    const data = await res.json();
                    setSpecialSearchResults(data.translations || []);
                  } catch (err) {
                    showNotification('Search failed', 'error');
                  }
                }
              }}
            />
            <button
              onClick={async () => {
                const input = document.getElementById('specialWordSearch');
                const query = input?.value?.trim();
                if (!query) return;
                try {
                  const res = await fetch(`${API}/admin/translations?search=${encodeURIComponent(query)}&per_page=20`, {
                    headers: { 'Authorization': `Bearer ${adminToken}` }
                  });
                  const data = await res.json();
                  setSpecialSearchResults(data.translations || []);
                } catch (err) {
                  showNotification('Search failed', 'error');
                }
              }}
              className="px-6 py-2 bg-yellow-500 text-white rounded-lg font-bold hover:bg-yellow-600"
            >
              Search
            </button>
          </div>

          {/* Search Results */}
          {specialSearchResults.length > 0 && (
            <div className="mt-3 space-y-2">
              {specialSearchResults.filter(t => !t.specialStyle).map(t => (
                <div key={t.id} className={`flex items-center justify-between p-3 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-600' : 'border-gray-200 bg-gray-50'}`}>
                  <div>
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t.sourceText}</span>
                    <span className="mx-2 text-gray-400">→</span>
                    <span className={`${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{t.latinScript || ''}</span>
                    {t.translatedText && <span className="ml-2 text-blue-500" dir="rtl">{t.translatedText}</span>}
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/admin/translations/${t.id}/special-style`, {
                          method: 'PUT',
                          headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ style: specialPreviewStyle, size: specialPreviewSize })
                        });
                        if (res.ok) {
                          showNotification(`"${t.sourceText}" marked as special with ${specialPreviewStyle}!`, 'success');
                          setSpecialSearchResults(prev => prev.filter(r => r.id !== t.id));
                          loadSpecialWords();
                        } else {
                          showNotification('Failed to set style', 'error');
                        }
                      } catch (err) {
                        showNotification('Error setting style', 'error');
                      }
                    }}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg font-bold hover:bg-yellow-600 text-sm"
                  >
                    ✨ Set as Special
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── CURRENT SPECIAL WORDS LIST ── */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-white'}`}>
          <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Current Special Words ({specialWords.length})
          </h4>
          {specialWords.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p className="text-4xl mb-2">✨</p>
              <p>No special words yet. Search above to add some!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {specialWords.map(t => {
                const ss = t.specialStyle || {};
                return (
                  <div key={t.id} className={`p-4 rounded-lg border ${isDarkMode ? 'border-yellow-700 bg-gray-600' : 'border-yellow-300 bg-yellow-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t.sourceText}</span>
                        <span className="text-gray-400">→</span>
                        <span className={`special-text-${ss.style || 'gold-shimmer'} special-size-${ss.size || 'normal'}`}>
                          {t.latinScript || ''}
                        </span>
                        {t.translatedText && (
                          <>
                            <span className="text-gray-400">|</span>
                            <span className={`special-text-${ss.style || 'gold-shimmer'} special-size-${ss.size || 'normal'}`} dir="rtl">
                              {t.translatedText}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded text-xs font-bold">{ss.style}</span>
                        <span className="px-2 py-0.5 bg-blue-200 text-blue-800 rounded text-xs font-bold">{ss.size}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 items-center flex-wrap">
                      <select
                        defaultValue={ss.style || 'gold-shimmer'}
                        id={`sw-style-${t.id}`}
                        className={`px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}
                      >
                        <option value="gold-shimmer">Gold Shimmer</option>
                        <option value="soft-glow">Soft Glow</option>
                        <option value="neon-blue">Neon Blue</option>
                        <option value="neon-green">Neon Green</option>
                        <option value="rainbow">Rainbow</option>
                        <option value="fire">Fire</option>
                        <option value="silver-shine">Silver Shine</option>
                        <option value="purple-pulse">Purple Pulse</option>
                        <option value="ocean-wave">Ocean Wave</option>
                        <option value="sunset">Sunset</option>
                        <option value="electric">Electric</option>
                        <option value="emerald">Emerald</option>
                        <option value="party">Party</option>
              <option value="confetti">Confetti</option>
              <option value="pulse">Pulse</option>
              <option value="voltage">Voltage</option>
                      </select>
                      <select
                        defaultValue={ss.size || 'normal'}
                        id={`sw-size-${t.id}`}
                        className={`px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}
                      >
                        <option value="small">Small</option>
                        <option value="normal">Normal</option>
                        <option value="large">Large</option>
                        <option value="xl">Extra Large</option>
                      </select>
                      <button
                        onClick={async () => {
                          const style = document.getElementById(`sw-style-${t.id}`)?.value || 'gold-shimmer';
                          const size = document.getElementById(`sw-size-${t.id}`)?.value || 'normal';
                          try {
                            const res = await fetch(`${API}/admin/translations/${t.id}/special-style`, {
                              method: 'PUT',
                              headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ style, size })
                            });
                            if (res.ok) {
                              showNotification('Style updated!', 'success');
                              loadSpecialWords();
                            }
                          } catch (err) {
                            showNotification('Error updating style', 'error');
                          }
                        }}
                        className="px-3 py-1 bg-green-600 text-white rounded font-bold hover:bg-green-700 text-sm"
                      >
                        Update
                      </button>
                      <button
                        onClick={() => setRemoveStyleModal({ id: t.id, sourceText: t.sourceText })}
                        className="px-3 py-1 bg-red-500 text-white rounded font-bold hover:bg-red-600 text-sm"
                      >
                        Remove Effect
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Remove Effect Confirmation Modal */}
        {removeStyleModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setRemoveStyleModal(null)}>
            <div
              className={`w-full max-w-md mx-4 rounded-2xl shadow-2xl p-6 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-4">
                <div className="text-5xl mb-3">✨</div>
                <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Remove Special Effect
                </h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Remove the special effect from <strong>"{removeStyleModal.sourceText}"</strong>?
                </p>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                  The word will stay in the database — only the visual effect is removed.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setRemoveStyleModal(null)}
                  className={`flex-1 px-6 py-3 rounded-xl font-bold transition-all ${
                    isDarkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/admin/translations/${removeStyleModal.id}/special-style`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${adminToken}` }
                      });
                      if (res.ok) {
                        showNotification(`Effect removed from "${removeStyleModal.sourceText}" — word is safe!`, 'success');
                        setRemoveStyleModal(null);
                        loadSpecialWords();
                      }
                    } catch (err) {
                      showNotification('Error removing effect', 'error');
                    }
                  }}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold hover:from-orange-600 hover:to-red-600 transition-all"
                >
                  Remove Effect
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* ═══════ AUDIT SUB-TAB ═══════ */}
      {translationSubTab === 'audit' && (
      <div className="space-y-4">
        {/* Header */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-blue-700 bg-gray-800' : 'border-blue-300 bg-blue-50'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                Translation Audit
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Check coverage by category, find gaps and duplicates
              </p>
              <div className={`mt-2 text-xs space-y-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <p><strong>Click</strong> a missing word (red) → DeepSeek generates translation in side panel → review/edit → save</p>
                <p><strong>Shift+Click</strong> to select multiple words → "Generate with DeepSeek" button → bulk review → Save All</p>
                <p>Words already in DB show ✅. Verbs are skipped (handled by Grammar). Add variations (plural/female) with + button.</p>
              </div>
            </div>
            <button
              onClick={fetchAuditData}
              disabled={auditLoading}
              className={`px-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} disabled:opacity-50`}
            >
              {auditLoading ? 'Scanning...' : auditData ? 'Refresh' : 'Run Audit'}
            </button>
          </div>
        </div>

        {/* Loading */}
        {auditLoading && (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {auditData && !auditLoading && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Your Words', value: auditData.summary.uniqueWords, color: 'blue' },
                { label: 'Category Coverage', value: `${auditData.summary.coverage}%`, color: auditData.summary.coverage >= 80 ? 'green' : auditData.summary.coverage >= 50 ? 'yellow' : 'red' },
                { label: 'Missing', value: auditData.summary.missing, color: 'red' },
                { label: 'Duplicates', value: auditData.summary.duplicates, color: auditData.summary.duplicates > 0 ? 'orange' : 'green' },
              ].map((card, i) => (
                <div key={i} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className={`text-2xl font-bold ${
                    card.color === 'green' ? (isDarkMode ? 'text-green-400' : 'text-green-600') :
                    card.color === 'red' ? (isDarkMode ? 'text-red-400' : 'text-red-600') :
                    card.color === 'yellow' ? (isDarkMode ? 'text-yellow-400' : 'text-yellow-600') :
                    card.color === 'orange' ? (isDarkMode ? 'text-orange-400' : 'text-orange-600') :
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`}>{card.value}</div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{card.label}</div>
                </div>
              ))}
            </div>

            {/* Oxford 3000 Coverage */}
            {auditData.oxford3000 && (
              <div className={`rounded-lg border p-4 ${isDarkMode ? 'border-purple-700 bg-gray-800' : 'border-purple-300 bg-purple-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className={`font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                      Oxford 3000 Coverage
                    </h4>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      The 3000 most important English words — curated by Oxford linguists
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${
                      auditData.oxford3000.coverage >= 80 ? (isDarkMode ? 'text-green-400' : 'text-green-600') :
                      auditData.oxford3000.coverage >= 50 ? (isDarkMode ? 'text-yellow-400' : 'text-yellow-600') :
                      isDarkMode ? 'text-red-400' : 'text-red-600'
                    }`}>{auditData.oxford3000.coverage}%</div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {auditData.oxford3000.covered.length} / {auditData.oxford3000.total}
                    </div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className={`w-full h-3 rounded-full overflow-hidden mb-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                  <div
                    className={`h-full rounded-full transition-all ${
                      auditData.oxford3000.coverage >= 80 ? 'bg-green-500' :
                      auditData.oxford3000.coverage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${auditData.oxford3000.coverage}%` }}
                  />
                </div>
                {/* Expandable missing words */}
                <button
                  onClick={() => setAuditExpandedCat(auditExpandedCat === 'oxford3000' ? null : 'oxford3000')}
                  className={`text-sm font-medium ${isDarkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-700 hover:text-purple-600'}`}
                >
                  {auditExpandedCat === 'oxford3000' ? 'Hide' : 'Show'} missing words ({auditData.oxford3000.missing.length})
                  <span className={`ml-1 inline-block transition-transform ${auditExpandedCat === 'oxford3000' ? 'rotate-180' : ''}`}>▼</span>
                </button>
                {auditExpandedCat === 'oxford3000' && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-xs font-bold ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                        Missing from Oxford 3000 ({auditData.oxford3000.missing.length})
                      </p>
                      <button
                        onClick={() => {
                          const allMissing = new Set(auditSelectedMissing);
                          const allSelected = auditData.oxford3000.missing.every(w => allMissing.has(w));
                          if (allSelected) {
                            auditData.oxford3000.missing.forEach(w => allMissing.delete(w));
                          } else {
                            auditData.oxford3000.missing.forEach(w => allMissing.add(w));
                          }
                          setAuditSelectedMissing(new Set(allMissing));
                        }}
                        className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >
                        {auditData.oxford3000.missing.every(w => auditSelectedMissing.has(w)) ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto">
                      {auditData.oxford3000.missing.map(w => (
                        <button
                          key={w}
                          onClick={(e) => {
                            if (e.shiftKey) {
                              const next = new Set(auditSelectedMissing);
                              next.has(w) ? next.delete(w) : next.add(w);
                              setAuditSelectedMissing(next);
                            } else {
                              setAuditPanelOpen(true);
                              setAuditGenerating(true);
                              setAuditGenResults([]);
                              fetch(`${API}/admin/translations/generate-words`, {
                                method: 'POST', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                body: JSON.stringify({ words: [w] })
                              }).then(r => r.json()).then(data => {
                                setAuditGenResults(mapGenResults(data.results));
                                setAuditGenerating(false);
                              }).catch(() => { setAuditGenerating(false); showNotification('Failed to generate', 'error'); });
                            }
                          }}
                          className={`text-xs px-2 py-1 rounded cursor-pointer border transition-all ${
                            auditSelectedMissing.has(w)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : isDarkMode
                                ? 'bg-red-900/40 text-red-300 border-red-800 hover:bg-red-900/60'
                                : 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
                          }`}
                        >
                          {auditSelectedMissing.has(w) ? '\u2713 ' : ''}{w}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Category List */}
            <div className="space-y-2">
              <h4 className={`font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Categories</h4>
              {Object.entries(auditData.categories)
                .sort((a, b) => a[1].coverage - b[1].coverage)
                .map(([catName, catData]) => {
                  const icons = { greetings: '👋', food: '🍞', family: '👨‍👩‍👧', body: '🦴', colors: '🎨', numbers: '🔢', animals: '🐾', clothing: '👕', household: '🏠', transport: '🚗', time: '⏰', weather: '🌤️', places: '📍', emotions: '😊', common_verbs: '🏃', adjectives: '📝', education: '🎓', technology: '📱', money: '💰', religion: '🕌', sports: '⚽', moroccan: '🇲🇦' };
                  const isExpanded = auditExpandedCat === catName;
                  return (
                    <div key={catName} className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                      <button
                        onClick={() => setAuditExpandedCat(isExpanded ? null : catName)}
                        className={`w-full px-4 py-3 flex items-center gap-3 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                      >
                        <span className="text-lg">{icons[catName] || icons[catName.split('_')[0]] || '📂'}</span>
                        <span className={`font-medium flex-1 text-left capitalize ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          {catName.replace('_', ' ')}
                        </span>
                        <span className={`text-sm font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {catData.existingCount}/{catData.totalExpected}
                        </span>
                        {/* Progress bar */}
                        <div className={`w-24 h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                          <div
                            className={`h-full rounded-full ${
                              catData.coverage >= 80 ? 'bg-green-500' :
                              catData.coverage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${catData.coverage}%` }}
                          />
                        </div>
                        <span className={`text-sm font-bold w-12 text-right ${
                          catData.coverage >= 80 ? (isDarkMode ? 'text-green-400' : 'text-green-600') :
                          catData.coverage >= 50 ? (isDarkMode ? 'text-yellow-400' : 'text-yellow-600') :
                          isDarkMode ? 'text-red-400' : 'text-red-600'
                        }`}>
                          {catData.coverage}%
                        </span>
                        <span className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                      </button>

                      {isExpanded && (
                        <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          {/* Existing words */}
                          {catData.existing.length > 0 && (
                            <div className="mt-3">
                              <p className={`text-xs font-bold mb-1.5 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                                Existing ({catData.existingCount})
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {catData.existing.map(w => (
                                  <span key={w} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-800' : 'bg-green-100 text-green-800'}`}>
                                    {w}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Missing words */}
                          {catData.missing.length > 0 && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between mb-1.5">
                                <p className={`text-xs font-bold ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                                  Missing ({catData.missingCount})
                                </p>
                                <button
                                  onClick={() => {
                                    const allMissing = new Set(auditSelectedMissing);
                                    const allSelected = catData.missing.every(w => allMissing.has(w));
                                    if (allSelected) {
                                      catData.missing.forEach(w => allMissing.delete(w));
                                    } else {
                                      catData.missing.forEach(w => allMissing.add(w));
                                    }
                                    setAuditSelectedMissing(new Set(allMissing));
                                  }}
                                  className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                                >
                                  {catData.missing.every(w => auditSelectedMissing.has(w)) ? 'Deselect All' : 'Select All'}
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {catData.missing.map(w => (
                                  <button
                                    key={w}
                                    onClick={(e) => {
                                      if (e.shiftKey) {
                                        // Shift+click: toggle selection for bulk
                                        const next = new Set(auditSelectedMissing);
                                        next.has(w) ? next.delete(w) : next.add(w);
                                        setAuditSelectedMissing(next);
                                      } else {
                                        // Normal click: generate single word
                                        setAuditPanelOpen(true);
                                        setAuditGenerating(true);
                                        setAuditGenResults([]);
                                        fetch(`${API}/admin/translations/generate-words`, {
                                          method: 'POST', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                          body: JSON.stringify({ words: [w] })
                                        }).then(r => r.json()).then(data => {
                                          setAuditGenResults(mapGenResults(data.results));
                                          setAuditGenerating(false);
                                        }).catch(() => { setAuditGenerating(false); showNotification('Failed to generate', 'error'); });
                                      }
                                    }}
                                    className={`text-xs px-2 py-1 rounded cursor-pointer border transition-all ${
                                      auditSelectedMissing.has(w)
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : isDarkMode
                                          ? 'bg-red-900/40 text-red-300 border-red-800 hover:bg-red-900/60'
                                          : 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
                                    }`}
                                  >
                                    {auditSelectedMissing.has(w) ? '✓ ' : ''}{w}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {catData.missingCount === 0 && (
                            <p className={`mt-3 text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                              All words covered in this category!
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Duplicates Section */}
            {auditData.duplicates.length > 0 && (
              <div className={`rounded-lg border p-4 ${isDarkMode ? 'border-orange-700 bg-gray-800' : 'border-orange-300 bg-orange-50'}`}>
                <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}>
                  Duplicates ({auditData.duplicates.length})
                </h4>
                <div className="space-y-2">
                  {auditData.duplicates.map((dup, i) => (
                    <div key={i} className={`p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>"{dup.sourceText}"</span>
                      <span className={`text-sm ml-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>× {dup.count} entries</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {dup.entries.map((e, j) => (
                          <span key={j} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            {e.translatedText || e.latinScript || 'empty'} ({e.status})
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Missing Words — Sticky Footer */}
            {auditSelectedMissing.size > 0 && (
              <div className={`sticky bottom-0 p-4 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 border-blue-600' : 'bg-blue-50 border-blue-400'}`}>
                <div className="flex items-center justify-between">
                  <span className={`font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                    {auditSelectedMissing.size} words selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAuditSelectedMissing(new Set())}
                      className={`px-3 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => {
                        const text = [...auditSelectedMissing].sort().join('\n');
                        navigator.clipboard.writeText(text);
                        showNotification(`${auditSelectedMissing.size} words copied to clipboard!`, 'success');
                      }}
                      className={`px-3 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      Copy
                    </button>
                    <button
                      disabled={auditGenerating}
                      onClick={async () => {
                        const words = [...auditSelectedMissing];
                        setAuditPanelOpen(true);
                        setAuditGenerating(true);
                        setAuditGenResults([]);
                        try {
                          const res = await fetch(`${API}/admin/translations/generate-words`, {
                            method: 'POST', headers: {...authHeaders, 'Content-Type': 'application/json'},
                            body: JSON.stringify({ words })
                          });
                          const data = await res.json();
                          if (res.ok) {
                            setAuditGenResults(mapGenResults(data.results));
                          } else {
                            showNotification(data.detail || 'Generation failed', 'error');
                          }
                        } catch(e) { showNotification('Failed to generate', 'error'); }
                        setAuditGenerating(false);
                      }}
                      className="px-4 py-1.5 rounded text-sm bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-medium"
                    >
                      {auditGenerating ? 'Generating...' : 'Generate with DeepSeek'}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {[...auditSelectedMissing].sort().map(w => (
                    <span key={w} className="text-xs px-2 py-0.5 rounded bg-blue-600 text-white">{w}</span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* DeepSeek Generate Side Panel */}
      {auditPanelOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={(e) => { if (e.target === e.currentTarget) setAuditPanelOpen(false); }}>
          <div className={`w-full max-w-md h-full overflow-y-auto shadow-2xl ${isDarkMode ? 'bg-gray-800 border-l border-gray-700' : 'bg-white border-l border-gray-200'}`}>
            {/* Panel Header */}
            <div className={`sticky top-0 z-10 p-4 border-b flex items-center justify-between ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Generate Translations
              </h3>
              <button onClick={() => setAuditPanelOpen(false)}
                className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                ✕
              </button>
            </div>

            {/* Loading */}
            {auditGenerating && (
              <div className="p-8 text-center">
                <div className="inline-block w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>DeepSeek is generating translations...</p>
              </div>
            )}

            {/* Results */}
            {!auditGenerating && auditGenResults.length > 0 && (
              <div className="p-4 space-y-3">
                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Click to select for bulk. Shift+click missing words in audit.
                </p>
                {auditGenResults.map((item, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${
                    item.exists
                      ? isDarkMode ? 'bg-gray-700/50 border-gray-600 opacity-60' : 'bg-gray-50 border-gray-200 opacity-60'
                      : item._saved
                        ? isDarkMode ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-300'
                        : isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    {/* Word header */}
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {item.exists ? '✅ ' : item._saved ? '✅ ' : '📝 '}{item.sourceText}
                      </span>
                      {item.exists && (
                        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {item.isVerb ? 'Grammar verb' : 'Already exists'}
                        </span>
                      )}
                      {item._saved && (
                        <span className="text-xs text-green-500 font-medium">Saved</span>
                      )}
                    </div>

                    {/* Existing word info */}
                    {item.exists && !item.isVerb && (
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {item.existingLatin} / {item.existingArabic}
                      </p>
                    )}

                    {/* Editable fields for new words */}
                    {!item.exists && !item._saved && (<>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Latin</label>
                            <input type="text" value={item.latinScript || ''}
                              onChange={(e) => {
                                const updated = [...auditGenResults];
                                updated[idx] = {...updated[idx], latinScript: e.target.value};
                                setAuditGenResults(updated);
                              }}
                              className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                            />
                          </div>
                          <div className="flex-1">
                            <label className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Arabic</label>
                            <input type="text" value={item.translatedText || ''} dir="rtl"
                              onChange={(e) => {
                                const updated = [...auditGenResults];
                                updated[idx] = {...updated[idx], translatedText: e.target.value};
                                setAuditGenResults(updated);
                              }}
                              className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Gender</label>
                            <select value={item.gender || ''}
                              onChange={(e) => {
                                const updated = [...auditGenResults];
                                updated[idx] = {...updated[idx], gender: e.target.value || null};
                                setAuditGenResults(updated);
                              }}
                              className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                            >
                              <option value="">-- none --</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Number</label>
                            <select value={item.number || 'singular'}
                              onChange={(e) => {
                                const updated = [...auditGenResults];
                                updated[idx] = {...updated[idx], number: e.target.value};
                                setAuditGenResults(updated);
                              }}
                              className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                            >
                              <option value="singular">Singular</option>
                              <option value="plural">Plural</option>
                            </select>
                          </div>
                        </div>
                        {/* Note / Explanation - Rich Text (same as Add New Translation) */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Note (Optional)</label>
                            {item.note && (
                              <button onClick={() => {
                                const updated = [...auditGenResults];
                                updated[idx] = {...updated[idx], note: ''};
                                setAuditGenResults(updated);
                              }} className="text-red-400 hover:text-red-500 text-[10px]" title="Delete note">delete</button>
                            )}
                          </div>
                          {/* Toolbar */}
                          <div className={`flex items-center gap-0.5 px-1.5 py-0.5 border border-b-0 rounded-t-lg ${isDarkMode ? 'bg-gray-700 border-gray-500' : 'bg-gray-100 border-gray-300'}`}>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }} className={`px-1.5 py-0.5 rounded text-[10px] font-bold hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Bold">B</button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }} className={`px-1.5 py-0.5 rounded text-[10px] italic hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Italic">I</button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('underline'); }} className={`px-1.5 py-0.5 rounded text-[10px] underline hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Underline">U</button>
                            <span className={`mx-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-300'}`}>|</span>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '1'); }} className={`px-1 py-0.5 rounded hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Small text" style={{fontSize:'8px'}}>S</button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '4'); }} className={`px-1 py-0.5 rounded hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Normal text" style={{fontSize:'11px'}}>M</button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '6'); }} className={`px-1 py-0.5 rounded hover:bg-opacity-20 hover:bg-blue-500 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} title="Large text" style={{fontSize:'14px'}}>L</button>
                            <span className={`mx-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-300'}`}>|</span>
                            {['#ef4444','#f97316','#22c55e','#3b82f6','#a855f7','#6b7280'].map(color => (
                              <button key={color} type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('foreColor', false, color); }} className="w-3.5 h-3.5 rounded-full border border-gray-400 hover:scale-125 transition-transform" style={{backgroundColor: color}} title={color} />
                            ))}
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('removeFormat'); }} className={`ml-0.5 px-1 py-0.5 rounded text-[10px] hover:bg-opacity-20 hover:bg-red-500 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} title="Clear formatting">✕</button>
                          </div>
                          {/* Editable area — key forces re-mount when DeepSeek data loads */}
                          <div
                            key={`note-${idx}-${item.note ? 'filled' : 'empty'}`}
                            dir="ltr"
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={(e) => {
                              const html = e.currentTarget?.innerHTML ?? '';
                              const updated = [...auditGenResults];
                              updated[idx] = {...updated[idx], note: html};
                              setAuditGenResults(updated);
                            }}
                            dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(item.note || '')}}
                            className={`w-full px-2 py-1.5 border rounded-b-lg focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[36px] text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300 bg-white'}`}
                            style={{minHeight: '36px', lineHeight: '1.6'}}
                          />
                        </div>
                        {/* Variations */}
                        {item.variations && item.variations.length > 0 && (
                          <div className={`pl-3 border-l-2 space-y-1.5 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                            {item.variations.map((v, vi) => (
                              <div key={vi} className={`p-2 rounded ${isDarkMode ? 'bg-gray-600/50' : 'bg-gray-50'}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Variation {vi + 1}</span>
                                  <button onClick={() => {
                                    const updated = [...auditGenResults];
                                    const vars = [...updated[idx].variations];
                                    vars.splice(vi, 1);
                                    updated[idx] = {...updated[idx], variations: vars};
                                    setAuditGenResults(updated);
                                  }} className="text-red-400 hover:text-red-500 text-xs" title="Remove variation">✕</button>
                                </div>
                                <div className="flex gap-2 mb-1">
                                  <input type="text" value={v.latinScript || ''} placeholder="Latin"
                                    onChange={(e) => {
                                      const updated = [...auditGenResults];
                                      const vars = [...updated[idx].variations];
                                      vars[vi] = {...vars[vi], latinScript: e.target.value};
                                      updated[idx] = {...updated[idx], variations: vars};
                                      setAuditGenResults(updated);
                                    }}
                                    className={`flex-1 px-2 py-1 border rounded text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                  />
                                  <input type="text" value={v.translatedText || ''} dir="rtl" placeholder="Arabic"
                                    onChange={(e) => {
                                      const updated = [...auditGenResults];
                                      const vars = [...updated[idx].variations];
                                      vars[vi] = {...vars[vi], translatedText: e.target.value};
                                      updated[idx] = {...updated[idx], variations: vars};
                                      setAuditGenResults(updated);
                                    }}
                                    className={`flex-1 px-2 py-1 border rounded text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <select value={v.gender || ''}
                                    onChange={(e) => {
                                      const updated = [...auditGenResults];
                                      const vars = [...updated[idx].variations];
                                      vars[vi] = {...vars[vi], gender: e.target.value || null};
                                      updated[idx] = {...updated[idx], variations: vars};
                                      setAuditGenResults(updated);
                                    }}
                                    className={`flex-1 px-1.5 py-0.5 border rounded text-[10px] ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                  >
                                    <option value="">Gender: none</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                  </select>
                                  <select value={v.number || 'singular'}
                                    onChange={(e) => {
                                      const updated = [...auditGenResults];
                                      const vars = [...updated[idx].variations];
                                      vars[vi] = {...vars[vi], number: e.target.value};
                                      updated[idx] = {...updated[idx], variations: vars};
                                      setAuditGenResults(updated);
                                    }}
                                    className={`flex-1 px-1.5 py-0.5 border rounded text-[10px] ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                  >
                                    <option value="singular">Singular</option>
                                    <option value="plural">Plural</option>
                                  </select>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Add Variation button */}
                        <button onClick={() => {
                          const updated = [...auditGenResults];
                          const vars = [...(updated[idx].variations || [])];
                          vars.push({ latinScript: '', translatedText: '', gender: null, number: 'singular' });
                          updated[idx] = {...updated[idx], variations: vars};
                          setAuditGenResults(updated);
                        }} className={`text-xs flex items-center gap-1 mt-1 ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-600'}`}>
                          <span>+</span> Add Variation
                        </button>
                      </div>
                      {/* Save single word */}
                      <button
                        onClick={async () => {
                          try {
                            const formData = new FormData();
                            formData.append('sourceText', item.sourceText);
                            formData.append('sourceLang', 'en');
                            formData.append('translatedText', item.translatedText || '');
                            formData.append('latinScript', item.latinScript || '');
                            if (item.gender) formData.append('gender', item.gender);
                            if (item.number) formData.append('number', item.number);
                            if (item.note) formData.append('note', item.note);
                            formData.append('status', 'verified');
                            const res = await fetch(`${API}/admin/translations`, {
                              method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
                            });
                            if (res.ok) {
                              // Save variations
                              if (item.variations) {
                                for (const v of item.variations) {
                                  if (v.latinScript || v.translatedText) {
                                    const vForm = new FormData();
                                    vForm.append('sourceText', item.sourceText);
                                    vForm.append('sourceLang', 'en');
                                    vForm.append('translatedText', v.translatedText || '');
                                    vForm.append('latinScript', v.latinScript || '');
                                    if (v.gender) vForm.append('gender', v.gender);
                                    if (v.number) vForm.append('number', v.number);
                                    await fetch(`${API}/admin/translations/add-variant`, {
                                      method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: vForm
                                    });
                                  }
                                }
                              }
                              const updated = [...auditGenResults];
                              updated[idx] = {...updated[idx], _saved: true};
                              setAuditGenResults(updated);
                              showNotification(`"${item.sourceText}" saved!`, 'success');
                            } else { showNotification('Failed to save', 'error'); }
                          } catch(e) { showNotification('Save failed', 'error'); }
                        }}
                        className="mt-2 w-full px-3 py-1.5 rounded text-sm bg-green-600 hover:bg-green-700 text-white font-medium"
                      >Save</button>
                    </>)}
                  </div>
                ))}

                {/* Save All button */}
                {auditGenResults.some(r => !r.exists && !r._saved) && (
                  <button
                    onClick={async () => {
                      const toSave = auditGenResults.filter(r => !r.exists && !r._saved);
                      let saved = 0;
                      for (let i = 0; i < auditGenResults.length; i++) {
                        const item = auditGenResults[i];
                        if (item.exists || item._saved) continue;
                        try {
                          const formData = new FormData();
                          formData.append('sourceText', item.sourceText);
                          formData.append('sourceLang', 'en');
                          formData.append('translatedText', item.translatedText || '');
                          formData.append('latinScript', item.latinScript || '');
                          if (item.gender) formData.append('gender', item.gender);
                          if (item.number) formData.append('number', item.number);
                          if (item.note) formData.append('note', item.note);
                          formData.append('status', 'verified');
                          const res = await fetch(`${API}/admin/translations`, {
                            method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: formData
                          });
                          if (res.ok) {
                            if (item.variations) {
                              for (const v of item.variations) {
                                if (v.latinScript || v.translatedText) {
                                  const vForm = new FormData();
                                  vForm.append('sourceText', item.sourceText);
                                  vForm.append('sourceLang', 'en');
                                  vForm.append('translatedText', v.translatedText || '');
                                  vForm.append('latinScript', v.latinScript || '');
                                  if (v.gender) vForm.append('gender', v.gender);
                                  if (v.number) vForm.append('number', v.number);
                                  await fetch(`${API}/admin/translations/add-variant`, {
                                    method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: vForm
                                  });
                                }
                              }
                            }
                            const updated = [...auditGenResults];
                            updated[i] = {...updated[i], _saved: true};
                            setAuditGenResults(updated);
                            saved++;
                          }
                        } catch(e) {}
                      }
                      showNotification(`${saved} words saved!`, 'success');
                      // Refresh audit
                      try {
                        const res = await fetch(`${API}/admin/translations/audit`, { headers: authHeaders });
                        if (res.ok) { const d = await res.json(); setAuditData(d); }
                      } catch(e) {}
                    }}
                    className="w-full py-2.5 rounded-lg text-sm bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold"
                  >
                    Save All New Words ({auditGenResults.filter(r => !r.exists && !r._saved).length})
                  </button>
                )}

                {/* All saved message */}
                {auditGenResults.length > 0 && auditGenResults.every(r => r.exists || r._saved) && (
                  <div className={`text-center py-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                    <p className="font-medium">All words saved!</p>
                    <button onClick={() => { setAuditPanelOpen(false); setAuditGenResults([]); }}
                      className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                    >Close panel</button>
                  </div>
                )}
              </div>
            )}

            {/* No results */}
            {!auditGenerating && auditGenResults.length === 0 && (
              <div className={`p-8 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <p>Click a missing word or select words and click "Generate with DeepSeek"</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Icon Picker Modal — uses shared IconPicker component (browses ALL Image Manager folders) */}
      {iconPickerOpen && (
        <IconPicker
          API={API}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          onClose={() => setIconPickerOpen(null)}
          onPick={async (url) => {
            const field = iconPickerOpen === 'favorites' ? 'favorites_icon_url' : 'request_word_icon_url';
            setSettings(prev => ({...prev, [field]: url}));
            setIconPickerOpen(null);
            try {
              await fetch(`${API}/admin/settings`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ [field]: url }) });
              showNotification('Icon saved!', 'success');
              if (onSettingsUpdate) onSettingsUpdate();
            } catch {}
          }}
        />
      )}

    </div>
  );

};

export default TranslationsAdmin;
